import React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceDot,
  Bar,
  BarChart,
  Line,
  LineChart,
} from "recharts";

import {
  BuyLevelsMode,
	ComparableMetric,
	InvestmentAnalysisV2,
  InvestmentResearchContext,
  InvestmentDetailResponse,
  InvestmentSeriesPoint,
  MoatSignal,
  PortfolioPositionStage,
} from "@features/investments/api";
import { GlossaryTerm } from "@features/investments/glossary/term";
import { InvestmentsAccountMenu } from "@features/investments/components/account-menu";
import { CollapsibleDescriptionCard } from "@features/investments/components/collapsible-description-card";
import { InvestmentsPageState } from "@features/investments/components/page-state";
import { QuoteSummaryStrip } from "@features/investments/components/quote-summary-strip";
import { InvestmentsSubnav } from "@features/investments/components/workspace-shell";
import { ComparableValuationView } from "@features/investments/components/comparable-valuation";
import {
	useEquityComparablesQuery,
  useEquityDetailQuery,
  useInvestmentEventsQuery,
  useInstrumentCatalogQuery,
  useInvestmentsAccessQuery,
  useInvestmentsWatchlistQuery,
  useInvestmentQuoteQuery,
  useRefreshInvestmentMutation,
  useUpdatePortfolioMutation,
} from "@features/investments/queries";
import {
  describeCurveRegime,
  formatBasisPointsNullable,
  formatPercentNullable as formatMacroPercentNullable,
  getDetailDateMeta,
} from "@features/investments/screens/workspace-shared";
import {
  BuyLevelsHorizon,
  BuyLevelsScenario,
  createInitialEquityDetailState,
  DetailTab,
  EquityDetailExportFormat,
  EquityDetailExportSection,
  equityDetailReducer,
  ETFDetailTab,
  SeriesMode,
  useActivePortfolioSelection,
} from "@features/investments/state";
import { INVESTMENTS_PORTFOLIO_POSITION_LIMIT } from "@features/investments/state/constants";
import { buildQueryErrorMessage, buildSortedSeries } from "@features/investments/transforms";
import "@views/investments/index.scss";

type MetricKind = "currency" | "ratio" | "percentDecimal" | "percentValue" | "number" | "pp";
type DataConfidenceLabel = "HIGH" | "MEDIUM" | "LOW";
type BuyLevelsReportData = NonNullable<InvestmentDetailResponse["report"]>["buyLevels"];
type BuyLevelsModeViewData = BuyLevelsReportData["static"];
type BuyLevelsHorizonData = BuyLevelsModeViewData["dcf"]["horizon3y"];
type BuyLevelsScenarioData = BuyLevelsHorizonData["scenarios"][number];
type BuyLevelsBandData = BuyLevelsModeViewData["multiples"]["bands"][number];
type BuyLevelsWarningData = NonNullable<BuyLevelsReportData["warnings"]>[number];
type BuyPriceOverlayBandData = NonNullable<InvestmentDetailResponse["report"]>["buyPriceOverlay"]["bands"][number];
type BuyLevelsGaugeTone = "deep-value" | "value" | "fair" | "rich" | "overpriced" | "unavailable";
type BuyLevelsReverseStatus = "ALIGNED" | "STRETCHED" | "AGGRESSIVE" | "DISCOUNTED" | "UNAVAILABLE";
type RateBaselineMetrics = NonNullable<InvestmentDetailResponse["ratesBaseline"]>;
type ChartRenderMode = "line" | "auto-multiples";
type CustomDCFState = {
  growthRate: number;
  discountRate: number;
  terminalGrowth: number;
  horizon: BuyLevelsHorizon;
};
type BuyLevelsCopyInput = {
  data: InvestmentDetailResponse;
  buyLevels: BuyLevelsReportData;
  view: BuyLevelsModeViewData;
  mode: BuyLevelsMode;
  horizon: BuyLevelsHorizon;
  reverseScenario: BuyLevelsScenario;
};
type ExportPortfolioRow = {
  stage: string;
  recommendedAction: string;
  recommendedActionReason: string;
} | null;

type ChartSeriesPoint = InvestmentSeriesPoint & {
  capexToCfo: number;
  relativeGrossMarginPp: number;
  relativeOperatingMarginPp: number;
  relativeFcfMarginPp: number;
};

type HistoryCategory = "cash-flow" | "profitability" | "valuation" | "balance-sheet" | "capital-allocation";
type HistoryMetric = {
  key: keyof ChartSeriesPoint;
  label: string;
  color: string;
  kind: MetricKind;
  autoMultiples?: boolean;
};

const HISTORY_METRICS: Record<HistoryCategory, HistoryMetric[]> = {
  "cash-flow": [
    { key: "cfo", label: "Operating Cash Flow", color: "#22d3ee", kind: "currency" },
    { key: "capex", label: "Capital Expenditures", color: "#f59e0b", kind: "currency" },
    { key: "fcf", label: "Free Cash Flow", color: "#22c55e", kind: "currency" },
    { key: "fcfMargin", label: "FCF Margin", color: "#67e8f9", kind: "percentDecimal" },
  ],
  profitability: [
    { key: "revenue", label: "Revenue", color: "#38bdf8", kind: "currency" },
    { key: "operatingIncome", label: "Operating Income", color: "#f59e0b", kind: "currency" },
    { key: "operatingMargin", label: "Operating Margin", color: "#facc15", kind: "percentDecimal" },
    { key: "roic", label: "ROIC", color: "#a3e635", kind: "percentValue" },
  ],
  valuation: [
    { key: "evFcf", label: "EV / FCF", color: "#60a5fa", kind: "ratio", autoMultiples: true },
    { key: "pFcf", label: "P / FCF", color: "#f97316", kind: "ratio", autoMultiples: true },
    { key: "evEbitda", label: "EV / EBITDA", color: "#a78bfa", kind: "ratio", autoMultiples: true },
    { key: "pERatio", label: "P / E", color: "#14b8a6", kind: "ratio", autoMultiples: true },
  ],
  "balance-sheet": [
    { key: "cashAndEquivalents", label: "Cash & Equivalents", color: "#34d399", kind: "currency" },
    { key: "totalDebt", label: "Total Debt", color: "#f87171", kind: "currency" },
    { key: "netDebt", label: "Net Debt", color: "#fb7185", kind: "currency" },
    { key: "currentRatio", label: "Current Ratio", color: "#2dd4bf", kind: "number" },
  ],
  "capital-allocation": [
    { key: "shareRepurchases", label: "Share Repurchases", color: "#f97316", kind: "currency" },
    { key: "sharesOutstanding", label: "Weighted Average Shares", color: "#14b8a6", kind: "number" },
    { key: "capexToCfo", label: "CapEx / CFO", color: "#22c55e", kind: "percentValue" },
    { key: "dividendPayout", label: "Dividend Payout Ratio", color: "#facc15", kind: "percentValue" },
  ],
};

function ProviderResearchContextView({context}:{context:InvestmentResearchContext}):JSX.Element{
  const productSegments=context.productSegments??[];const latestProductDate=productSegments[0]?.date;const products=productSegments.filter(item=>item.date===latestProductDate).slice(0,8);
  const geographicSegments=context.geographicSegments??[];const latestGeographyDate=geographicSegments[0]?.date;const geographies=geographicSegments.filter(item=>item.date===latestGeographyDate).slice(0,8);
  const insiderSummary=context.insiderStatistics?.[0];
  const estimate=context.analystEstimates?.[0];
  const datasets=Object.entries(context.datasets??{}).sort(([left],[right])=>left.localeCompare(right));
  return <div className="provider-evidence-stack">
  <div className="provider-evidence-grid">
    <article><h4>Business concentration</h4>{products.length?<ul>{products.map(item=><li key={`${item.date}-${item.name}`}>{item.name}: {formatCurrency(item.value)}</li>)}</ul>:<p>No product segmentation cached.</p>}{geographies.length?<ul>{geographies.map(item=><li key={`${item.date}-${item.name}`}>{item.name}: {formatCurrency(item.value)}</li>)}</ul>:null}</article>
    <article><h4>Capital structure</h4><KeyValue label="Float shares" value={formatNumberNullable(context.shareFloat.floatShares)}/><KeyValue label="Outstanding shares" value={formatNumberNullable(context.shareFloat.outstandingShares)}/><p className="buy-levels-note">Company valuation history is presented with trading comparables under Valuation.</p></article>
    <article><h4>External expectations</h4><KeyValue label="Consensus price" value={formatCurrencyNullable(context.externalConsensus.targetConsensus)}/><KeyValue label="Target range" value={`${formatCurrencyNullable(context.externalConsensus.targetLow)} – ${formatCurrencyNullable(context.externalConsensus.targetHigh)}`}/>{estimate&&<><KeyValue label={`Revenue range (${estimate.date})`} value={`${formatCurrencyNullable(estimate.revenueLow)} – ${formatCurrencyNullable(estimate.revenueHigh)}`}/><KeyValue label="EPS range" value={`${formatNumberNullable(estimate.epsLow)} – ${formatNumberNullable(estimate.epsHigh)}`}/></>}<KeyValue label="Altman Z" value={formatNumberNullable(context.financialScores.altmanZScore)}/><KeyValue label="Piotroski" value={formatNumberNullable(context.financialScores.piotroskiScore)}/><p className="buy-levels-note">External comparisons, not house valuation inputs.</p></article>
    <article><h4>Completed earnings</h4>{context.earningsResults?.length?<ul>{context.earningsResults.slice(0,5).map((result,index)=><li key={`${result.date}-${index}`}>{result.date}: EPS {formatNumberNullable(result.epsActual)} actual vs. {formatNumberNullable(result.epsEstimated)} external estimate</li>)}</ul>:<p>No completed earnings comparisons cached.</p>}</article>
    <article><h4>Recent filings and insider activity</h4>{insiderSummary?<p>{insiderSummary.year} Q{insiderSummary.quarter}: {formatNumberNullable(insiderSummary.totalAcquired)} acquired / {formatNumberNullable(insiderSummary.totalDisposed)} disposed.</p>:null}<ul>{context.filings?.slice(0,5).map((filing,index)=><li key={`${filing.filingDate}-${filing.form}-${index}`}><a href={filing.finalLink||filing.url} target="_blank" rel="noreferrer">{filing.form||"SEC filing"}</a> · {filing.filingDate}</li>)}</ul><ul>{context.insiderTransactions?.slice(0,5).map((item,index)=><li key={`${item.transactionDate}-${item.owner}-${index}`}>{item.owner||"Insider"}: {item.acquisitionOrDisposition||item.transactionType} {formatNumberNullable(item.shares)} shares · {item.transactionDate}</li>)}</ul></article>
  </div>
  <section className="report-card provider-dataset-status"><h4>Provider dataset status</h4><div className="report-grid two-up report-grid-dense">{datasets.map(([name,status])=><div className="kv-item" key={name}><span className="k">{name.replace(/-/g," ")}</span><span className="v">{status.status.toUpperCase()} · {formatDateTimeLabel(status.fetchedAt)}</span>{status.error&&<span className="freshness-error">{status.error}</span>}<a href={status.sourceUrl} target="_blank" rel="noreferrer">Source</a></div>)}</div>{datasets.length===0&&<p className="workflow-empty">No per-dataset freshness metadata is available.</p>}</section>
  </div>
}

function V2Fundamentals({analysis,data}:{analysis:InvestmentAnalysisV2;data:InvestmentDetailResponse}):JSX.Element{
  const factorLabels:Record<string,string>={operatingQuality:"Operating quality",durabilityProxy:"Historical durability",financialResilience:"Financial resilience",valuation:"Valuation"};
  return <>
    <section className="report-card"><h3>V2 factor coverage</h3><p className="buy-levels-note">Points are not rescaled when evidence is missing. Covered points show how much of each factor could be evaluated.</p><div className="analysis-factor-grid">{Object.entries(analysis.factors).map(([key,factor])=><article className="analysis-factor-card" key={key}><span>{factorLabels[key]||key.replace(/([A-Z])/g," $1")}</span><strong>{factor.earned.toFixed(1)} / {factor.maximum.toFixed(0)}</strong><small>{factor.covered.toFixed(1)} points covered</small></article>)}</div></section>
    <section className="kpi-rail-grid" aria-label="Current reported fundamentals"><KPI label="Revenue TTM" value={formatCurrency(data.summary.revenueTtm)}/><KPI label="Free Cash Flow TTM" value={formatCurrency(data.summary.fcfTtm)}/><KPI label="FCF Margin TTM" value={formatPercent(data.summary.fcfMarginTtm)}/><KPI label="Debt / FCF" value={formatRatio(data.summary.debtToFcfTtm)}/><KPI label="R&D / Revenue" value={formatPercentNullable(analysis.researchAndDevelopmentToRevenue)}/><KPI label="Diluted Share Change (1Y)" value={formatPercentNullable(analysis.shareCountChange1y)}/></section>
    <section className="kpi-rail-grid" aria-label="Current reported valuation multiples"><KPI label="P / E (TTM)" value={formatRatio(data.summary.pERatioTtm)}/><KPI label="P / FCF (TTM)" value={formatRatio(data.summary.pFcfTtm)}/><KPI label="EV / FCF (TTM)" value={formatRatio(data.summary.evFcfTtm)}/><KPI label="EV / EBITDA (TTM)" value={formatRatio(data.summary.evEbitdaTtm)}/></section>
    <section className="report-card"><h3>Reported and pro-forma reconciliation</h3><div className="report-grid three-up report-grid-dense"><KeyValue label="Reported Revenue TTM" value={formatCurrency(analysis.reportedSummary.revenueTtm)}/><KeyValue label="Pro Forma Revenue TTM" value={formatCurrency(analysis.proFormaSummary.revenueTtm)}/><KeyValue label="Reported Enterprise Value" value={formatCurrency(analysis.reportedSummary.enterpriseValue)}/><KeyValue label="Pro Forma Enterprise Value" value={formatCurrency(analysis.proFormaSummary.enterpriseValue)}/><KeyValue label="Reported FCF TTM" value={formatCurrency(analysis.reportedSummary.fcfTtm)}/><KeyValue label="Pro Forma FCF TTM" value={formatCurrency(analysis.proFormaSummary.fcfTtm)}/></div>{analysis.reconciliation.length?<ul className="report-list">{analysis.reconciliation.map(item=><li key={item.id}>{item.explanation}</li>)}</ul>:<p className="buy-levels-note">No pro-forma adjustments are active.</p>}</section>
    <section className="report-card"><h3>Observation dates</h3><div className="report-grid two-up report-grid-dense"><KeyValue label="Market" value={data.observationDates?.market||data.asOfDate}/><KeyValue label="Income Statement" value={data.observationDates?.incomeStatement||data.asOfDate}/><KeyValue label="Cash Flow Statement" value={data.observationDates?.cashFlowStatement||data.asOfDate}/><KeyValue label="Balance Sheet" value={data.observationDates?.balanceSheet||data.asOfDate}/></div></section>
  </>
}

function CuratedResearchEvidence({analysis}:{analysis:InvestmentAnalysisV2}):JSX.Element|null{
  const research=analysis.research;if(!research)return null;
  const severityRank:Record<string,number>={high:0,medium:1,low:2};
  const risks=[...research.risks].sort((left,right)=>(severityRank[left.severity.toLowerCase()]??3)-(severityRank[right.severity.toLowerCase()]??3)||left.id.localeCompare(right.id));
  return <section className="report-card biopharma-research-card"><h3>{analysis.model==="biopharma"?"Biopharma research overlay":"Curated research overlay"}</h3><p className="buy-levels-note">Sourced research judgments remain separate from measured accounting facts. Review by {research.reviewBy}. Status: {analysis.researchStale?"STALE":"CURRENT"}.</p><div className="memo-insight-grid"><div className="memo-insight-card"><h4>Material risks</h4><ul>{risks.map(risk=><li key={risk.id}><strong>{risk.severity.toUpperCase()} · {risk.state.toUpperCase()}</strong>: {risk.explanation}{risk.effectiveDate?` Effective ${risk.effectiveDate}.`:""}</li>)}</ul></div><div className="memo-insight-card"><h4>Counter-evidence</h4><ul>{research.counterEvidence.map(item=><li key={item.id}>{item.explanation}</li>)}</ul></div></div>{research.concentrations?.length?<div className="memo-insight-card"><h4>Product concentration</h4><ul>{research.concentrations.map(item=><li key={`${item.product}-${item.periodEnd}`}>{item.product}: {formatPercentNullable(item.revenueShare)} of revenue for the period ended {item.periodEnd}.</li>)}</ul></div>:null}<p className="buy-levels-note">Sources: {research.sources.map((source,index)=><React.Fragment key={source.id}>{index>0?" · ":""}<a href={source.url} target="_blank" rel="noreferrer">{source.title}</a></React.Fragment>)}</p></section>
}

const InvestmentTickerView = (): JSX.Element => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ticker = (params.ticker || "").toUpperCase();
  const [state, dispatch] = React.useReducer(equityDetailReducer, undefined, createInitialEquityDetailState);
  const {
    etfTab,
    buyMode,
    buyHorizon,
    buyReverseScenario,
    exportDrawerOpen,
    exportSection,
    exportFormat,
    exportStatus,
    refreshMessage,
    refreshError,
  } = state;
  const activeTab = normalizeDetailTab(searchParams.get("tab"));
  const valuationView = normalizeValuationView(searchParams.get("view"));
  const comparableMetric = normalizeComparableMetric(searchParams.get("metric"));
  const mode = normalizeSeriesMode(searchParams.get("period"));
  const catalogQuery = useInstrumentCatalogQuery(ticker);
  const detailQuery = useEquityDetailQuery(ticker, catalogQuery.data?.instrumentType !== "ETF");
  const comparablesQuery = useEquityComparablesQuery(ticker, comparableMetric, activeTab==="valuation"&&valuationView==="comparables"&&catalogQuery.data?.instrumentType!=="ETF");
  const eventsQuery = useInvestmentEventsQuery(ticker?[ticker]:[],ticker!==""&&catalogQuery.data?.instrumentType!=="ETF");
  const accessQuery = useInvestmentsAccessQuery();
  const isGod = accessQuery.data?.isGod === true;
  const watchlistQuery = useInvestmentsWatchlistQuery(isGod);
  const updatePortfolioMutation = useUpdatePortfolioMutation();
  const canUseFmp = accessQuery.data?.canUseFmp === true;
  const quoteRestricted = accessQuery.data?.authenticated === true && accessQuery.data?.authorized === true && !canUseFmp;
  const quoteQuery = useInvestmentQuoteQuery(ticker, canUseFmp);
  const refreshMutation = useRefreshInvestmentMutation();
  const data = detailQuery.data ?? null;
  const detailDateMeta = data ? getDetailDateMeta(data.refreshedAt, data.asOfDate) : null;
  const quote = quoteQuery.data ?? null;
  const loading = ticker === "" ? false : (catalogQuery.isLoading || detailQuery.isLoading);
  const error = ticker === ""
    ? "Ticker is missing"
    : catalogQuery.error
      ? buildQueryErrorMessage(catalogQuery.error, "Failed to resolve instrument")
      : detailQuery.error
      ? buildQueryErrorMessage(detailQuery.error, "Failed to load ticker details")
      : "";
  const quoteLoading = ticker === "" ? false : quoteQuery.isLoading;
  const quoteError = ticker === ""
    ? "Ticker is missing"
    : quoteRestricted
      ? ""
    : quoteQuery.error
      ? buildQueryErrorMessage(quoteQuery.error, "Quote data unavailable")
      : "";
  const quoteLastSuccessAt = quoteQuery.data ? new Date(quoteQuery.dataUpdatedAt).toISOString() : null;
  const refreshing = refreshMutation.isPending;
  const backHref = isGod ? "/investments" : "/investments/coverage";
  const backLabel = isGod ? "Back to Command" : "Back to Coverage";
  const watchlistPortfolios = watchlistQuery.data?.portfolios ?? [];
  const [activePortfolioID] = useActivePortfolioSelection(watchlistPortfolios.map((portfolio) => portfolio.id));
  const activePortfolio = React.useMemo(
    () => watchlistPortfolios.find((portfolio) => portfolio.id === activePortfolioID) ?? null,
    [watchlistPortfolios, activePortfolioID],
  );
  const activePortfolioRow = React.useMemo(
    () => activePortfolio?.rows.find((row) => row.ticker.toUpperCase() === ticker) ?? null,
    [activePortfolio, ticker],
  );
  const [workflowModalOpen, setWorkflowModalOpen] = React.useState(false);
  const [portfolioStageDraft, setPortfolioStageDraft] = React.useState<PortfolioPositionStage>("ACTIVE_RESEARCH");
  const [portfolioActionError, setPortfolioActionError] = React.useState("");
  const [portfolioActionMessage, setPortfolioActionMessage] = React.useState("");
  useModalKeyboard(exportDrawerOpen, () => dispatch({ type: "closeExportDrawer" }));
  useModalKeyboard(workflowModalOpen, () => setWorkflowModalOpen(false));

  React.useEffect(() => {
    if (catalogQuery.data?.instrumentType === "ETF") {
      navigate(`/investments/etf/${ticker}`, { replace: true });
    }
  }, [catalogQuery.data, navigate, ticker]);

  React.useEffect(() => {
    dispatch({ type: "tickerChanged" });
  }, [ticker]);

  React.useEffect(() => {
    setPortfolioStageDraft(activePortfolioRow?.stage ?? "ACTIVE_RESEARCH");
    setPortfolioActionError("");
    setPortfolioActionMessage("");
  }, [activePortfolio?.id, activePortfolioRow?.stage, ticker]);

  React.useEffect(() => {
    setWorkflowModalOpen(false);
  }, [ticker]);

  const series = React.useMemo<InvestmentSeriesPoint[]>(() => {
    return buildSortedSeries(data, mode);
  }, [data, mode]);

  const isETF = data?.instrumentType === "ETF";
  const report = !isETF && hasEquityReportShape(data?.report) ? data.report : null;
  const etfData = data?.etf;
  const buyLevels = report?.buyLevels;
  const companyDescription = (data?.companyDescription || "").trim();
  const displayEVFcf = report?.valuationAndDiscipline?.valuation?.evFcfTtm ?? null;
  const displayPFcf = report?.valuationAndDiscipline?.valuation?.pFcfTtm ?? null;
  const displayEVEbitda = report?.valuationAndDiscipline?.valuation?.evEbitdaTtm ?? null;
  const metricWarnings = data?.summary?.metricWarnings || {};
  const rateBaseline = data?.ratesBaseline ?? null;
  const fundamentalsPlainText = React.useMemo(() => {
    if (!data || !report) {
      return "";
    }
    return buildCopyFriendlyFundamentals(data);
  }, [data, report]);
  const fundamentalsMarkdown = React.useMemo(() => {
    if (!data || !report) {
      return "";
    }
    return buildCopyFriendlyFundamentalsMarkdown(data);
  }, [data, report]);
  const chartSeries = React.useMemo<ChartSeriesPoint[]>(() => {
    if (!report) {
      return series.map((point) => ({
        ...point,
        capexToCfo: Number.NaN,
        relativeGrossMarginPp: Number.NaN,
        relativeOperatingMarginPp: Number.NaN,
        relativeFcfMarginPp: Number.NaN,
      }));
    }
    const grossMedian = report.businessQuality.margins.industryMedianGrossMargin;
    const operatingMedian = report.businessQuality.margins.industryMedianOperatingMargin;
    const fcfMedian = report.businessQuality.margins.industryMedianFcfMargin;
    return series.map((point) => ({
      ...point,
      capexToCfo: point.cfo > 0 ? Math.abs(point.capex) / point.cfo * 100 : Number.NaN,
      relativeGrossMarginPp: grossMedian !== null && typeof point.grossMargin === "number" ? (point.grossMargin - grossMedian) * 100 : Number.NaN,
      relativeOperatingMarginPp: operatingMedian !== null && typeof point.operatingMargin === "number" ? (point.operatingMargin - operatingMedian) * 100 : Number.NaN,
      relativeFcfMarginPp: fcfMedian !== null && typeof point.fcfMargin === "number" ? (point.fcfMargin - fcfMedian) * 100 : Number.NaN,
    }));
  }, [series, report]);
  const detailConfidence = React.useMemo<DataConfidenceLabel>(() => {
    if (!report) {
      return "LOW";
    }
	if (report.analysisV2) {
	  return report.analysisV2.dataConfidence;
	}
    const warningCount = report.warnings?.length || 0;
    const weakCoverage = report.benchmarkPeerCount < 3 || report.benchmarkIndustryCount < 3;
    if (warningCount >= 4 || weakCoverage) {
      return "LOW";
    }
    if (warningCount >= 1) {
      return "MEDIUM";
    }
    return "HIGH";
  }, [report]);
  const memoStrengths = React.useMemo<string[]>(() => {
    if (!data || !report) {
      return [];
    }
    const strengths: string[] = [];
    const operatingFactor = report.analysisV2?.factors.operatingQuality;
    if (operatingFactor ? operatingFactor.earned >= 25 : report.businessQuality.score >= 45) {
      strengths.push(report.analysisV2 ? `Operating quality contributes ${formatScore(operatingFactor?.earned || 0, 35)} to the conservative screen.` : `Business quality is robust at ${formatScore(report.businessQuality.score, 65)}.`);
    }
    const durabilityFactor = report.analysisV2?.factors.durabilityProxy;
    if (durabilityFactor ? durabilityFactor.earned >= 15 : report.moatProxy.score >= 15) {
      strengths.push(report.analysisV2 ? `Historical durability proxy contributes ${formatScore(durabilityFactor?.earned || 0, 20)}; it is not proof of a moat.` : `Moat proxy scores strong at ${formatScore(report.moatProxy.score, 20)}.`);
    }
    if (data.summary.fcfMarginTtm >= 0.15) {
      strengths.push(`FCF margin remains healthy at ${formatPercent(data.summary.fcfMarginTtm)}.`);
    }
    if (data.summary.debtToFcfTtm > 0 && data.summary.debtToFcfTtm <= 2) {
      strengths.push(`Debt burden is contained at ${formatRatio(data.summary.debtToFcfTtm)} debt/FCF.`);
    }
    if (report.valuationAndDiscipline.adjustedScore >= 20) {
      strengths.push(`Valuation and discipline score is supportive at ${formatScore(report.valuationAndDiscipline.adjustedScore, 35)}.`);
    }
    if (strengths.length === 0) {
      strengths.push(`Final action rationale: ${report.finalActionMapping.reasons[0] || "No positive catalysts were identified."}`);
    }
    return strengths.slice(0, 2);
  }, [data, report]);
  const memoWatchouts = React.useMemo<string[]>(() => {
    if (!data || !report) {
      return [];
    }
    const watchouts: string[] = [];
	for (const risk of report.analysisV2?.research?.risks || []) {
	  if (risk.state.toLowerCase() === "open") {
		watchouts.push(`${risk.severity.toUpperCase()}: ${risk.explanation}`);
	  }
	}
    for (const redFlag of report.redFlags || []) {
      if (redFlag.triggered) {
        watchouts.push(`${redFlag.label} (${redFlag.evidence || "evidence unavailable"}).`);
      }
    }
    for (const warning of report.warnings || []) {
      watchouts.push(warning);
    }
    if (displayEVFcf !== null && displayEVFcf > 50) {
      watchouts.push(`EV/FCF is elevated at ${formatRatio(displayEVFcf)}.`);
    }
    if (data.summary.debtToFcfTtm > 4) {
      watchouts.push(`Debt/FCF is high at ${formatRatio(data.summary.debtToFcfTtm)}.`);
    }
    if (watchouts.length === 0) {
	  watchouts.push(report.analysisV2
		? "No model-detected accounting red flags were found; this does not establish that material business risks are absent."
		: "No material warning signals are currently flagged by the model.");
    }
	return dedupeStrings(watchouts);
  }, [data, report, displayEVFcf]);
  const groupedWarnings = React.useMemo(() => {
    const groups: Record<string, string[]> = {
      Coverage: [],
      Metrics: [],
      Provider: [],
      Other: [],
    };
    for (const warning of report?.warnings || []) {
      const normalized = warning.toLowerCase();
      if (normalized.includes("peer") || normalized.includes("benchmark")) {
        groups.Coverage.push(warning);
        continue;
      }
      if (normalized.includes("fmp") || normalized.includes("provider") || normalized.includes("subscription")) {
        groups.Provider.push(warning);
        continue;
      }
      if (normalized.includes("metric") || normalized.includes("margin") || normalized.includes("calculated")) {
        groups.Metrics.push(warning);
        continue;
      }
      groups.Other.push(warning);
    }
    return Object.entries(groups).filter(([, entries]) => entries.length > 0);
  }, [report]);
  const etfPriceSeries = React.useMemo(() => {
    if (!etfData?.historical) {
      return [];
    }
    return [...etfData.historical]
      .filter((point) => point.date && point.close !== null && point.close !== undefined)
      .map((point) => ({
        date: point.date,
        close: point.close as number,
        volume: point.volume ?? null,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [etfData]);

  const etfReturns = React.useMemo(() => {
    return {
      oneMonth: computeTrailingReturn(etfPriceSeries, 21),
      threeMonth: computeTrailingReturn(etfPriceSeries, 63),
      sixMonth: computeTrailingReturn(etfPriceSeries, 126),
      oneYear: computeTrailingReturn(etfPriceSeries, 252),
    };
  }, [etfPriceSeries]);

  React.useEffect(() => {
    dispatch({
      type: "buyLevelsChanged",
      defaultMode: buyLevels?.defaultMode,
    });
  }, [buyLevels, ticker]);

  const entryMode = buyLevels?.defaultMode || "STATIC";
  const entryModeView = React.useMemo(() => {
    if (!buyLevels) {
      return null;
    }
    return entryMode === "ADJUSTED" ? buyLevels.adjusted : buyLevels.static;
  }, [buyLevels, entryMode]);
  const entryHorizon = React.useMemo<BuyLevelsHorizon>(() => {
    if (!entryModeView) {
      return "3Y";
    }
    return entryModeView.dcf.defaultHorizon === "5Y" ? "5Y" : "3Y";
  }, [entryModeView]);
  const entryHorizonData = React.useMemo(() => {
    if (!entryModeView) {
      return null;
    }
    return entryHorizon === "5Y" ? entryModeView.dcf.horizon5y : entryModeView.dcf.horizon3y;
  }, [entryModeView, entryHorizon]);
  const entryBaseScenario = React.useMemo(
    () => entryHorizonData ? pickScenario(entryHorizonData, "BASE") ?? null : null,
    [entryHorizonData],
  );
  const entryGaugeMetrics = React.useMemo(
    () => computeBuyLevelsGauge(buyLevels?.currentPrice ?? null, entryBaseScenario?.fairValuePrice ?? null),
    [buyLevels?.currentPrice, entryBaseScenario],
  );
  const buyModeView = React.useMemo(() => {
    if (!buyLevels) {
      return null;
    }
    return buyMode === "ADJUSTED" ? buyLevels.adjusted : buyLevels.static;
  }, [buyLevels, buyMode]);
  const dcfHorizonData = React.useMemo(() => {
    if (!buyModeView) {
      return null;
    }
    return buyHorizon === "5Y" ? buyModeView.dcf.horizon5y : buyModeView.dcf.horizon3y;
  }, [buyModeView, buyHorizon]);
  const dcfBaseScenario = React.useMemo(
    () => dcfHorizonData ? pickScenario(dcfHorizonData, "BASE") ?? null : null,
    [dcfHorizonData],
  );
  const [customDCF, setCustomDCF] = React.useState<CustomDCFState>({
    growthRate: 0,
    discountRate: 0,
    terminalGrowth: 0,
    horizon: "3Y",
  });

  React.useEffect(() => {
    if (!dcfBaseScenario) {
      return;
    }
    setCustomDCF({
      growthRate: dcfBaseScenario.growthRate * 100,
      discountRate: dcfBaseScenario.discountRate * 100,
      terminalGrowth: dcfBaseScenario.terminalGrowth * 100,
      horizon: buyHorizon,
    });
  }, [dcfBaseScenario, buyHorizon, ticker]);

  const customDCFResult = React.useMemo(
    () => computeCustomDCFScenario({
      fcfTtm: data?.summary?.fcfTtm ?? null,
      netDebt: data?.summary?.netDebt ?? null,
      sharesOutstanding: buyLevels?.sharesOutstanding ?? null,
      horizonYears: customDCF.horizon === "5Y" ? 5 : 3,
      growthRatePct: customDCF.growthRate,
      discountRatePct: customDCF.discountRate,
      terminalGrowthPct: customDCF.terminalGrowth,
    }),
    [buyLevels?.sharesOutstanding, customDCF, data?.summary?.fcfTtm, data?.summary?.netDebt],
  );
  const overviewBuyTarget = React.useMemo(
    () => entryModeView ? impliedTargetPrice(entryModeView.impliedPrices, "BUY") : null,
    [entryModeView],
  );
  const overviewWatchTarget = React.useMemo(
    () => entryModeView ? impliedTargetPrice(entryModeView.impliedPrices, "WATCHLIST") : null,
    [entryModeView],
  );
  const riskNarrative = React.useMemo(() => {
    if (!data || !report) {
      return [];
    }
    const notes: string[] = [...memoWatchouts];
    if (report.analysisV2?.dataConfidence === "LOW") {
	  notes.push("Evidence confidence is low, so the quantitative screen cannot support an autonomous candidate disposition.");
    } else if (!report.analysisV2 && report.moatConfidence.band === "FRAGILE") {
      notes.push("Moat confidence is fragile, so underwriting needs less forgiveness on execution and valuation.");
    }
    if (rateBaseline?.currentSpreadToTenYear !== null && rateBaseline?.buyThresholdSpreadToTenYear !== null) {
      const spreadDeltaBp = rateBaseline.currentSpreadToTenYear - rateBaseline.buyThresholdSpreadToTenYear;
      if (spreadDeltaBp < 0) {
        notes.push(`Current FCF yield still trails the buy hurdle by ${formatBasisPointsNullable(Math.abs(spreadDeltaBp))} versus the 10Y baseline.`);
      } else {
        notes.push(`Rates baseline is supportive by ${formatBasisPointsNullable(spreadDeltaBp)} versus the 10Y buy hurdle, so company-specific risk matters more than macro drag.`);
      }
    }
    if (buyLevels?.warnings && buyLevels.warnings.length > 0) {
      notes.push(`Model warnings remain active across ${buyLevels.warnings.length} buy-level input check${buyLevels.warnings.length === 1 ? "" : "s"}.`);
    }
    return dedupeStrings(notes).slice(0, 4);
  }, [buyLevels?.warnings, data, memoWatchouts, rateBaseline, report]);
  const riskReverseMetrics = React.useMemo(
	() => reverseMetricsFromAnalysis(report?.analysisV2) || (entryHorizonData ? computeReverseDCFHeuristic(buyLevels?.currentPrice ?? null, entryHorizonData, pickScenario(entryHorizonData, "BASE")) : null),
	[buyLevels?.currentPrice, entryHorizonData, report?.analysisV2],
  );
	const curatedScenarioValuationRequired = Boolean(report?.analysisV2?.model === "biopharma" && report.analysisV2.scenarioValuations.length === 0);
  const exportSections = React.useMemo<Record<EquityDetailExportFormat, Record<EquityDetailExportSection, string>>>(() => {
    if (!data || !report) {
      return {
        plain: {
          overview: "",
          fundamentals: "",
          entry: "",
          risk: "",
          "full-memo": "",
        },
        markdown: {
          overview: "",
          fundamentals: "",
          entry: "",
          risk: "",
          "full-memo": "",
        },
      };
    }
    const overviewPlain = buildEquityOverviewExport({
      data,
      report,
      detailConfidence,
      memoStrengths,
      overviewBuyTarget,
      overviewWatchTarget,
      activePortfolioRow,
    });
    const overviewMarkdown = buildEquityOverviewExportMarkdown({
      data,
      report,
      detailConfidence,
      memoStrengths,
      overviewBuyTarget,
      overviewWatchTarget,
      activePortfolioRow,
    });
    const entryPlain = buildEquityEntryExport({
      data,
      report,
      buyLevels,
      view: entryModeView,
      mode: entryMode,
      horizon: entryHorizon,
      baseScenario: entryBaseScenario,
      gaugeMetrics: entryGaugeMetrics,
      overviewBuyTarget,
      overviewWatchTarget,
    });
    const entryMarkdown = buildEquityEntryExportMarkdown({
      data,
      report,
      buyLevels,
      view: entryModeView,
      mode: entryMode,
      horizon: entryHorizon,
      baseScenario: entryBaseScenario,
      gaugeMetrics: entryGaugeMetrics,
      overviewBuyTarget,
      overviewWatchTarget,
    });
    const riskPlain = buildEquityRiskExport({
      data,
      report,
      detailConfidence,
      memoWatchouts,
      riskNarrative,
      groupedWarnings,
      buyLevels,
      rateBaseline,
      reverseMetrics: riskReverseMetrics,
    });
    const riskMarkdown = buildEquityRiskExportMarkdown({
      data,
      report,
      detailConfidence,
      memoWatchouts,
      riskNarrative,
      groupedWarnings,
      buyLevels,
      rateBaseline,
      reverseMetrics: riskReverseMetrics,
    });

    return {
      plain: {
        overview: overviewPlain,
        fundamentals: fundamentalsPlainText,
        entry: entryPlain,
        risk: riskPlain,
        "full-memo": [overviewPlain, fundamentalsPlainText, entryPlain, riskPlain].filter(Boolean).join("\n\n"),
      },
      markdown: {
        overview: overviewMarkdown,
        fundamentals: fundamentalsMarkdown,
        entry: entryMarkdown,
        risk: riskMarkdown,
        "full-memo": [overviewMarkdown, fundamentalsMarkdown, entryMarkdown, riskMarkdown].filter(Boolean).join("\n\n"),
      },
    };
  }, [
    activePortfolioRow,
    buyLevels,
    data,
    detailConfidence,
    entryBaseScenario,
    entryGaugeMetrics,
    entryHorizon,
    entryMode,
    entryModeView,
    fundamentalsMarkdown,
    fundamentalsPlainText,
    groupedWarnings,
    memoStrengths,
    memoWatchouts,
    overviewBuyTarget,
    overviewWatchTarget,
    rateBaseline,
    report,
    riskNarrative,
    riskReverseMetrics,
  ]);
  const activeExportText = exportSections[exportFormat][exportSection];

  async function handleRefreshTicker(): Promise<void> {
    if (!ticker || refreshing || !canUseFmp) {
      return;
    }
    dispatch({ type: "clearRefreshFeedback" });
    try {
      const result = await refreshMutation.mutateAsync(ticker);
      dispatch({
        type: "setRefreshMessage",
        value: `Refresh complete (${result.target}): synced=${result.sync.updated ? "updated" : "already-fresh"}, uploaded=${result.bucketSync.uploaded} file(s).`,
      });
      await Promise.all([detailQuery.refetch(), quoteQuery.refetch()]);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Ticker refresh failed";
      dispatch({ type: "setRefreshError", value: message });
    }
  }

  function setDetailTab(tab: DetailTab): void {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("tab", tab);
      if (tab !== "valuation") next.delete("view");
      if (tab !== "history") next.delete("period");
      return next;
    });
  }

  function setValuationView(view: "entry" | "comparables" | "dcf"): void {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("tab", "valuation");
      next.set("view", view);
	  if(view!=="comparables")next.delete("metric");
      return next;
    });
  }

  function setComparableMetric(metric: ComparableMetric): void {
	setSearchParams((current)=>{const next=new URLSearchParams(current);next.set("tab","valuation");next.set("view","comparables");next.set("metric",metric);return next;});
  }

  function setHistoryPeriod(period: SeriesMode): void {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("tab", "history");
      next.set("period", period);
      return next;
    });
  }

  async function handleSavePortfolioStage(): Promise<void> {
    if (!isGod || activePortfolio == null || updatePortfolioMutation.isPending) {
      return;
    }
    setPortfolioActionError("");
    setPortfolioActionMessage("");

    const existingIndex = activePortfolio.positions.findIndex((position) => position.ticker.toUpperCase() === ticker);
    if (existingIndex < 0 && activePortfolio.positions.length >= INVESTMENTS_PORTFOLIO_POSITION_LIMIT) {
      setPortfolioActionError(
        `Active portfolio already has ${INVESTMENTS_PORTFOLIO_POSITION_LIMIT} positions. Open Portfolio to remove one before adding ${ticker}.`,
      );
      return;
    }

    const nextPositions = existingIndex >= 0
      ? activePortfolio.positions.map((position) => (
        position.ticker.toUpperCase() === ticker
          ? {
            ...position,
            stage: portfolioStageDraft,
            holdingState: holdingStateForPortfolioStage(portfolioStageDraft),
          }
          : position
      ))
      : activePortfolio.positions.concat({
        ticker,
        stage: portfolioStageDraft,
        holdingState: holdingStateForPortfolioStage(portfolioStageDraft),
      });

    try {
      const updatedPortfolio = await updatePortfolioMutation.mutateAsync({
        portfolioID: activePortfolio.id,
        payload: {
          name: activePortfolio.name,
          notes: activePortfolio.notes,
          positions: nextPositions,
        },
      });
      setPortfolioActionMessage(
        existingIndex >= 0
          ? `Updated ${ticker} in ${updatedPortfolio.name}.`
          : `Added ${ticker} to ${updatedPortfolio.name}.`,
      );
    } catch (err) {
      setPortfolioActionError(err instanceof Error ? err.message : "Failed to update portfolio workflow.");
    }
  }

  return (
    <div className="investments-page">
      <div className="investments-shell">
        <header className="investments-header">
          <div>
            <h1>{isETF ? `${ticker || "Ticker"} ETF` : `${ticker || "Ticker"} Research`}</h1>
            <p>{data?.companyName || "Investment detail view"} {detailDateMeta?.value && detailDateMeta.value !== "N/A" ? `• ${detailDateMeta.inlineLabel} ${detailDateMeta.value}` : ""}</p>
          </div>
          <div className="investments-header-right">
            <div className="investments-header-toolbar">
              <div className="investments-header-actions">
                <button
                  className="investments-link investments-link-button"
                  type="button"
                  disabled={loading || !!error || !data || isETF || !report}
                  onClick={() => dispatch({ type: "openExportDrawer", section: activeTabToExportSection(activeTab) })}
                >
                  Export
                </button>
                {canUseFmp && (
                  <button
                    className="investments-link investments-link-button"
                    type="button"
                    disabled={refreshing || loading || !!error}
                    onClick={() => void handleRefreshTicker()}
                  >
                    {refreshing ? "Refreshing..." : "Refresh Ticker"}
                  </button>
                )}
                <Link className="investments-link" to={backHref}>{backLabel}</Link>
              </div>
              <InvestmentsAccountMenu />
            </div>
            <div className="investments-header-note">
              {quoteRestricted ? "Snapshot Research uses cached data only. " : ""}
              <Link className="investments-link" to="/investments/reference/method">Method</Link>
              {" · "}
              <Link className="investments-link" to="/investments/reference/glossary">Glossary</Link>
            </div>
            {canUseFmp && !loading && !error && data && report && (
              <QuoteSummaryStrip
                ticker={ticker}
                quote={quote}
                loading={quoteLoading}
                error={quoteError}
                lastSuccessAt={quoteLastSuccessAt}
                restricted={quoteRestricted}
                onRefresh={() => void quoteQuery.refetch()}
                compact
              />
            )}
          </div>
        </header>

        <InvestmentsSubnav isOperator={isGod} />

        {refreshMessage && <div className="ticker-refresh-status" role="status" aria-live="polite">{refreshMessage}</div>}
        {refreshError && <div className="ticker-refresh-status error" role="alert">{refreshError}</div>}

        {exportDrawerOpen && !loading && !error && data && !isETF && report && (
          <>
            <button
              type="button"
              className="detail-export-drawer-backdrop"
              aria-label="Close export drawer"
              onClick={() => dispatch({ type: "closeExportDrawer" })}
            />
            <aside className="detail-export-drawer" role="dialog" aria-modal="true" aria-label="Ticker export drawer">
              <div className="detail-export-drawer-header">
                <div>
                  <h2>Export Ticker Memo</h2>
                  <p>{data.companyName} • {ticker}</p>
                </div>
                <button
                  type="button"
                  className="detail-export-close"
                  aria-label="Close export drawer"
                  onClick={() => dispatch({ type: "closeExportDrawer" })}
                >
                  Close
                </button>
              </div>
              <div className="detail-export-toolbar">
                <div className="fundamentals-view-toggle detail-export-section-toggle">
                  <button
                    type="button"
                    className={exportSection === "overview" ? "active" : ""}
                    onClick={() => dispatch({ type: "setExportSection", value: "overview" })}
                  >
                    Overview
                  </button>
                  <button
                    type="button"
                    className={exportSection === "fundamentals" ? "active" : ""}
                    onClick={() => dispatch({ type: "setExportSection", value: "fundamentals" })}
                  >
                    Fundamentals
                  </button>
                  <button
                    type="button"
                    className={exportSection === "entry" ? "active" : ""}
                    onClick={() => dispatch({ type: "setExportSection", value: "entry" })}
                  >
                    Entry
                  </button>
                  <button
                    type="button"
                    className={exportSection === "risk" ? "active" : ""}
                    onClick={() => dispatch({ type: "setExportSection", value: "risk" })}
                  >
                    Risk
                  </button>
                  <button
                    type="button"
                    className={exportSection === "full-memo" ? "active" : ""}
                    onClick={() => dispatch({ type: "setExportSection", value: "full-memo" })}
                  >
                    Full Memo
                  </button>
                </div>
              </div>
              <div className="detail-export-toolbar detail-export-toolbar-secondary">
                <div className="copy-visual-toggle">
                  <button
                    type="button"
                    className={exportFormat === "plain" ? "active" : ""}
                    onClick={() => dispatch({ type: "setExportFormat", value: "plain" })}
                  >
                    Plain Text
                  </button>
                  <button
                    type="button"
                    className={exportFormat === "markdown" ? "active" : ""}
                    onClick={() => dispatch({ type: "setExportFormat", value: "markdown" })}
                  >
                    Markdown
                  </button>
                </div>
                <button
                  type="button"
                  className="copy-friendly-btn"
                  onClick={() => void copyToClipboard(activeExportText, (value) => dispatch({ type: "setExportStatus", value }))}
                >
                  {exportStatus || "Copy Text"}
                </button>
              </div>
              <textarea
                className="copy-friendly-text detail-export-text"
                value={activeExportText}
                readOnly
                spellCheck={false}
                onFocus={(event) => event.currentTarget.select()}
              />
            </aside>
          </>
        )}

        {workflowModalOpen && !loading && !error && data && !isETF && report && isGod && (
          <div
            className="watchlist-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !updatePortfolioMutation.isPending) {
                setWorkflowModalOpen(false);
              }
            }}
          >
            <section
              className="investments-card watchlist-modal detail-workflow-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="detail-workflow-modal-title"
            >
              <div className="watchlist-modal-header">
                <div className="watchlist-panel-header">
                  <div>
                    <div className="section-eyebrow">Operator Mode</div>
                    <h2 id="detail-workflow-modal-title">Portfolio Workflow</h2>
                    <p>
                      {activePortfolio
                        ? `${activePortfolio.name} is the active portfolio for direct workflow updates from this detail page.`
                        : "Create a portfolio in Operator Mode before sending this name into workflow."}
                    </p>
                  </div>
                </div>
                <div className="detail-workflow-modal-actions">
                  <Link className="workflow-header-controls-button" to="/investments/portfolio">
                    Open Portfolio
                  </Link>
                  <button
                    type="button"
                    className="watchlist-modal-close"
                    onClick={() => setWorkflowModalOpen(false)}
                    disabled={updatePortfolioMutation.isPending}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="detail-workflow-modal-body">
                {activePortfolio == null ? (
                  <div className="workflow-empty">No active portfolio selected yet.</div>
                ) : (
                  <>
                    <div className="portfolio-workflow-metrics">
                      <div className="portfolio-workflow-metric">
                        <span>Portfolio</span>
                        <strong>{activePortfolio.name}</strong>
                      </div>
                      <div className="portfolio-workflow-metric">
                        <span>Current Stage</span>
                        <strong>{activePortfolioRow ? activePortfolioRow.stage.replace(/_/g, " ") : "Not in portfolio"}</strong>
                      </div>
                      <div className="portfolio-workflow-metric">
                        <span>Recommended Action</span>
                        <strong>{activePortfolioRow?.recommendedAction.replace(/_/g, " ") || "N/A"}</strong>
                      </div>
                      <div className="portfolio-workflow-metric">
                        <span>Status</span>
                        <strong>{activePortfolioRow ? "Tracked in active portfolio" : "Ready to add"}</strong>
                      </div>
                    </div>
                    <div className="portfolio-workflow-form">
                      <label className="watchlist-field">
                        <span>Portfolio Stage</span>
                        <select
                          className="watchlist-select"
                          value={portfolioStageDraft}
                          onChange={(event) => setPortfolioStageDraft(event.target.value as PortfolioPositionStage)}
                        >
                          <option value="ACTIVE_RESEARCH">Active Research</option>
                          <option value="CANDIDATE">Candidate</option>
                          <option value="OWNED">Owned</option>
                        </select>
                      </label>
                    </div>
                    {portfolioActionError && <div className="ticker-refresh-status error">{portfolioActionError}</div>}
                    {portfolioActionMessage && <div className="ticker-refresh-status">{portfolioActionMessage}</div>}
                    <div className="watchlist-editor-actions">
                      <button
                        type="button"
                        className="watchlist-editor-submit-btn"
                        onClick={() => void handleSavePortfolioStage()}
                        disabled={updatePortfolioMutation.isPending}
                      >
                        {updatePortfolioMutation.isPending
                          ? "Saving..."
                          : activePortfolioRow
                            ? "Update Portfolio Stage"
                            : "Add To Active Portfolio"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>
        )}

        {loading && <InvestmentsPageState message="Loading ticker data..." />}
        {!loading && error && <InvestmentsPageState message={`Failed to load: ${error}`} />}

        {!loading && !error && data && isETF && (
          <>
            <div className="investments-tab-toggle">
              <button className={etfTab === "overview" ? "active" : ""} onClick={() => dispatch({ type: "setETFTab", value: "overview" })}>
                Overview
              </button>
              <button className={etfTab === "composition" ? "active" : ""} onClick={() => dispatch({ type: "setETFTab", value: "composition" })}>
                Composition
              </button>
              <button className={etfTab === "performance" ? "active" : ""} onClick={() => dispatch({ type: "setETFTab", value: "performance" })}>
                Performance
              </button>
            </div>

            <CollapsibleDescriptionCard title="What This ETF Does" description={companyDescription} />

            <section className="kpi-rail-grid">
              <KPI label="Asset Class" value={etfData?.profile?.assetClass || "N/A"} />
              <KPI label="ETF Company" value={etfData?.profile?.etfCompany || "N/A"} />
              <KPI label="Expense Ratio" value={formatETFPercentValue(etfData?.profile?.expenseRatio ?? null)} />
              <KPI label="AUM" value={formatCurrencyNullable(etfData?.profile?.assetsUnderManagement ?? null)} />
              <KPI label="NAV" value={formatCurrencyNullable(etfData?.profile?.nav ?? null)} />
              <KPI label="Avg Volume" value={formatNumberCompact(etfData?.profile?.avgVolume ?? null)} />
              <KPI label="Holdings" value={formatNumberCompact(etfData?.profile?.holdingsCount ?? null)} />
              <KPI label="Domicile" value={etfData?.profile?.domicile || "N/A"} />
              <KPI label="ISIN" value={etfData?.profile?.isin || "N/A"} />
              <KPI label="Inception Date" value={etfData?.profile?.inceptionDate || "N/A"} />
              <KPI label="Live Price" value={formatCurrencyNullable(quote?.price ?? null)} />
              <KPI label="Daily Move" value={`${formatSignedCurrencyNullable(quote?.change ?? null)} (${formatSignedPercentNullable(quote?.changePercent ?? null)})`} />
            </section>

            {etfData?.warnings && etfData.warnings.length > 0 && (
              <details className="warning-banner">
                <summary>
                  <span>ETF Data Warnings</span>
                  <span className="warning-count">{etfData.warnings.length}</span>
                </summary>
                <div className="warning-groups">
                  <div className="warning-group">
                    <h4>Provider Coverage</h4>
                    <ul>
                      {etfData.warnings.map((warning: string) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </details>
            )}

            {etfTab === "overview" && (
              <section className="report-card">
                <h3>ETF Profile</h3>
                <div className="report-grid two-up report-grid-dense">
                  <KeyValue label="Name" value={data.companyName || "N/A"} />
                  <KeyValue label="Symbol" value={etfData?.profile?.symbol || data.ticker} />
                  <KeyValue label="Asset Class" value={etfData?.profile?.assetClass || "N/A"} />
                  <KeyValue label="ETF Company" value={etfData?.profile?.etfCompany || "N/A"} />
                  <KeyValue label="Expense Ratio" value={formatETFPercentValue(etfData?.profile?.expenseRatio ?? null)} />
                  <KeyValue label="Assets Under Management" value={formatCurrencyNullable(etfData?.profile?.assetsUnderManagement ?? null)} />
                  <KeyValue label="NAV" value={formatCurrencyNullable(etfData?.profile?.nav ?? null)} />
                  <KeyValue label="NAV Currency" value={etfData?.profile?.navCurrency || "N/A"} />
                  <KeyValue label="Average Volume" value={formatNumberCompact(etfData?.profile?.avgVolume ?? null)} />
                  <KeyValue label="Holdings Count" value={formatNumberCompact(etfData?.profile?.holdingsCount ?? null)} />
                  <KeyValue label="ISIN" value={etfData?.profile?.isin || "N/A"} />
                  <KeyValue label="CUSIP" value={etfData?.profile?.securityCusip || "N/A"} />
                  <KeyValue label="Domicile" value={etfData?.profile?.domicile || "N/A"} />
                  <KeyValue label="Inception Date" value={etfData?.profile?.inceptionDate || "N/A"} />
                  <KeyValue label="Actively Trading" value={etfData?.profile?.isActivelyTrading === undefined ? "N/A" : boolLabel(Boolean(etfData.profile.isActivelyTrading))} />
                  <KeyValue label={detailDateMeta?.fieldLabel ?? "Refreshed Date"} value={detailDateMeta?.value ?? "N/A"} />
                </div>
                {etfData?.profile?.website && (
                  <p className="buy-levels-note">
                    Source: <a href={etfData.profile.website} target="_blank" rel="noreferrer">{etfData.profile.website}</a>
                  </p>
                )}
              </section>
            )}

            {etfTab === "composition" && (
              <section className="report-card buy-levels-panel">
                <div className="buy-levels-panel-section">
                  <h3>Sector Weights</h3>
                  <div className="buy-levels-table-wrap">
                    <table className="buy-levels-table">
                      <thead>
                        <tr>
                          <th>Sector</th>
                          <th className="numeric-col">Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(etfData?.sectorWeights || []).map((weight: any) => (
                          <tr key={`sector-${weight.name}`}>
                            <td>{weight.name}</td>
                            <td className="numeric-col">{formatETFPercentValue(weight.weightPercentage ?? null)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="buy-levels-panel-section">
                  <h3>Country Weights</h3>
                  <div className="buy-levels-table-wrap">
                    <table className="buy-levels-table">
                      <thead>
                        <tr>
                          <th>Country</th>
                          <th className="numeric-col">Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(etfData?.countryWeights || []).map((weight: any) => (
                          <tr key={`country-${weight.name}`}>
                            <td>{weight.name}</td>
                            <td className="numeric-col">{formatETFPercentValue(weight.weightPercentage ?? null)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </section>
            )}

            {etfTab === "performance" && (
              <>
                <section className="kpi-rail-grid">
                  <KPI label="1M Return" value={formatSignedPercentValueNullable(etfReturns.oneMonth)} />
                  <KPI label="3M Return" value={formatSignedPercentValueNullable(etfReturns.threeMonth)} />
                  <KPI label="6M Return" value={formatSignedPercentValueNullable(etfReturns.sixMonth)} />
                  <KPI label="1Y Return" value={formatSignedPercentValueNullable(etfReturns.oneYear)} />
                </section>
                <section className="report-card">
                  <h3>Price History (1Y)</h3>
                  {etfPriceSeries.length > 0 ? (
                    <div className="investments-chart-scroll">
                      <div className="investments-chart-min">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={etfPriceSeries}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a3552" />
                            <XAxis dataKey="date" tick={{ fill: "#c4d3f2", fontSize: 11 }} />
                            <YAxis tick={{ fill: "#c4d3f2", fontSize: 11 }} tickFormatter={(value) => formatMetricAxisTick(Number(value), "currency")} />
                            <Tooltip formatter={(value: unknown) => (typeof value === "number" ? formatCurrency(value) : "N/A")} />
                            <Line type="monotone" dataKey="close" stroke="#60a5fa" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="chart-empty">No ETF price history available.</div>
                  )}
                </section>
              </>
            )}
          </>
        )}

        {!loading && !error && data && !isETF && report && (
          <>
            <div className="investments-tab-toggle" role="tablist" aria-label="Ticker research sections" onKeyDown={(event) => handleDetailTabKeyDown(event, activeTab, setDetailTab)}>
              <button type="button" role="tab" aria-selected={activeTab === "summary"} tabIndex={activeTab === "summary" ? 0 : -1} className={activeTab === "summary" ? "active" : ""} onClick={() => setDetailTab("summary")}>
                Summary
              </button>
              <button type="button" role="tab" aria-selected={activeTab === "fundamentals"} tabIndex={activeTab === "fundamentals" ? 0 : -1} className={activeTab === "fundamentals" ? "active" : ""} onClick={() => setDetailTab("fundamentals")}>
                Fundamentals
              </button>
              <button type="button" role="tab" aria-selected={activeTab === "valuation"} tabIndex={activeTab === "valuation" ? 0 : -1} className={activeTab === "valuation" ? "active" : ""} onClick={() => setDetailTab("valuation")}>
                Valuation
              </button>
              <button type="button" role="tab" aria-selected={activeTab === "risk"} tabIndex={activeTab === "risk" ? 0 : -1} className={activeTab === "risk" ? "active" : ""} onClick={() => setDetailTab("risk")}>
                Risks &amp; Evidence
              </button>
              <button type="button" role="tab" aria-selected={activeTab === "history"} tabIndex={activeTab === "history" ? 0 : -1} className={activeTab === "history" ? "active" : ""} onClick={() => setDetailTab("history")}>
                History
              </button>
            </div>

            {isGod && (
              <section className="investments-card workflow-card detail-operator-summary-card">
                <div className="detail-operator-summary-header">
                  <div>
                    <div className="section-eyebrow">Operator Mode</div>
                    <h3>Workflow Summary</h3>
                    <p>
                      {activePortfolio
                        ? `${activePortfolio.name} is the active portfolio for this ticker.`
                        : "No active portfolio selected yet."}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="workflow-header-controls-button"
                    onClick={() => setWorkflowModalOpen(true)}
                  >
                    Open Workflow
                  </button>
                </div>
                <div className="detail-operator-summary-metrics">
                  <div className="portfolio-workflow-metric">
                    <span>Portfolio</span>
                    <strong>{activePortfolio?.name || "None selected"}</strong>
                  </div>
                  <div className="portfolio-workflow-metric">
                    <span>Stage</span>
                    <strong>{activePortfolioRow ? activePortfolioRow.stage.replace(/_/g, " ") : "Not in portfolio"}</strong>
                  </div>
                  <div className="portfolio-workflow-metric">
                    <span>Recommended Action</span>
                    <strong>{activePortfolioRow?.recommendedAction.replace(/_/g, " ") || "N/A"}</strong>
                  </div>
                  <div className="portfolio-workflow-metric">
                    <span>Status</span>
                    <strong>{activePortfolioRow ? "Tracked in active portfolio" : "Ready to add"}</strong>
                  </div>
                </div>
              </section>
            )}

            {activeTab === "valuation" && (
              <nav className="valuation-view-nav" aria-label="Valuation views">
                <button type="button" className={valuationView === "entry" ? "active" : ""} aria-current={valuationView === "entry" ? "page" : undefined} onClick={() => setValuationView("entry")}>House Hurdle</button>
                <button type="button" className={valuationView === "comparables" ? "active" : ""} aria-current={valuationView === "comparables" ? "page" : undefined} onClick={() => setValuationView("comparables")}>Comparables</button>
                <button type="button" className={valuationView === "dcf" ? "active" : ""} aria-current={valuationView === "dcf" ? "page" : undefined} onClick={() => setValuationView("dcf")}>DCF lab</button>
                <span>Both views use the same cached facts. Custom scenarios never change the research disposition.</span>
              </nav>
            )}

            {activeTab === "summary" && (
              <>
                <section className="memo-header-card">
                  <div className="memo-header-top">
                    <div className="memo-title-block">
                      <div className="memo-title">Summary</div>
                      <div className="memo-chip-row">
						<span className={`action-chip action-${(report.analysisV2?.screening.disposition || report.finalActionMapping.resolvedAction).toLowerCase()}`}>
						  {(report.analysisV2?.screening.disposition || report.finalActionMapping.resolvedAction).replace(/_/g, " ")}
                        </span>
                        <span className={`action-chip zone-${data.summary.valuationZone.toLowerCase()}`}>
                          {data.summary.valuationZone}
                        </span>
                        <span className={`action-chip confidence-${detailConfidence.toLowerCase()}`}>
                          Data Confidence: {detailConfidence}
                        </span>
                        {activePortfolioRow && (
                          <span className={`action-chip portfolio-action-${activePortfolioRow.recommendedAction.toLowerCase().replace(/_/g, "-")}`}>
                            {activePortfolioRow.recommendedAction.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="memo-score-block">
					  <div className="memo-score-label">Quantitative Screen</div>
					  <div className="memo-score-value">{formatScore(report.analysisV2?.coverage.conservativeScore ?? report.scoringSummary.normalizedScore100, 100)}</div>
                    </div>
                  </div>
                  <div className="memo-insight-grid">
                    <div className="memo-insight-card">
					  <h3>Evidence and durability</h3>
                      <ul>
						<li>{report.analysisV2 ? `Evidence coverage is ${report.analysisV2.coverage.coveragePercent.toFixed(0)}%, with a possible score range of ${report.analysisV2.coverage.possibleLow.toFixed(0)}–${report.analysisV2.coverage.possibleHigh.toFixed(0)}.` : `Business quality scores ${formatScore(report.businessQuality.score, 65)}.`}</li>
						<li>{report.analysisV2 ? `Historical durability proxy is ${formatScore(report.analysisV2.factors.durabilityProxy?.earned || 0, 20)}; it is not proof of a competitive moat.` : `Moat confidence is ${report.moatConfidence.band}.`}</li>
                        <li>{activePortfolioRow ? `Active portfolio stage: ${activePortfolioRow.stage.replace(/_/g, " ")}.` : "Not currently in the active portfolio."}</li>
                      </ul>
                    </div>
                    <div className="memo-insight-card">
                      <h3>Action</h3>
                      <ul>
                        <li>{activePortfolioRow?.recommendedActionReason || report.analysisV2?.screening.reasons[0] || report.finalActionMapping.reasons[0] || "Research disposition is available in the quantitative screen."}</li>
                        <li>Entry framework: EV/FCF overlay.</li>
						<li>{overviewBuyTarget != null ? `House multiple threshold price is ${formatCurrencyNullable(overviewBuyTarget)}; it is mechanical, not intrinsic value.` : "House multiple threshold price is unavailable."}</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <CollapsibleDescriptionCard title="What The Company Does" description={companyDescription} />

                {!report.analysisV2 && <div className="legacy-analysis-banner" role="note">Legacy V1 analysis: publish and reload a V2 runtime policy to use evidence-aware scoring and research dispositions.</div>}

				{report.analysisV2 && (
				  <section className="report-card analysis-v2-card">
					<h3>Evidence-aware analysis</h3>
					<p className="buy-levels-note">
					  Model: {report.analysisV2.model.replace(/-/g, " ")} · Generated {new Date(report.analysisV2.generatedAt).toLocaleDateString()}
					</p>
					<div className="report-grid two-up report-grid-dense">
					  <KeyValue label="Disposition" value={report.analysisV2.screening.disposition.replace(/_/g, " ")} />
					  <KeyValue label="Evidence Coverage" value={`${report.analysisV2.coverage.coveragePercent.toFixed(0)}%`} />
					  <KeyValue label="Possible Score Range" value={`${report.analysisV2.coverage.possibleLow.toFixed(0)}–${report.analysisV2.coverage.possibleHigh.toFixed(0)}`} />
					  <KeyValue label="Analysis Model" value={`${report.analysisV2.model.replace(/-/g, " ")}${report.analysisV2.modelSupported ? "" : " · unsupported"}`} />
					  <KeyValue label="Effective EV / FCF Ceiling" value={formatRatioNullable(report.analysisV2.rateAwareValuation.effectiveMaximumEvFcf)} />
					  <KeyValue label="Current FCF Yield Spread" value={formatBasisPointsNullable(report.analysisV2.rateAwareValuation.currentFcfYieldSpreadBps)} />
					  <KeyValue label="10Y Treasury" value={formatPercentValueNullable(report.analysisV2.rateAwareValuation.tenYearYield)} />
					  <KeyValue label="Treasury Observation" value={report.analysisV2.rateAwareValuation.tenYearAsOfDate || "N/A"} />
					  <KeyValue label="Market Data Date" value={data.observationDates?.market || data.asOfDate} />
					  <KeyValue label="Balance Sheet Date" value={data.observationDates?.balanceSheet || data.asOfDate} />
					</div>
				  </section>
				)}

                <section className="kpi-rail-grid">
                  <KPI label="Current Price" value={formatCurrencyNullable(quote?.price ?? buyLevels?.currentPrice ?? null)} />
                  {report.analysisV2 ? <KPI label="Conservative Score" value={formatScore(report.analysisV2.coverage.conservativeScore, 100)} /> : <KPI label="Legacy Business Quality" value={formatScore(report.businessQuality.score, 65)} />}
				  <KPI label="Historical Durability Proxy" value={formatScore(report.analysisV2?.factors.durabilityProxy?.earned ?? report.moatProxy.score, 20)} />
                  {report.analysisV2 && <KPI label="Evidence Coverage" value={`${report.analysisV2.coverage.coveragePercent.toFixed(0)}%`} />}
                  <KPI label="Valuation Now" value={zoneLabel(data.summary.valuationZone)} />
				  <KPI label="Screening Disposition" value={(report.analysisV2?.screening.disposition || report.finalActionMapping.resolvedAction).replace(/_/g, " ")} />
                  <KPI label="Recommended Action" value={activePortfolioRow?.recommendedAction.replace(/_/g, " ") || "N/A"} />
                  <KPI label="Portfolio Stage" value={activePortfolioRow?.stage.replace(/_/g, " ") || "N/A"} />
				  <KPI label="House Multiple Threshold" value={formatCurrencyNullable(overviewBuyTarget)} />
                  <KPI label="Watch Target" value={formatCurrencyNullable(overviewWatchTarget)} />
                  <KPI label="Next Catalyst" value={eventsQuery.data?.events[0] ? `${eventsQuery.data.events[0].date} · ${eventsQuery.data.events[0].type}` : "None in cached window"} />
                </section>

                {report.warnings && report.warnings.length > 0 && (
                  <details className="warning-banner">
                    <summary>
                      <span>Data Warnings</span>
                      <span className="warning-count">{report.warnings.length}</span>
                    </summary>
                    <div className="warning-groups">
                      {groupedWarnings.map(([group, entries]) => (
                        <div className="warning-group" key={group}>
                          <h4>{group}</h4>
                          <ul>
                            {entries.map((warning: string) => (
                              <li key={warning}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                <div className="memo-insight-grid">
                  <section className="report-card">
                    <h3>Top Strengths</h3>
                    <ul className="report-list">
                      {memoStrengths.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </section>
                  <section className="report-card">
                    <h3>Decision Snapshot</h3>
                    <ul className="report-list">
					  <li>{activePortfolioRow?.recommendedActionReason || report.analysisV2?.screening.reasons[0] || report.finalActionMapping.reasons[0] || "Research disposition is available in the quantitative screen."}</li>
						<li>{overviewBuyTarget != null ? `House multiple threshold price is ${formatCurrencyNullable(overviewBuyTarget)}.` : "House multiple threshold price is unavailable."}</li>
                      <li>{overviewWatchTarget != null ? `Watch target is ${formatCurrencyNullable(overviewWatchTarget)}.` : "Watch target is unavailable."}</li>
                    </ul>
                  </section>
                </div>
              </>
            )}

            {activeTab === "fundamentals" && (
              <>
                <section className="memo-header-card">
                  <div className="memo-header-top">
                    <div className="memo-title-block">
                      <div className="memo-title">Fundamentals</div>
                      <div className="memo-chip-row">
						<span className={`action-chip action-${displayScreeningDisposition(report).toLowerCase()}`}>
						  {displayScreeningDisposition(report).replace(/_/g, " ")}
                        </span>
                        <span className={`action-chip zone-${data.summary.valuationZone.toLowerCase()}`}>
                          {data.summary.valuationZone}
                        </span>
                        <span className={`action-chip confidence-${detailConfidence.toLowerCase()}`}>
                          Data Confidence: {detailConfidence}
                        </span>
                      </div>
                    </div>
                    <div className="memo-score-block">
                      <div className="memo-score-label">{report.analysisV2 ? "Conservative Score" : "Legacy Normalized Score"}</div>
					  <div className="memo-score-value">{formatScore(displayQuantitativeScore(report), 100)}</div>
                    </div>
                  </div>
                  <div className="memo-insight-grid">
                    <div className="memo-insight-card">
                      <h3>Strengths</h3>
                      <ul>
                        {memoStrengths.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="memo-insight-card">
                      <h3>Watchouts</h3>
                      <ul>
                        {memoWatchouts.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>

                {report.analysisV2 ? <V2Fundamentals analysis={report.analysisV2} data={data} /> : <>
                <div className="legacy-analysis-banner" role="note">Legacy V1 analysis: this cached report predates evidence-aware V2 scoring.</div>
                <section className="kpi-rail-grid">
                  <KPI label="EV / FCF (TTM)" value={formatRatioNullable(displayEVFcf)} warning={metricWarnings.evFcfTtm} />
                  <KPI label="P / FCF (TTM)" value={formatRatioNullable(displayPFcf)} warning={metricWarnings.pFcfTtm} />
                  <KPI label="EV / EBITDA (TTM)" value={formatRatioNullable(displayEVEbitda)} warning={metricWarnings.evEbitdaTtm} />
                  <KPI label="FCF Margin (TTM)" value={formatPercent(data.summary.fcfMarginTtm)} />
                  <KPI label="Debt / FCF" value={formatRatio(data.summary.debtToFcfTtm)} />
				  <KPI label={report.analysisV2 ? "Evidence Confidence" : "Moat Band"} value={report.analysisV2?.dataConfidence || report.moatConfidence.band} />
				  <KPI label="Quantitative Screen" value={formatScore(displayQuantitativeScore(report), 100)} />
                  <KPI label="Business Score" value={formatScore(report.scoringSummary.businessQualityScore, 65)} />
				  <KPI label={report.analysisV2 ? "Historical Durability" : "Moat Score"} value={formatScore(report.analysisV2?.factors.durabilityProxy?.earned ?? report.scoringSummary.moatProxyScore, 20)} />
                  <KPI label="Valuation Score" value={formatScore(report.scoringSummary.valuationScoreAdjusted, 35)} />
                  <KPI label="Zone" value={zoneLabel(data.summary.valuationZone)} />
				  <KPI label="Screening Disposition" value={displayScreeningDisposition(report).replace(/_/g, " ")} />
                </section>

                {report.warnings && report.warnings.length > 0 && (
                  <details className="warning-banner">
                    <summary>
                      <span>Data Warnings</span>
                      <span className="warning-count">{report.warnings.length}</span>
                    </summary>
                    <div className="warning-groups">
                      {groupedWarnings.map(([group, entries]) => (
                        <div className="warning-group" key={group}>
                          <h4>{group}</h4>
                          <ul>
                            {entries.map((warning: string) => (
                              <li key={warning}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                  <section className="report-card">
                      <h3>Business Quality</h3>
                      <div className="report-grid two-up report-grid-dense">
                        <KeyValue label="Revenue TTM" value={formatCurrency(report.businessQuality.revenue.revenueTtm)} />
                        <KeyValue label="Revenue YoY (TTM)" value={formatPercentNullable(report.businessQuality.revenue.revenueYoYTtm)} />
                        <KeyValue label="Revenue 3Y CAGR" value={formatPercentNullable(report.businessQuality.revenue.revenue3yCagr)} />
                        <KeyValue label="Gross Margin" value={formatPercentNullable(report.businessQuality.margins.grossMargin)} />
                        <KeyValue label="Industry Gross Margin" value={formatPercentNullable(report.businessQuality.margins.industryMedianGrossMargin)} />
                        <KeyValue label="Relative Gross Margin" value={formatPP(report.businessQuality.margins.relativeGrossMarginPp)} />
                        <KeyValue label="Operating Margin" value={formatPercentNullable(report.businessQuality.margins.operatingMargin)} />
                        <KeyValue label="Industry Operating Margin" value={formatPercentNullable(report.businessQuality.margins.industryMedianOperatingMargin)} />
                        <KeyValue label="Relative Operating Margin" value={formatPP(report.businessQuality.margins.relativeOperatingMarginPp)} />
                        <KeyValue label="FCF Margin" value={formatPercentNullable(report.businessQuality.margins.fcfMargin)} />
                        <KeyValue label="Industry FCF Margin" value={formatPercentNullable(report.businessQuality.margins.industryMedianFcfMargin)} />
                        <KeyValue label="Relative FCF Margin" value={formatPP(report.businessQuality.margins.relativeFcfMarginPp)} />
                        <KeyValue label="CFO (TTM)" value={formatCurrency(report.businessQuality.cashFlow.cfoTtm)} />
                        <KeyValue label="CapEx (TTM)" value={formatCurrency(report.businessQuality.cashFlow.capexTtm)} />
                        <KeyValue label="FCF (TTM)" value={formatCurrency(report.businessQuality.cashFlow.fcfTtm)} />
                        <KeyValue label="ROIC (TTM)" value={formatPercentValueNullable(report.businessQuality.capitalEfficiency.roicTtm)} />
                        <KeyValue label="ROE (TTM)" value={formatPercentValueNullable(report.businessQuality.capitalEfficiency.roeTtm)} />
                        <KeyValue label="Debt / FCF" value={formatRatio(report.businessQuality.balanceSheet.debtToFcfTtm)} />
                        <KeyValue label="Current Ratio" value={formatNumberNullable(report.businessQuality.balanceSheet.currentRatio)} />
                        <KeyValue label="Business Quality Score" value={formatScore(report.businessQuality.score, 65)} />
                      </div>
                    </section>

					<section className="report-card">
					  <h3>{report.analysisV2 ? "Historical Durability Proxy" : "Buffett Moat Proxy"}</h3>
					  {report.analysisV2 && <p className="buy-levels-note">A backward-looking consistency screen, not proof of a competitive moat.</p>}
                      <div className="report-grid two-up report-grid-dense">
                        <SignalValue label="ROIC persistence" signal={report.moatProxy.signals.roicPersistence} />
                        <SignalValue label="Margin stability" signal={report.moatProxy.signals.marginStability} />
                        <SignalValue label="Revenue durability vs peers" signal={report.moatProxy.signals.revenueDurability} />
						{!report.analysisV2 && <SignalValue label="Reinvestment efficiency" signal={report.moatProxy.signals.reinvestmentEfficiency} />}
                        <KeyValue label="ROIC persistence points" value={`${report.moatProxy.breakdown.roicPersistencePoints} / 8`} />
                        <KeyValue label="Margin stability points" value={`${report.moatProxy.breakdown.marginStabilityPoints} / 5`} />
                        <KeyValue label="Revenue durability points" value={`${report.moatProxy.breakdown.revenueDurabilityPoints} / 4`} />
						{!report.analysisV2 && <KeyValue label="Reinvestment efficiency points" value={`${report.moatProxy.breakdown.reinvestmentEfficiencyPoints} / 3`} />}
						<KeyValue label={report.analysisV2 ? "Historical durability score" : "Moat Proxy Score"} value={formatScore(report.analysisV2?.factors.durabilityProxy?.earned ?? report.moatProxy.score, 20)} />
                      </div>
                    </section>

                    <section className="report-card">
					  <h3>{report.analysisV2 ? "Evidence Confidence" : "Moat Confidence Band"}</h3>
                      <div className="report-grid two-up">
						<KeyValue label="Band" value={report.analysisV2?.dataConfidence || report.moatConfidence.band} />
                        <KeyValue label="Benchmarked peers" value={`${report.benchmarkIndustryCount}/${report.benchmarkPeerCount}`} />
                      </div>
                      <ul className="report-list">
						{(report.analysisV2?.screening.reasons || report.moatConfidence.justification).map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </section>

                    <section className="report-card">
                      <h3>FCF Regime Shift Analysis</h3>
                      <div className="report-grid two-up report-grid-dense">
                        <KeyValue label="CapEx / CFO" value={formatPercentValueNullable(report.fcfRegimeShift.capexToCfo)} />
                        <KeyValue label="3Y Trend" value={report.fcfRegimeShift.trend3y} />
                        <KeyValue label="Maintenance-like" value={boolLabel(report.fcfRegimeShift.maintenanceLike)} />
                        <KeyValue label="Growth-optional" value={boolLabel(report.fcfRegimeShift.growthOptional)} />
                        <KeyValue label="Structurally rising" value={boolLabel(report.fcfRegimeShift.structurallyRising)} />
                        <KeyValue label="Regime" value={`${report.fcfRegimeShift.regime} (${report.fcfRegimeShift.penaltyPoints} pts)`} />
                      </div>
                      <ul className="report-list">
                        {report.fcfRegimeShift.justification.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </section>

                    <section className="report-card">
                      <h3>Valuation & Capital Discipline</h3>
                      <div className="report-grid two-up report-grid-dense">
                        <KeyValue label="Market Cap" value={formatCurrency(report.valuationAndDiscipline.marketData.marketCap)} />
                        <KeyValue label="Enterprise Value" value={formatCurrency(report.valuationAndDiscipline.marketData.enterpriseValue)} />
                        <KeyValue label="P/E (TTM)" value={formatRatioNullable(report.valuationAndDiscipline.valuation.pERatioTtm)} />
                        <KeyValue label="P/FCF (TTM)" value={formatRatioNullable(report.valuationAndDiscipline.valuation.pFcfTtm)} />
                        <KeyValue label="EV/FCF (TTM)" value={formatRatioNullable(report.valuationAndDiscipline.valuation.evFcfTtm)} />
                        <KeyValue label="EV/EBITDA (TTM)" value={formatRatioNullable(report.valuationAndDiscipline.valuation.evEbitdaTtm)} />
                        <KeyValue label="Dividend payout ratio" value={formatPercentValueNullable(report.valuationAndDiscipline.capitalAllocation.dividendPayoutRatio)} />
                        <KeyValue label="Buybacks (TTM)" value={formatCurrencyNullable(report.valuationAndDiscipline.capitalAllocation.buybacksTtm)} />
                        <KeyValue label="Net share trend (3Y)" value={report.valuationAndDiscipline.capitalAllocation.netShareCountTrend3y} />
                        <KeyValue label="Net share CAGR (3Y)" value={formatPercentNullable(report.valuationAndDiscipline.capitalAllocation.netShareCountCagr3y)} />
                        <KeyValue label="CapEx / CFO" value={formatPercentValueNullable(report.valuationAndDiscipline.capitalAllocation.capexToCfo)} />
                        <KeyValue label="Net debt discipline" value={report.valuationAndDiscipline.capitalAllocation.netDebtDisciplineNote || "N/A"} />
                        <KeyValue label="Pre-penalty score" value={formatScore(report.valuationAndDiscipline.prePenaltyScore, 35)} />
                        <KeyValue label="Penalty" value={`${report.valuationAndDiscipline.penaltyPoints}`} />
                        <KeyValue label="Adjusted score" value={formatScore(report.valuationAndDiscipline.adjustedScore, 35)} />
                      </div>
                    </section>

                    <section className="report-card">
                      <h3>Automatic Red Flags</h3>
                      <div className="report-grid red-flags-grid">
                        {(report.redFlags || []).map((flag) => (
                          <div key={flag.key} className={`red-flag ${flag.triggered ? "is-on" : "is-off"}`}>
                            <span className="red-flag-label">{flag.triggered ? "TRIGGERED" : "CLEAR"}</span>
                            <span>{flag.label}</span>
                            <span className="red-flag-evidence">{flag.evidence || "No evidence available"}</span>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className="report-card">
                      <h3>Scoring Summary</h3>
                      <div className="report-grid two-up report-grid-dense">
                        <KeyValue label="Business Quality" value={formatScore(report.scoringSummary.businessQualityScore, 65)} />
						<KeyValue label={report.analysisV2 ? "Historical Durability" : "Moat Proxy"} value={formatScore(report.analysisV2?.factors.durabilityProxy?.earned ?? report.scoringSummary.moatProxyScore, 20)} />
                        <KeyValue label="Valuation & Discipline" value={formatScore(report.scoringSummary.valuationScoreAdjusted, 35)} />
                        <KeyValue label="Total" value={formatScore(report.scoringSummary.totalScore120, 120)} />
						<KeyValue label="Conservative Screen" value={formatScore(displayQuantitativeScore(report), 100)} />
                      </div>
                    </section>

                    <section className="report-card">
					  <h3>{report.analysisV2 ? "House Multiple Thresholds" : "Buy-Price Overlay"}</h3>
					  {report.analysisV2 && <p className="buy-levels-note">Mechanical prices at selected EV/FCF multiples; these are not intrinsic values.</p>}
                      <div className="report-grid overlay-summary-grid">
                        <KeyValue label="Current EV/FCF" value={formatRatio(report.buyPriceOverlay.evFcfTtm)} />
                        <KeyValue label="Current Zone" value={report.buyPriceOverlay.currentZone} />
                      </div>
                      <div className="report-grid overlay-band-grid">
                        {report.buyPriceOverlay.bands.map((band: BuyPriceOverlayBandData) => (
                          <div key={band.label} className="overlay-band">
                            <strong>{entryLevelLabel(band.label)}</strong>
                            <div>{formatBandRange(band.min, band.max)}</div>
                            <div className="muted">{band.description}</div>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className="report-card">
					  <h3>{report.analysisV2 ? "Research Disposition" : "Final Action Mapping"}</h3>
                      <div className="report-grid final-action-grid">
						<KeyValue label="Screening Disposition" value={displayScreeningDisposition(report).replace(/_/g, " ")} />
						{!report.analysisV2 && <KeyValue label="Moat Band" value={report.finalActionMapping.moatBand} />}
                        <KeyValue label="Valuation Zone" value={report.finalActionMapping.valuationZone} />
                        <KeyValue label="Score Used" value={formatScore(report.finalActionMapping.score, 100)} />
                      </div>
                      <ul className="report-list">
                        {report.finalActionMapping.reasons.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                  </section>
                </>}
              </>
            )}

            {activeTab === "history" && (
              <HistoryDashboard
                mode={mode}
                series={series}
                chartSeries={chartSeries}
                onPeriodChange={setHistoryPeriod}
              />
            )}

            {activeTab === "valuation" && valuationView === "entry" && buyLevels && entryModeView && entryHorizonData && (
              <div className="buy-levels-shell">
                <section className="memo-header-card entry-decision-card">
                  <div className="memo-header-top">
                    <div className="memo-title-block">
                      <div className="memo-title"><SectionHeading label="Entry" slug="entry-discipline" /></div>
                      <div className="memo-chip-row">
						<span className={`action-chip action-${displayScreeningDisposition(report).toLowerCase()}`}>
						  {displayScreeningDisposition(report).replace(/_/g, " ")}
                        </span>
                        <span className={`action-chip zone-${report.buyPriceOverlay.currentZone.toLowerCase()}`}>
                          {report.buyPriceOverlay.currentZone}
                        </span>
                        <GlossaryTerm
                          label={entryMode === "ADJUSTED" ? "Adjusted Overlay" : "Static Overlay"}
                          slug="ev-fcf-overlay"
                          variant="inline"
                          className="action-chip confidence-high action-chip-link"
                        />
                      </div>
                    </div>
                    <div className="memo-score-block">
                      <div className="memo-score-label"><InlineGlossaryLabel label="House Frame" slug="dcf-horizon" /></div>
                      <div className="memo-score-value">{entryHorizon}</div>
                    </div>
                  </div>
                  <div className="memo-insight-grid">
                    <div className="memo-insight-card">
                      <h3><SectionHeading label="Research Disposition" slug="final-action" /></h3>
                      <ul>
                        <li>{activePortfolioRow?.recommendedActionReason || report.analysisV2?.screening.reasons[0] || report.finalActionMapping.reasons[0] || "Research disposition is available in the quantitative screen."}</li>
                        <li>Primary <GlossaryTerm label="Entry Framework" slug="entry-framework" /> stays <GlossaryTerm label="EV/FCF Overlay" slug="ev-fcf-overlay" />, with <GlossaryTerm label="DCF Sanity Check" slug="dcf-sanity-check" /> used only as a sanity check.</li>
                      </ul>
                    </div>
                    <div className="memo-insight-card">
                      <h3><SectionHeading label="Buyability Now" slug="buyability-now" /></h3>
                      <ul>
						<li>{overviewBuyTarget != null ? <>House multiple threshold price is {formatCurrencyNullable(overviewBuyTarget)}.</> : <>House multiple threshold price is unavailable.</>}</li>
                        <li>{overviewWatchTarget != null ? <><GlossaryTerm label="Watch Target" slug="watch-target" /> is {formatCurrencyNullable(overviewWatchTarget)}.</> : <><GlossaryTerm label="Watch Target" slug="watch-target" /> is unavailable.</>}</li>
						<li>{!curatedScenarioValuationRequired && entryGaugeMetrics.marginOfSafetyPct != null ? <>Base-case <GlossaryTerm label="Margin of Safety" slug="margin-of-safety" /> is {formatBuyLevelsSignedPercent(entryGaugeMetrics.marginOfSafetyPct)}.</> : <>Scenario margin of safety is unavailable until explicit annual FCF assumptions are curated.</>}</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section className="kpi-rail-grid entry-kpi-grid">
                  <KPI label="Current Price" value={formatCurrencyNullable(buyLevels.currentPrice ?? null)} glossarySlug="current-price" />
                  <KPI label="Current Zone" value={report.buyPriceOverlay.currentZone} glossarySlug="current-zone" />
				  <KPI label="House Multiple Threshold" value={formatCurrencyNullable(overviewBuyTarget)} glossarySlug="target-buy" />
                  <KPI label="Watch Target" value={formatCurrencyNullable(overviewWatchTarget)} glossarySlug="watch-target" />
				  <KPI label="Screening Disposition" value={displayScreeningDisposition(report).replace(/_/g, " ")} glossarySlug="final-action" />
                  <KPI label="Current EV / FCF" value={formatRatioNullable(entryModeView.multiples.currentMultiple ?? null)} glossarySlug="ev-fcf-ttm" />
                </section>

                <div className="entry-layout-grid">
                  <section className="report-card buy-levels-panel entry-framework-card">
                    <div className="buy-levels-panel-section">
                      <h3><SectionHeading label="Entry Framework" slug="entry-framework" /></h3>
					  <p className="buy-levels-note">Start with the EV/FCF house multiple thresholds and rate-aware ceiling. These are mechanical policy levels, not intrinsic-value targets. Treat the <GlossaryTerm label="DCF Sanity Check" slug="dcf-sanity-check" /> block below as a cross-check.</p>
                      <div className="report-grid two-up report-grid-dense">
                        <KeyValue label="Current EV/FCF" value={formatRatioNullable(entryModeView.multiples.currentMultiple ?? null)} glossarySlug="ev-fcf-ttm" />
                        <KeyValue label="Current Zone" value={report.buyPriceOverlay.currentZone} glossarySlug="current-zone" />
                        <KeyValue label="House Mode" value={entryMode === "ADJUSTED" ? "Adjusted Overlay" : "Static Overlay"} glossarySlug="ev-fcf-overlay" />
                        <KeyValue label="DCF Sanity Horizon" value={entryHorizon} glossarySlug="dcf-horizon" />
						<KeyValue label="Fair Value (BASE)" value={curatedScenarioValuationRequired ? "Unavailable" : formatBuyLevelsCurrency(entryBaseScenario?.fairValuePrice ?? null)} glossarySlug="fair-value-per-share" />
						<KeyValue label="Valuation State" value={curatedScenarioValuationRequired ? "RESEARCH REQUIRED" : entryGaugeMetrics.state} glossarySlug="valuation-state" />
                      </div>
                    </div>
                    <details className="buy-levels-model-detail">
                      <summary>Show model detail</summary>
                      <div className="buy-levels-model-detail-body">
                        <BuyLevelsTables
                          mode={entryMode}
                          horizon={entryHorizon}
                          view={entryModeView}
                          currentPrice={buyLevels.currentPrice ?? null}
                          includeDCF={false}
                        />
                      </div>
                    </details>
                  </section>

				  <div className="entry-gauge-card-wrap">
					{curatedScenarioValuationRequired ? <section className="report-card"><h3>Scenario valuation unavailable</h3><p className="buy-levels-note">No bear, base, or bull annual FCF assumptions have been curated. The engine does not invent a biopharma forecast path.</p></section> : <BuyLevelsGaugeCard metrics={entryGaugeMetrics} />}
				  </div>

				  {!curatedScenarioValuationRequired && <div className="entry-secondary-grid">
                    <BuyLevelsAssumptionsCard summary={data.summary} selectedHorizon={entryHorizonData} baseScenario={entryBaseScenario} />
                    <section className="report-card entry-dcf-summary-card">
                      <h3><SectionHeading label="DCF Sanity Check" slug="dcf-sanity-check" /></h3>
                      <p className="buy-levels-note">Compact read-out from the base scenario only.</p>
                      <button type="button" className="copy-friendly-btn" onClick={() => setValuationView("dcf")}>Open advanced DCF Lab</button>
                      <div className="report-grid two-up report-grid-dense">
                        <KeyValue label="Growth Anchor" value={formatBuyLevelsPercentDecimal(entryHorizonData.growthAnchor ?? null)} glossarySlug="growth-anchor" />
                        <KeyValue label="Growth Source" value={entryHorizonData.growthSource || "N/A"} glossarySlug="growth-source" />
                        <KeyValue label="Discount Rate (BASE)" value={formatBuyLevelsPercentDecimal(entryBaseScenario?.discountRate ?? null)} glossarySlug="discount-rate" />
                        <KeyValue label="Terminal Growth (BASE)" value={formatBuyLevelsPercentDecimal(entryBaseScenario?.terminalGrowth ?? null)} glossarySlug="terminal-growth" />
                        <KeyValue label="Fair Value / Share" value={formatBuyLevelsCurrency(entryBaseScenario?.fairValuePrice ?? null)} glossarySlug="fair-value-per-share" />
                        <KeyValue label="Modest-discount Threshold" value={formatBuyLevelsCurrency(entryBaseScenario?.entryLadder.accumulate ?? null)} glossarySlug="accumulate-threshold" />
                      </div>
                    </section>
				  </div>}
                </div>
              </div>
            )}

            {activeTab === "risk" && (
              <div className="buy-levels-shell risk-shell">
                <section className="memo-header-card risk-summary-card">
                  <div className="memo-header-top">
                    <div className="memo-title-block">
                      <div className="memo-title"><SectionHeading label="Risk" slug="risk-review" /></div>
                      <div className="memo-chip-row">
                        <span className={`action-chip confidence-${detailConfidence.toLowerCase()}`}>
                          Data Confidence: {detailConfidence}
                        </span>
						<span className="action-chip confidence-low">
						  {report.analysisV2 ? `Evidence Coverage: ${report.analysisV2.coverage.coveragePercent.toFixed(0)}%` : `Moat Band: ${report.moatConfidence.band}`}
						</span>
                        <span className={`action-chip zone-${data.summary.valuationZone.toLowerCase()}`}>
                          {data.summary.valuationZone}
                        </span>
                      </div>
                    </div>
                    <div className="memo-score-block">
					  <div className="memo-score-label">{report.analysisV2 ? "Research Disposition" : <InlineGlossaryLabel label="Moat Band" slug="moat-band" />}</div>
					  <div className="memo-score-value">{report.analysisV2?.screening.disposition.replace(/_/g, " ") || report.moatConfidence.band}</div>
                    </div>
                  </div>
                  <div className="memo-insight-grid">
                    <div className="memo-insight-card">
                      <h3><SectionHeading label="Primary Risk Framing" slug="primary-risk-framing" /></h3>
                      <ul>
                        {(report.analysisV2?.screening.reasons || riskNarrative).map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="memo-insight-card">
                      <h3><SectionHeading label={report.analysisV2 ? "Triggered Gates" : "Top Risks"} slug="top-risks" /></h3>
                      <ul>
                        {(report.analysisV2?.screening.gates || memoWatchouts).map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>

                <section className="kpi-rail-grid risk-kpi-grid">
				  <KPI label={report.analysisV2 ? "Evidence Confidence" : "Moat Band"} value={report.analysisV2?.dataConfidence || report.moatConfidence.band} glossarySlug={report.analysisV2 ? undefined : "moat-band"} />
                  <KPI label="Debt / FCF" value={formatRatio(data.summary.debtToFcfTtm)} glossarySlug="debt-fcf" />
                  <KPI label="FCF Margin (TTM)" value={formatPercent(data.summary.fcfMarginTtm)} glossarySlug="fcf-margin" />
                  <KPI label="Current EV / FCF" value={formatRatioNullable(displayEVFcf)} warning={metricWarnings.evFcfTtm} glossarySlug="ev-fcf-ttm" />
                  <KPI label="Current Zone" value={zoneLabel(data.summary.valuationZone)} glossarySlug="current-zone" />
				  <KPI label={report.analysisV2 ? "Threshold Model Warnings" : "Buy Levels Warnings"} value={String(buyLevels?.warnings?.length || 0)} glossarySlug="buy-levels-warnings" />
                </section>

                {report.warnings && report.warnings.length > 0 && (
                  <details className="warning-banner">
                    <summary>
                      <span><GlossaryTerm label="Data Warnings" term="Warnings" /></span>
                      <span className="warning-count">{report.warnings.length}</span>
                    </summary>
                    <div className="warning-groups">
                      {groupedWarnings.map(([group, entries]) => (
                        <div className="warning-group" key={group}>
                          <h4>{group}</h4>
                          <ul>
                            {entries.map((warning: string) => (
                              <li key={warning}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

					<BuyLevelsWarnings warnings={buyLevels?.warnings || []} v2={Boolean(report.analysisV2)} />

                {report.analysisV2 && <CuratedResearchEvidence analysis={report.analysisV2} />}

                <section className="report-card provider-evidence-card">
                  <h3>Provider evidence and external expectations</h3>
                  <p className="buy-levels-note">Cached FMP evidence is research context only. It does not add points to the quantitative score or determine the house disposition.</p>
                  {eventsQuery.error ? <p className="workflow-empty">Catalyst history is unavailable until the event cache is refreshed.</p> : eventsQuery.data?.events.length ? <div className="catalyst-list">{eventsQuery.data.events.slice(0, 8).map((event) => <article className="catalyst-row" key={event.id}><time>{event.date}</time><strong>{event.type}</strong><span>{event.type === "earnings" && event.epsEstimated != null ? `External EPS estimate ${event.epsEstimated.toFixed(2)}` : event.type === "dividend" && event.dividend != null ? `Dividend ${event.dividend.toFixed(2)}` : event.type === "split" ? `${event.splitNumerator ?? "—"}:${event.splitDenominator ?? "—"}` : "Details pending"}</span><a href={event.sourceUrl} target="_blank" rel="noreferrer">FMP</a></article>)}</div> : <p className="workflow-empty">No cached catalysts in the current window.</p>}
                  {data.researchContext ? <ProviderResearchContextView context={data.researchContext} /> : <p className="workflow-empty">Company evidence has not reached this ticker in the rolling deep-refresh cycle.</p>}
                </section>

                <div className="risk-detail-grid">
                  <section className="report-card risk-metrics-card">
                    <h3><SectionHeading label="Downside Indicators" slug="downside-indicators" /></h3>
                    <p className="buy-levels-note">Read these as fragility markers, not stand-alone buy triggers.</p>
                    <div className="report-grid two-up report-grid-dense">
                      <KeyValue label="Debt / FCF" value={formatRatio(data.summary.debtToFcfTtm)} glossarySlug="debt-fcf" />
                      <KeyValue label="Current Ratio" value={formatNumberNullable(report.businessQuality.balanceSheet.currentRatio)} glossarySlug="current-ratio" />
                      <KeyValue label="FCF Margin" value={formatPercentNullable(report.businessQuality.margins.fcfMargin)} glossarySlug="fcf-margin" />
                      <KeyValue label="CapEx / CFO" value={formatPercentValueNullable(report.fcfRegimeShift.capexToCfo)} glossarySlug="capex-cfo" />
                      <KeyValue label="Current EV/FCF" value={formatRatioNullable(displayEVFcf)} glossarySlug="ev-fcf-ttm" />
					  <KeyValue label="House Threshold vs 10Y" value={formatBasisPointsNullable(rateBaseline?.buyThresholdSpreadToTenYear ?? null)} glossarySlug="buy-yld-10y" />
                    </div>
                  </section>
                  <div className="risk-sidebar-stack">
                    <BuyLevelsRateBaselineCard metrics={rateBaseline} />
                    <section className="report-card risk-metrics-card">
                      <h3><SectionHeading label="Model Watchouts" slug="model-watchouts" /></h3>
                      <p className="buy-levels-note">Model limitations and counter-signals belong here; valuation diagnostics remain under Valuation.</p>
                      {!report.analysisV2 && <ul className="report-list">{riskNarrative.map((line) => <li key={line}>{line}</li>)}</ul>}
                    </section>
                  </div>
                </div>
              </div>
            )}

			{activeTab === "valuation" && valuationView === "dcf" && curatedScenarioValuationRequired && (
			  <section className="report-card"><h3>Scenario valuation unavailable</h3><p>No numeric biopharma forecast path is generated automatically. Add sourced bear, base, and bull annual FCF assumptions to the research overlay to enable scenario valuation.</p></section>
			)}

			{activeTab === "valuation" && valuationView === "comparables" && (
			  comparablesQuery.isLoading?<InvestmentsPageState message="Loading cached comparable valuation..."/>:
			  comparablesQuery.error?<section className="report-card"><h3>Comparable valuation unavailable</h3><p>{comparablesQuery.error.message}</p><p className="buy-levels-note">The rest of the company report remains available. Comparables appear after a peer candidate cache is refreshed.</p></section>:
			  comparablesQuery.data?<ComparableValuationView data={comparablesQuery.data} detail={data} metric={comparablesQuery.data.requestedMetric} onMetric={setComparableMetric}/>:null
			)}

			{activeTab === "valuation" && valuationView === "dcf" && !curatedScenarioValuationRequired && buyLevels && buyModeView && dcfHorizonData && (
              <div className="buy-levels-shell dcf-lab-shell">
                <BuyLevelsHeader buyLevels={buyLevels} />
                <section className="report-card buy-levels-panel dcf-lab-intro-card">
                  <div className="buy-levels-panel-section">
                    <h3><SectionHeading label="DCF Lab" slug="dcf-lab" /></h3>
					<p className="buy-levels-note">Custom DCF scenario. Does not change the research disposition.</p>
					<button type="button" className="copy-friendly-btn" onClick={() => setValuationView("entry")}>Back to Entry</button>
                  </div>
                </section>
                <div className="dcf-lab-grid">
                  <section className="report-card dcf-lab-house-card">
                    <h3>House Model Snapshot</h3>
					<p className="buy-levels-note">Advanced controls live here. They change the read-only house table and constant-growth diagnostic, but not the research disposition.</p>
                    <div className="dcf-lab-control-stack">
                      <div className="fundamentals-view-toggle buy-levels-view-toggle">
                        <button
                          type="button"
                          className={buyMode === "STATIC" ? "active" : ""}
                          onClick={() => dispatch({ type: "setBuyMode", value: "STATIC" })}
                        >
                          Static
                        </button>
                        <button
                          type="button"
                          className={buyMode === "ADJUSTED" ? "active" : ""}
                          onClick={() => dispatch({ type: "setBuyMode", value: "ADJUSTED" })}
                        >
                          Adjusted
                        </button>
                        <button
                          type="button"
                          className={buyHorizon === "3Y" ? "active" : ""}
                          onClick={() => dispatch({ type: "setBuyHorizon", value: "3Y" })}
                        >
                          3Y
                        </button>
                        <button
                          type="button"
                          className={buyHorizon === "5Y" ? "active" : ""}
                          onClick={() => dispatch({ type: "setBuyHorizon", value: "5Y" })}
                        >
                          5Y
                        </button>
                      </div>
                      <div className="fundamentals-view-toggle buy-levels-view-toggle">
                        <button
                          type="button"
                          className={buyReverseScenario === "BEAR" ? "active" : ""}
                          onClick={() => dispatch({ type: "setBuyReverseScenario", value: "BEAR" })}
                        >
                          Bear Reverse
                        </button>
                        <button
                          type="button"
                          className={buyReverseScenario === "BASE" ? "active" : ""}
                          onClick={() => dispatch({ type: "setBuyReverseScenario", value: "BASE" })}
                        >
                          Base Reverse
                        </button>
                        <button
                          type="button"
                          className={buyReverseScenario === "BULL" ? "active" : ""}
                          onClick={() => dispatch({ type: "setBuyReverseScenario", value: "BULL" })}
                        >
                          Bull Reverse
                        </button>
                      </div>
                    </div>
                    <BuyLevelsDCFTable view={buyModeView} horizon={buyHorizon} />
                  </section>
                  <section className="report-card dcf-lab-custom-card">
                    <div className="copy-friendly-header">
                      <h3>Custom Scenario</h3>
                      <div className="copy-friendly-actions">
                      <button type="button" className="copy-friendly-btn" onClick={() => void navigator.clipboard?.writeText(buildCustomDCFCopy(ticker, customDCF, customDCFResult))}>Copy scenario</button>
                      <button
                        type="button"
                        className="copy-friendly-btn"
                        onClick={() => {
                          if (!dcfBaseScenario) {
                            return;
                          }
                          setCustomDCF({
                            growthRate: dcfBaseScenario.growthRate * 100,
                            discountRate: dcfBaseScenario.discountRate * 100,
                            terminalGrowth: dcfBaseScenario.terminalGrowth * 100,
                            horizon: buyHorizon,
                          });
                        }}
                      >
                        Reset to House Defaults
                      </button>
                      </div>
                    </div>
                    <div className="dcf-lab-form-grid">
                      <label className="watchlist-inline-field">
                        <span><GlossaryTerm label="Growth Rate" slug="growth-rate" variant="kv" className="metric-label-text" /></span>
                        <input type="number" step="0.1" value={customDCF.growthRate} onChange={(event) => setCustomDCF((prev) => ({ ...prev, growthRate: Number(event.target.value) }))} />
                      </label>
                      <label className="watchlist-inline-field">
                        <span><GlossaryTerm label="Discount Rate" slug="discount-rate" variant="kv" className="metric-label-text" /></span>
                        <input type="number" step="0.1" value={customDCF.discountRate} onChange={(event) => setCustomDCF((prev) => ({ ...prev, discountRate: Number(event.target.value) }))} />
                      </label>
                      <label className="watchlist-inline-field">
                        <span><GlossaryTerm label="Terminal Growth" slug="terminal-growth" variant="kv" className="metric-label-text" /></span>
                        <input type="number" step="0.1" value={customDCF.terminalGrowth} onChange={(event) => setCustomDCF((prev) => ({ ...prev, terminalGrowth: Number(event.target.value) }))} />
                      </label>
                      <label className="watchlist-inline-field">
                        <span><GlossaryTerm label="Horizon" slug="dcf-horizon" variant="kv" className="metric-label-text" /></span>
                        <select value={customDCF.horizon} onChange={(event) => setCustomDCF((prev) => ({ ...prev, horizon: event.target.value as BuyLevelsHorizon }))}>
                          <option value="3Y">3Y</option>
                          <option value="5Y">5Y</option>
                        </select>
                      </label>
                    </div>
                    <div className="report-grid two-up report-grid-dense dcf-lab-result-grid">
                      <KeyValue label="FCF TTM" value={formatCurrency(data.summary.fcfTtm)} glossarySlug="fcf-ttm" />
                      <KeyValue label="Net Debt" value={formatCurrency(data.summary.netDebt)} glossarySlug="net-debt" />
                      <KeyValue label="Weighted Avg Shares" value={formatNumberNullable(buyLevels.sharesOutstanding ?? null)} glossarySlug="weighted-avg-shares" />
                      <KeyValue label="Fair EV" value={formatCurrencyNullable(customDCFResult.fairValueEv)} glossarySlug="enterprise-value" />
                      <KeyValue label="Fair Value / Share" value={formatCurrencyNullable(customDCFResult.fairValuePrice)} glossarySlug="fair-value-per-share" />
                      <KeyValue label="Validity" value={customDCFResult.valid ? "Valid" : "Invalid"} />
                      <KeyValue label="Deep-discount Price" value={formatCurrencyNullable(customDCFResult.entryLadder.strongBuy)} />
                      <KeyValue label="Standard-discount Price" value={formatCurrencyNullable(customDCFResult.entryLadder.buy)} />
                      <KeyValue label="Modest-discount Price" value={formatCurrencyNullable(customDCFResult.entryLadder.accumulate)} glossarySlug="accumulate-threshold" />
                    </div>
                    {!customDCFResult.valid && <p className="buy-levels-note">{customDCFResult.note || "Custom scenario is invalid."}</p>}
                  </section>
                  <div className="dcf-lab-reverse-card">
					<BuyLevelsReverseDCFCard metrics={reverseMetricsFromAnalysis(report.analysisV2) || computeReverseDCFHeuristic(buyLevels.currentPrice ?? null, dcfHorizonData, pickScenario(dcfHorizonData, buyReverseScenario))} />
                  </div>
                  <section className="report-card dcf-lab-explainer-card">
                    <h3>What Each Input Means</h3>
                    <p className="buy-levels-note">These controls only change the local DCF what-if. They do not change the house recommendation or saved report.</p>
                    <div className="dcf-lab-explainer-list">
                      <article className="dcf-lab-explainer-item">
                        <div className="dcf-lab-explainer-label">
                          Growth Rate
                          <span>{customDCF.growthRate.toFixed(1)}%</span>
                        </div>
                        <p>Annual free-cash-flow growth during the explicit forecast years. Higher growth lifts projected cash flows and usually increases fair value.</p>
                      </article>
                      <article className="dcf-lab-explainer-item">
                        <div className="dcf-lab-explainer-label">
                          Discount Rate
                          <span>{customDCF.discountRate.toFixed(1)}%</span>
                        </div>
                        <p>Your required return. It discounts future cash flows back to today. Higher discount rates lower present value and make the scenario stricter.</p>
                      </article>
                      <article className="dcf-lab-explainer-item">
                        <div className="dcf-lab-explainer-label">
                          Terminal Growth
                          <span>{customDCF.terminalGrowth.toFixed(1)}%</span>
                        </div>
                        <p>Perpetual growth after the explicit forecast ends. Keep this conservative because it has a large effect on terminal value.</p>
                      </article>
                      <article className="dcf-lab-explainer-item">
                        <div className="dcf-lab-explainer-label">
                          Horizon
                          <span>{customDCF.horizon}</span>
                        </div>
                        <p>How long the higher-growth forecast runs before terminal value takes over. Longer horizons give compounding more time to matter.</p>
                      </article>
                    </div>
                    <div className="dcf-lab-explainer-footer">
                      Read-only anchors: FCF TTM {formatCurrency(data.summary.fcfTtm)}, net debt {formatCurrency(data.summary.netDebt)}, and weighted average shares {formatNumberNullable(buyLevels.sharesOutstanding ?? null)}.
                    </div>
                  </section>
                </div>
              </div>
            )}

            {activeTab === "valuation" && (!buyLevels || !buyModeView) && (
              <InvestmentsPageState message="Buy-level outputs are unavailable for this ticker." />
            )}
          </>
        )}

        {!loading && !error && data && !isETF && !report && (
          <InvestmentsPageState message="Equity detail report is unavailable for this ticker. Run a fresh sync/recompute and reload." />
        )}
      </div>
    </div>
  );
};

function activeTabToExportSection(activeTab: DetailTab): EquityDetailExportSection {
  if (activeTab === "summary") return "overview";
  if (activeTab === "valuation") return "entry";
  if (activeTab === "fundamentals" || activeTab === "risk") return activeTab;
  return "full-memo";
}

function useModalKeyboard(open: boolean, onClose: () => void): void {
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  React.useEffect(() => {
    if (!open) return undefined;
    const dialog = document.querySelector<HTMLElement>('[role="dialog"][aria-modal="true"]');
    const focusableSelector = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    window.requestAnimationFrame(() => dialog?.querySelector<HTMLElement>(focusableSelector)?.focus());
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);
}

const DETAIL_TABS: DetailTab[] = ["summary", "fundamentals", "valuation", "risk", "history"];

function normalizeDetailTab(value: string | null): DetailTab {
  return DETAIL_TABS.includes(value as DetailTab) ? value as DetailTab : "summary";
}

function normalizeValuationView(value: string | null): "entry" | "comparables" | "dcf" {
  return value === "dcf"||value === "comparables" ? value : "entry";
}

function normalizeComparableMetric(value:string|null):ComparableMetric|undefined{
  return value==="evFcf"||value==="evEbitda"||value==="pFcf"||value==="pe"||value==="evSales"?value:undefined;
}

function normalizeSeriesMode(value: string | null): SeriesMode {
  return value === "quarterly" ? "quarterly" : "annual";
}

function handleDetailTabKeyDown(
  event: React.KeyboardEvent<HTMLDivElement>,
  activeTab: DetailTab,
  onSelect: (tab: DetailTab) => void,
): void {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight" && event.key !== "Home" && event.key !== "End") return;
  event.preventDefault();
  const currentIndex = DETAIL_TABS.indexOf(activeTab);
  let nextIndex = currentIndex;
  if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + DETAIL_TABS.length) % DETAIL_TABS.length;
  if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % DETAIL_TABS.length;
  if (event.key === "Home") nextIndex = 0;
  if (event.key === "End") nextIndex = DETAIL_TABS.length - 1;
  const tablist = event.currentTarget;
  onSelect(DETAIL_TABS[nextIndex]);
  window.requestAnimationFrame(() => {
    const buttons = tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons[nextIndex]?.focus();
  });
}

function buildDetailDateLine(
  data: Pick<InvestmentDetailResponse, "companyName" | "refreshedAt" | "asOfDate">,
  markdown = false,
): string {
  const dateMeta = getDetailDateMeta(data.refreshedAt, data.asOfDate);
  if (markdown) {
    return `**${data.companyName}** | ${dateMeta.inlineLabel} **${dateMeta.value}**`;
  }
  return `${data.companyName} | ${dateMeta.inlineLabel} ${dateMeta.value}`;
}

function buildEquityOverviewExport(input: {
  data: InvestmentDetailResponse;
  report: NonNullable<InvestmentDetailResponse["report"]>;
  detailConfidence: DataConfidenceLabel;
  memoStrengths: string[];
  overviewBuyTarget: number | null;
  overviewWatchTarget: number | null;
  activePortfolioRow: ExportPortfolioRow;
}): string {
  const { data, report, detailConfidence, memoStrengths, overviewBuyTarget, overviewWatchTarget, activePortfolioRow } = input;
  const lines: string[] = [];

  lines.push(`${data.ticker} - Overview`);
  lines.push(buildDetailDateLine(data));
  lines.push("");
	lines.push(`- Screening Disposition: ${displayScreeningDisposition(report)}`);
  lines.push(`- Valuation Zone: ${data.summary.valuationZone}`);
  lines.push(`- Data Confidence: ${detailConfidence}`);
  if (activePortfolioRow) {
    lines.push(`- Portfolio Stage: ${activePortfolioRow.stage}`);
    lines.push(`- Portfolio Action: ${activePortfolioRow.recommendedAction.replace(/_/g, " ")}`);
    lines.push(`- Portfolio Rationale: ${activePortfolioRow.recommendedActionReason}`);
  }
	lines.push(`- House Multiple Threshold: ${formatCurrencyNullable(overviewBuyTarget)}`);
  lines.push(`- Watch Target: ${formatCurrencyNullable(overviewWatchTarget)}`);
  lines.push("");
  lines.push("Strengths");
  for (const line of memoStrengths) {
    lines.push(`- ${line}`);
  }
  lines.push("");
  lines.push("Decision Snapshot");
  lines.push(`- ${report.analysisV2?.screening.reasons[0] || report.finalActionMapping.reasons[0] || "Research disposition is available in the quantitative screen."}`);
  return lines.join("\n");
}

function buildEquityOverviewExportMarkdown(input: {
  data: InvestmentDetailResponse;
  report: NonNullable<InvestmentDetailResponse["report"]>;
  detailConfidence: DataConfidenceLabel;
  memoStrengths: string[];
  overviewBuyTarget: number | null;
  overviewWatchTarget: number | null;
  activePortfolioRow: ExportPortfolioRow;
}): string {
  const { data, report, detailConfidence, memoStrengths, overviewBuyTarget, overviewWatchTarget, activePortfolioRow } = input;
  const lines: string[] = [];

  lines.push(`# ${data.ticker} Overview`);
  lines.push(buildDetailDateLine(data, true));
  lines.push("");
  lines.push("## Decision");
	lines.push(`- Screening Disposition: ${displayScreeningDisposition(report)}`);
  lines.push(`- Valuation Zone: ${data.summary.valuationZone}`);
  lines.push(`- Data Confidence: ${detailConfidence}`);
  if (activePortfolioRow) {
    lines.push(`- Portfolio Stage: ${activePortfolioRow.stage}`);
    lines.push(`- Portfolio Action: ${activePortfolioRow.recommendedAction.replace(/_/g, " ")}`);
    lines.push(`- Portfolio Rationale: ${activePortfolioRow.recommendedActionReason}`);
  }
	lines.push(`- House Multiple Threshold: ${formatCurrencyNullable(overviewBuyTarget)}`);
  lines.push(`- Watch Target: ${formatCurrencyNullable(overviewWatchTarget)}`);
  lines.push("");
  lines.push("## Strengths");
  for (const line of memoStrengths) {
    lines.push(`- ${line}`);
  }
  lines.push("");
  lines.push("## Decision Snapshot");
  lines.push(`- ${report.analysisV2?.screening.reasons[0] || report.finalActionMapping.reasons[0] || "Research disposition is available in the quantitative screen."}`);
  return lines.join("\n");
}

function buildEquityEntryExport(input: {
  data: InvestmentDetailResponse;
  report: NonNullable<InvestmentDetailResponse["report"]>;
  buyLevels: BuyLevelsReportData | undefined;
  view: BuyLevelsModeViewData | null;
  mode: BuyLevelsMode;
  horizon: BuyLevelsHorizon;
  baseScenario: BuyLevelsScenarioData | null;
  gaugeMetrics: ReturnType<typeof computeBuyLevelsGauge>;
  overviewBuyTarget: number | null;
  overviewWatchTarget: number | null;
}): string {
  const { data, report, buyLevels, view, mode, horizon, baseScenario, gaugeMetrics, overviewBuyTarget, overviewWatchTarget } = input;
  if (!buyLevels || !view) {
    return `${data.ticker} - Entry\nEntry outputs unavailable.`;
  }
  const bandsPlacement = computeBuyLevelsBandsPlacement(view.multiples.currentMultiple ?? null, view.multiples.bands);
	const scenarioUnavailable = report.analysisV2?.model === "biopharma" && report.analysisV2.scenarioValuations.length === 0;
  const lines: string[] = [];

  lines.push(`${data.ticker} - Entry`);
  lines.push(buildDetailDateLine(data));
  lines.push("");
	lines.push(`- Screening Disposition: ${displayScreeningDisposition(report)}`);
  lines.push(`- Current Price: ${formatCurrencyNullable(buyLevels.currentPrice ?? null)}`);
  lines.push(`- Current Zone: ${report.buyPriceOverlay.currentZone}`);
	lines.push(`- House Multiple Threshold: ${formatCurrencyNullable(overviewBuyTarget)}`);
  lines.push(`- Watch Target: ${formatCurrencyNullable(overviewWatchTarget)}`);
  lines.push(`- House Mode: ${mode === "ADJUSTED" ? "Adjusted Overlay" : "Static Overlay"}`);
	if (!scenarioUnavailable) lines.push(`- DCF Sanity Horizon: ${horizon}`);
  lines.push(`- Current EV/FCF: ${formatRatioNullable(view.multiples.currentMultiple ?? null)}`);
	if (scenarioUnavailable) {
	  lines.push("- Scenario Valuation: Unavailable — explicit annual FCF assumptions are required.");
	} else {
	  lines.push(`- Fair Value (BASE): ${formatBuyLevelsCurrency(baseScenario?.fairValuePrice ?? null)}`);
	  lines.push(`- Margin of Safety: ${formatBuyLevelsSignedPercent(gaugeMetrics.marginOfSafetyPct)}`);
	  lines.push(`- Valuation State: ${gaugeMetrics.state}`);
	}
  lines.push(`- Current Band: ${bandsPlacement.activeBandLabel ? entryLevelLabel(bandsPlacement.activeBandLabel) : BUY_LEVELS_UNAVAILABLE}`);
  lines.push("");
  lines.push("Implied Entry Prices");
  for (const level of view.impliedPrices.levels) {
    lines.push(`- ${entryLevelLabel(level.label)}: ${formatBuyLevelsCurrency(level.targetPrice ?? null)}`);
  }
  return lines.join("\n");
}

function buildEquityEntryExportMarkdown(input: {
  data: InvestmentDetailResponse;
  report: NonNullable<InvestmentDetailResponse["report"]>;
  buyLevels: BuyLevelsReportData | undefined;
  view: BuyLevelsModeViewData | null;
  mode: BuyLevelsMode;
  horizon: BuyLevelsHorizon;
  baseScenario: BuyLevelsScenarioData | null;
  gaugeMetrics: ReturnType<typeof computeBuyLevelsGauge>;
  overviewBuyTarget: number | null;
  overviewWatchTarget: number | null;
}): string {
  const { data, report, buyLevels, view, mode, horizon, baseScenario, gaugeMetrics, overviewBuyTarget, overviewWatchTarget } = input;
  if (!buyLevels || !view) {
    return `# ${data.ticker} Entry\n\nEntry outputs unavailable.`;
  }
  const bandsPlacement = computeBuyLevelsBandsPlacement(view.multiples.currentMultiple ?? null, view.multiples.bands);
	const scenarioUnavailable = report.analysisV2?.model === "biopharma" && report.analysisV2.scenarioValuations.length === 0;
  const lines: string[] = [];

  lines.push(`# ${data.ticker} Entry`);
  lines.push(buildDetailDateLine(data, true));
  lines.push("");
  lines.push("## Decision Surface");
	lines.push(`- Screening Disposition: ${displayScreeningDisposition(report)}`);
  lines.push(`- Current Price: ${formatCurrencyNullable(buyLevels.currentPrice ?? null)}`);
  lines.push(`- Current Zone: ${report.buyPriceOverlay.currentZone}`);
	lines.push(`- House Multiple Threshold: ${formatCurrencyNullable(overviewBuyTarget)}`);
  lines.push(`- Watch Target: ${formatCurrencyNullable(overviewWatchTarget)}`);
  lines.push(`- House Mode: ${mode === "ADJUSTED" ? "Adjusted Overlay" : "Static Overlay"}`);
	if (!scenarioUnavailable) lines.push(`- DCF Sanity Horizon: ${horizon}`);
  lines.push(`- Current EV/FCF: ${formatRatioNullable(view.multiples.currentMultiple ?? null)}`);
	if (scenarioUnavailable) {
	  lines.push("- Scenario Valuation: Unavailable — explicit annual FCF assumptions are required.");
	} else {
	  lines.push(`- Fair Value (BASE): ${formatBuyLevelsCurrency(baseScenario?.fairValuePrice ?? null)}`);
	  lines.push(`- Margin of Safety: ${formatBuyLevelsSignedPercent(gaugeMetrics.marginOfSafetyPct)}`);
	  lines.push(`- Valuation State: ${gaugeMetrics.state}`);
	}
  lines.push(`- Current Band: ${bandsPlacement.activeBandLabel ? entryLevelLabel(bandsPlacement.activeBandLabel) : BUY_LEVELS_UNAVAILABLE}`);
  lines.push("");
  lines.push("## Implied Entry Prices");
  for (const level of view.impliedPrices.levels) {
    lines.push(`- ${entryLevelLabel(level.label)}: ${formatBuyLevelsCurrency(level.targetPrice ?? null)}`);
  }
  return lines.join("\n");
}

function buildEquityRiskExport(input: {
  data: InvestmentDetailResponse;
  report: NonNullable<InvestmentDetailResponse["report"]>;
  detailConfidence: DataConfidenceLabel;
  memoWatchouts: string[];
  riskNarrative: string[];
  groupedWarnings: Array<[string, string[]]>;
  buyLevels: BuyLevelsReportData | undefined;
  rateBaseline: RateBaselineMetrics | null;
  reverseMetrics: ReturnType<typeof computeReverseDCFHeuristic> | null;
}): string {
  const { data, report, detailConfidence, memoWatchouts, riskNarrative, groupedWarnings, buyLevels, rateBaseline, reverseMetrics } = input;
  const lines: string[] = [];

  lines.push(`${data.ticker} - Risk`);
  lines.push(buildDetailDateLine(data));
  lines.push("");
  lines.push(`- Data Confidence: ${detailConfidence}`);
	lines.push(report.analysisV2 ? `- Evidence Confidence: ${report.analysisV2.dataConfidence}` : `- Moat Band: ${report.moatConfidence.band}`);
  lines.push(`- Current Zone: ${data.summary.valuationZone}`);
  lines.push(`- Debt / FCF: ${formatRatio(data.summary.debtToFcfTtm)}`);
  lines.push(`- Current EV/FCF: ${formatRatioNullable(report.valuationAndDiscipline.valuation.evFcfTtm)}`);
  lines.push("");
  lines.push("Primary Risk Framing");
  for (const line of riskNarrative) {
    lines.push(`- ${line}`);
  }
  lines.push("");
  lines.push("Top Risks");
  for (const line of memoWatchouts) {
    lines.push(`- ${line}`);
  }
  if (groupedWarnings.length > 0) {
    lines.push("");
    lines.push("Grouped Warnings");
    for (const [group, entries] of groupedWarnings) {
      lines.push(`- ${group}: ${entries.join(" | ")}`);
    }
  }
  if (buyLevels?.warnings && buyLevels.warnings.length > 0) {
    lines.push("");
    lines.push("Model Warnings");
    for (const warning of buyLevels.warnings) {
      lines.push(`- [${warning.severity}] ${warning.message}`);
    }
  }
  if (rateBaseline) {
    lines.push("");
    lines.push("Rates Baseline");
    lines.push(`- Current FCF Yield - 10Y: ${formatBasisPointsNullable(rateBaseline.currentSpreadToTenYear ?? null)}`);
    lines.push(`- Buy Threshold - 10Y: ${formatBasisPointsNullable(rateBaseline.buyThresholdSpreadToTenYear ?? null)}`);
    lines.push(`- Curve Regime: ${describeCurveRegime(rateBaseline.curveRegime)}`);
  }
  if (reverseMetrics) {
    lines.push("");
	lines.push(report.analysisV2 ? "Constant-growth Diagnostic" : "Reverse DCF");
    lines.push(`- Status: ${reverseMetrics.status}`);
    lines.push(`- Implied Growth Approx: ${formatBuyLevelsPercentDecimal(reverseMetrics.impliedGrowthApprox)}`);
    lines.push(`- Growth Gap vs Anchor: ${formatBuyLevelsSignedPP(reverseMetrics.growthGapPp)}`);
    lines.push(`- Note: ${reverseMetrics.note}`);
  }
  return lines.join("\n");
}

function buildEquityRiskExportMarkdown(input: {
  data: InvestmentDetailResponse;
  report: NonNullable<InvestmentDetailResponse["report"]>;
  detailConfidence: DataConfidenceLabel;
  memoWatchouts: string[];
  riskNarrative: string[];
  groupedWarnings: Array<[string, string[]]>;
  buyLevels: BuyLevelsReportData | undefined;
  rateBaseline: RateBaselineMetrics | null;
  reverseMetrics: ReturnType<typeof computeReverseDCFHeuristic> | null;
}): string {
  const { data, report, detailConfidence, memoWatchouts, riskNarrative, groupedWarnings, buyLevels, rateBaseline, reverseMetrics } = input;
  const lines: string[] = [];

  lines.push(`# ${data.ticker} Risk`);
  lines.push(buildDetailDateLine(data, true));
  lines.push("");
  lines.push("## Risk Snapshot");
  lines.push(`- Data Confidence: ${detailConfidence}`);
	lines.push(report.analysisV2 ? `- Evidence Confidence: ${report.analysisV2.dataConfidence}` : `- Moat Band: ${report.moatConfidence.band}`);
  lines.push(`- Current Zone: ${data.summary.valuationZone}`);
  lines.push(`- Debt / FCF: ${formatRatio(data.summary.debtToFcfTtm)}`);
  lines.push(`- Current EV/FCF: ${formatRatioNullable(report.valuationAndDiscipline.valuation.evFcfTtm)}`);
  lines.push("");
  lines.push("## Primary Risk Framing");
  for (const line of riskNarrative) {
    lines.push(`- ${line}`);
  }
  lines.push("");
  lines.push("## Top Risks");
  for (const line of memoWatchouts) {
    lines.push(`- ${line}`);
  }
  if (groupedWarnings.length > 0) {
    lines.push("");
    lines.push("## Grouped Warnings");
    for (const [group, entries] of groupedWarnings) {
      lines.push(`- ${group}: ${entries.join(" | ")}`);
    }
  }
  if (buyLevels?.warnings && buyLevels.warnings.length > 0) {
    lines.push("");
    lines.push("## Model Warnings");
    for (const warning of buyLevels.warnings) {
      lines.push(`- [${warning.severity}] ${warning.message}`);
    }
  }
  if (rateBaseline) {
    lines.push("");
    lines.push("## Rates Baseline");
    lines.push(`- Current FCF Yield - 10Y: ${formatBasisPointsNullable(rateBaseline.currentSpreadToTenYear ?? null)}`);
    lines.push(`- Buy Threshold - 10Y: ${formatBasisPointsNullable(rateBaseline.buyThresholdSpreadToTenYear ?? null)}`);
    lines.push(`- Curve Regime: ${describeCurveRegime(rateBaseline.curveRegime)}`);
  }
  if (reverseMetrics) {
    lines.push("");
	lines.push(report.analysisV2 ? "## Constant-growth Diagnostic" : "## Reverse DCF");
    lines.push(`- Status: ${reverseMetrics.status}`);
    lines.push(`- Implied Growth Approx: ${formatBuyLevelsPercentDecimal(reverseMetrics.impliedGrowthApprox)}`);
    lines.push(`- Growth Gap vs Anchor: ${formatBuyLevelsSignedPP(reverseMetrics.growthGapPp)}`);
    lines.push(`- Note: ${reverseMetrics.note}`);
  }
  return lines.join("\n");
}

function HistoryDashboard({
  mode,
  series,
  chartSeries,
  onPeriodChange,
}: {
  mode: SeriesMode;
  series: InvestmentSeriesPoint[];
  chartSeries: ChartSeriesPoint[];
  onPeriodChange: (mode: SeriesMode) => void;
}): JSX.Element {
  const [category, setCategory] = React.useState<HistoryCategory>("cash-flow");
  const metrics = HISTORY_METRICS[category];
  const latest = chartSeries[chartSeries.length - 1];
  const prior = chartSeries[chartSeries.length - 2];
  const categoryLabel = category.split("-").map((part) => part[0].toUpperCase() + part.slice(1)).join(" ");

  return (
    <div className="history-dashboard">
      <section className="report-card history-controls-card">
        <div>
          <h2>Historical evidence</h2>
          <p className="buy-levels-note">Select one evidence family at a time. No more than four charts are shown, and the same values are available in the table below.</p>
        </div>
        <div className="graphs-toolbar">
          <div className="investments-toggle" aria-label="History period">
            <button type="button" className={mode === "annual" ? "active" : ""} aria-pressed={mode === "annual"} onClick={() => onPeriodChange("annual")}>Annual</button>
            <button type="button" className={mode === "quarterly" ? "active" : ""} aria-pressed={mode === "quarterly"} onClick={() => onPeriodChange("quarterly")}>Quarterly</button>
          </div>
        </div>
        <div className="history-category-controls" aria-label="History metric category">
          {(Object.keys(HISTORY_METRICS) as HistoryCategory[]).map((item) => (
            <button type="button" key={item} className={item === category ? "active" : ""} aria-pressed={item === category} onClick={() => setCategory(item)}>
              {item.split("-").map((part) => part[0].toUpperCase() + part.slice(1)).join(" ")}
            </button>
          ))}
        </div>
      </section>

      {series.length === 0 ? (
        <InvestmentsPageState message={`No ${mode} series data available for this ticker.`} />
      ) : (
        <>
          <section className="kpi-rail-grid history-highlight-grid" aria-label="Latest historical highlights">
            {metrics.map((metric) => {
              const current = numericSeriesValue(latest?.[metric.key]);
              const previous = numericSeriesValue(prior?.[metric.key]);
              const change = current != null && previous != null ? current - previous : null;
              return <KPI key={String(metric.key)} label={`${metric.label} · latest`} value={`${formatHistoryMetric(current, metric.kind)}${change == null ? "" : ` (${formatHistoryChange(change, metric.kind)})`}`} />;
            })}
          </section>

          <section className="chart-section">
            <h2>{categoryLabel} ({mode}{mode === "quarterly" && category === "valuation" ? ", rolling TTM" : ""})</h2>
            <div className="investments-two-grid history-chart-grid">
              {metrics.map((metric) => (
                <MetricLineChartCard
                  key={String(metric.key)}
                  data={chartSeries}
                  dataKey={metric.key}
                  label={metric.label}
                  color={metric.color}
                  kind={metric.kind}
                  seriesMode={mode}
                  renderMode={metric.autoMultiples ? "auto-multiples" : "line"}
                />
              ))}
            </div>
          </section>

          <details className="report-card history-table-card">
            <summary>View {categoryLabel.toLowerCase()} data table</summary>
            <div className="buy-levels-table-wrap">
              <table className="buy-levels-table">
                <thead><tr><th>Period end</th>{metrics.map((metric) => <th className="numeric-col" key={String(metric.key)}>{metric.label}</th>)}</tr></thead>
                <tbody>{[...chartSeries].reverse().map((row) => <tr key={row.periodEnd}><td>{row.periodEnd}</td>{metrics.map((metric) => <td className="numeric-col" key={String(metric.key)}>{formatHistoryMetric(numericSeriesValue(row[metric.key]), metric.kind)}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </div>
  );
}

function numericSeriesValue(value: ChartSeriesPoint[keyof ChartSeriesPoint] | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatHistoryMetric(value: number | null, kind: MetricKind): string {
  return value == null ? "N/A" : formatMetricValue(value, kind);
}

function formatHistoryChange(value: number, kind: MetricKind): string {
  const formatted = formatMetricValue(Math.abs(value), kind);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}

function SingleBarChartCard({
  data,
  dataKey,
  label,
  color,
}: {
  data: InvestmentSeriesPoint[];
  dataKey: "cfo" | "capex" | "fcf";
  label: string;
  color: string;
}): JSX.Element {
  const hasCoverage = React.useMemo(() => {
    if (data.length === 0) {
      return false;
    }
    const valid = data.reduce((count, row) => {
      const raw = row[dataKey];
      if (typeof raw !== "number") {
        return count;
      }
      return Number.isFinite(raw) && raw !== 0 ? count + 1 : count;
    }, 0);
    return valid / data.length >= 0.5;
  }, [data, dataKey]);

  return (
    <article className="investments-chart-card">
      <h3><GlossaryTerm label={label} variant="metric" /></h3>
      <p className="chart-metadata">Value scale: USD</p>
      {hasCoverage ? (
        <div className="investments-chart-scroll">
          <div className="investments-chart-min">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a3552" />
                <XAxis dataKey="periodEnd" tick={{ fill: "#c4d3f2", fontSize: 11 }} />
                <YAxis tick={{ fill: "#c4d3f2", fontSize: 11 }} tickFormatter={(value) => shortCurrency(value)} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Bar dataKey={dataKey} fill={color} name={label} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="chart-empty">Insufficient data coverage for selected series.</div>
      )}
    </article>
  );
}

function MetricLineChartCard({
  data,
  dataKey,
  label,
  color,
  kind,
  seriesMode,
  renderMode = "line",
}: {
  data: ChartSeriesPoint[];
  dataKey: keyof ChartSeriesPoint;
  label: string;
  color: string;
  kind: MetricKind;
  seriesMode?: SeriesMode;
  renderMode?: ChartRenderMode;
}): JSX.Element {
  const showSparseDots = renderMode === "auto-multiples" && seriesMode === "quarterly";
  const coverage = React.useMemo(() => {
    if (data.length === 0) {
      return 0;
    }
    const valid = data.reduce((count, row) => {
      const raw = row[dataKey];
      if (typeof raw !== "number") {
        return count;
      }
      return Number.isFinite(raw) && raw !== 0 ? count + 1 : count;
    }, 0);
    return valid / data.length;
  }, [data, dataKey]);
  const hasCoverage = coverage >= 0.5;
  const allowSparseQuarterlyMultiples = showSparseDots && coverage >= 0.2;
  const sparseDots = React.useMemo<Array<{ x: string; y: number }>>(() => {
    if (!showSparseDots) {
      return [];
    }
    return data.reduce<Array<{ x: string; y: number }>>((acc, row) => {
      const raw = row[dataKey];
      if (typeof raw !== "number" || !Number.isFinite(raw) || raw === 0) {
        return acc;
      }
      acc.push({ x: row.periodEnd, y: raw });
      return acc;
    }, []);
  }, [data, dataKey, showSparseDots]);

  return (
    <article className="investments-chart-card">
      <h3><GlossaryTerm label={label} variant="metric" /></h3>
      <p className="chart-metadata">Value scale: {metricLegend(kind)}</p>
      {hasCoverage || allowSparseQuarterlyMultiples ? (
        <div className="investments-chart-scroll">
          <div className="investments-chart-min">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a3552" />
                <XAxis dataKey="periodEnd" tick={{ fill: "#c4d3f2", fontSize: 11 }} />
                <YAxis tick={{ fill: "#c4d3f2", fontSize: 11 }} tickFormatter={(value) => formatMetricAxisTick(Number(value), kind)} />
                <Tooltip
                  formatter={(value: unknown) => {
                    if (typeof value !== "number" || !Number.isFinite(value)) {
                      return "N/A";
                    }
                    return formatMetricValue(value, kind);
                  }}
                />
                <Line
                  type={showSparseDots ? "linear" : "monotone"}
                  dataKey={dataKey}
                  stroke={color}
                  strokeWidth={2}
                  connectNulls={false}
                  strokeOpacity={showSparseDots ? 0.72 : 1}
                  dot={showSparseDots ? { r: 3, stroke: color, strokeWidth: 1, fill: color } : false}
                  activeDot={{ r: 4 }}
                  name={label}
                />
                {showSparseDots && sparseDots.map((dot, index) => (
                  <ReferenceDot
                    key={`${dot.x}-${index}`}
                    x={dot.x}
                    y={dot.y}
                    r={3}
                    fill={color}
                    stroke={color}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="chart-empty">Insufficient data coverage for selected series.</div>
      )}
    </article>
  );
}

function KPI({
  label,
  value,
  warning,
  glossarySlug,
  glossaryTerm,
}: {
  label: string;
  value: string;
  warning?: string;
  glossarySlug?: string;
  glossaryTerm?: string;
}): JSX.Element {
  return (
    <div className="investments-kpi">
      <div className="label">
        <MetricLabel label={label} variant="kpi" glossarySlug={glossarySlug} glossaryTerm={glossaryTerm} />
        {warning && <MetricWarningTooltip message={warning} />}
      </div>
      <div className="value">{value}</div>
    </div>
  );
}

function KeyValue({
  label,
  value,
  glossarySlug,
  glossaryTerm,
}: {
  label: string;
  value: string;
  glossarySlug?: string;
  glossaryTerm?: string;
}): JSX.Element {
  return (
    <div className="kv-item">
      <span className="k"><MetricLabel label={label} variant="kv" glossarySlug={glossarySlug} glossaryTerm={glossaryTerm} /></span>
      <span className="v">{value}</span>
    </div>
  );
}

function SignalValue({ label, signal }: { label: string; signal: MoatSignal; }): JSX.Element {
  const className = signal === "PASS"
    ? "signal-pass"
    : signal === "WEAK PASS"
      ? "signal-weak"
      : signal === "UNKNOWN"
        ? "signal-unknown"
        : "signal-fail";
  return (
    <div className="kv-item">
      <span className="k"><MetricLabel label={label} variant="kv" /></span>
      <span className={`v signal-pill ${className}`}>{signal}</span>
    </div>
  );
}

function MetricLabel({
  label,
  variant,
  glossarySlug,
  glossaryTerm,
}: {
  label: string;
  variant: "kpi" | "kv";
  glossarySlug?: string;
  glossaryTerm?: string;
}): JSX.Element {
  return <GlossaryTerm label={label} term={glossaryTerm} slug={glossarySlug} variant={variant} className="metric-label-text" />;
}

function SectionHeading({
  label,
  slug,
  term,
}: {
  label: string;
  slug?: string;
  term?: string;
}): JSX.Element {
  return <GlossaryTerm label={label} slug={slug} term={term} variant="metric" className="detail-section-heading" />;
}

function InlineGlossaryLabel({
  label,
  slug,
  term,
}: {
  label: string;
  slug?: string;
  term?: string;
}): JSX.Element {
  return <GlossaryTerm label={label} slug={slug} term={term} variant="inline" className="buy-levels-inline-label" />;
}

function TableHeaderLabel({
  label,
  slug,
  term,
}: {
  label: string;
  slug?: string;
  term?: string;
}): JSX.Element {
  return <GlossaryTerm label={label} slug={slug} term={term} variant="table-header" className="detail-table-heading" />;
}

function MetricWarningTooltip({ message }: { message: string }): JSX.Element {
  return (
    <span className="kpi-warning">
      <button type="button" className="kpi-warning-icon" aria-label={`Metric warning: ${message}`}>
        !
      </button>
      <span className="kpi-warning-tooltip" role="tooltip">
        {message}
      </span>
    </span>
  );
}

function BuyLevelsHeader({ buyLevels }: { buyLevels: BuyLevelsReportData }): JSX.Element {
  return (
    <section className="report-card buy-levels-header">
      <div className="buy-levels-header-top">
        <div>
          <h3><SectionHeading label="Buy Levels" slug="buy-levels" /></h3>
          <p className="buy-levels-header-subtitle">Structured entry levels from valuation bands, implied prices, and DCF scenarios.</p>
        </div>
      </div>
      <div className="buy-levels-meta">
        <div className="buy-levels-meta-chip">
          <span className="label"><InlineGlossaryLabel label="Quality Tier" term="Buy Tier" /></span>
          <span className="value">{buyLevels.qualityTier}</span>
        </div>
        <div className="buy-levels-meta-chip">
          <span className="label"><InlineGlossaryLabel label="Current Price (Derived)" slug="current-price" /></span>
          <span className="value">{formatBuyLevelsCurrency(buyLevels.currentPrice ?? null)}</span>
        </div>
        <div className="buy-levels-meta-chip">
          <span className="label"><InlineGlossaryLabel label="Weighted Avg Shares" slug="weighted-avg-shares" /></span>
          <span className="value">{formatBuyLevelsNumber(buyLevels.sharesOutstanding ?? null)}</span>
        </div>
      </div>
    </section>
  );
}

function BuyLevelsControlRail({
  mode,
  horizon,
  reverseScenario,
  onModeChange,
  onHorizonChange,
  onReverseScenarioChange,
}: {
  mode: BuyLevelsMode;
  horizon: BuyLevelsHorizon;
  reverseScenario: BuyLevelsScenario;
  onModeChange: (value: BuyLevelsMode) => void;
  onHorizonChange: (value: BuyLevelsHorizon) => void;
  onReverseScenarioChange: (value: BuyLevelsScenario) => void;
}): JSX.Element {
  return (
    <div className="buy-levels-context-rail">
      <div className="buy-levels-context-message">
        <strong>Entry &amp; Risk Controls</strong>
        <span>Use EV/FCF overlay as the primary entry framework and DCF as a heuristic sanity check.</span>
      </div>

      <div className="buy-levels-context-controls">
        <div className="buy-levels-control-group">
          <span className="buy-levels-control-label">Mode</span>
          <div className="buy-levels-mode-toggle" aria-label="Buy level mode">
            <button type="button" className={mode === "STATIC" ? "active" : ""} onClick={() => onModeChange("STATIC")}>
              Static
            </button>
            <button type="button" className={mode === "ADJUSTED" ? "active" : ""} onClick={() => onModeChange("ADJUSTED")}>
              Adjusted
            </button>
          </div>
        </div>

        <div className="buy-levels-control-group">
          <span className="buy-levels-control-label">DCF Horizon</span>
          <div className="buy-levels-horizon-toggle" aria-label="DCF horizon">
            <button type="button" className={horizon === "3Y" ? "active" : ""} onClick={() => onHorizonChange("3Y")}>
              3Y
            </button>
            <button type="button" className={horizon === "5Y" ? "active" : ""} onClick={() => onHorizonChange("5Y")}>
              5Y
            </button>
          </div>
        </div>

        <div className="buy-levels-control-group">
          <span className="buy-levels-control-label">Reverse Scenario</span>
          <div className="buy-levels-scenario-toggle" aria-label="Reverse DCF scenario">
            <button
              type="button"
              className={reverseScenario === "BEAR" ? "active" : ""}
              onClick={() => onReverseScenarioChange("BEAR")}
            >
              Bear
            </button>
            <button
              type="button"
              className={reverseScenario === "BASE" ? "active" : ""}
              onClick={() => onReverseScenarioChange("BASE")}
            >
              Base
            </button>
            <button
              type="button"
              className={reverseScenario === "BULL" ? "active" : ""}
              onClick={() => onReverseScenarioChange("BULL")}
            >
              Bull
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BuyLevelsValuationTopRow({
  summary,
  buyLevels,
  view,
  horizon,
  reverseScenario,
}: {
  summary: InvestmentDetailResponse["summary"];
  buyLevels: BuyLevelsReportData;
  view: BuyLevelsModeViewData;
  horizon: BuyLevelsHorizon;
  reverseScenario: BuyLevelsScenario;
}): JSX.Element {
  const selectedHorizon = horizon === "3Y" ? view.dcf.horizon3y : view.dcf.horizon5y;
  const baseScenario = pickScenario(selectedHorizon, "BASE");
  const selectedReverseScenario = pickScenario(selectedHorizon, reverseScenario);

  const gaugeMetrics = computeBuyLevelsGauge(
    buyLevels.currentPrice ?? null,
    baseScenario?.fairValuePrice ?? null,
  );
  const reverseMetrics = computeReverseDCFHeuristic(
    buyLevels.currentPrice ?? null,
    selectedHorizon,
    selectedReverseScenario,
  );

  return (
    <section className="buy-levels-top-row">
      <BuyLevelsAssumptionsCard summary={summary} selectedHorizon={selectedHorizon} baseScenario={baseScenario ?? null} />
      <BuyLevelsGaugeCard metrics={gaugeMetrics} />
      <BuyLevelsReverseDCFCard metrics={reverseMetrics} />
    </section>
  );
}

function BuyLevelsAssumptionsCard({
  summary,
  selectedHorizon,
  baseScenario,
}: {
  summary: InvestmentDetailResponse["summary"];
  selectedHorizon: BuyLevelsHorizonData;
  baseScenario: BuyLevelsScenarioData | null;
}): JSX.Element {
  return (
    <article className="buy-levels-top-card">
      <h3><SectionHeading label="Assumptions" slug="assumptions" /></h3>
      <div className="buy-levels-kv-list">
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="FCF (TTM)" slug="fcf-ttm" /></span>
          <span className="numeric">{formatBuyLevelsCurrency(summary.fcfTtm)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Net Debt" slug="net-debt" /></span>
          <span className="numeric">{formatBuyLevelsCurrency(summary.netDebt)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Growth Anchor" slug="growth-anchor" /></span>
          <span className="numeric">{formatBuyLevelsPercentDecimal(selectedHorizon.growthAnchor ?? null)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Discount Rate (BASE)" slug="discount-rate" /></span>
          <span className="numeric">{formatBuyLevelsPercentDecimal(baseScenario?.discountRate ?? null)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Terminal Growth (BASE)" slug="terminal-growth" /></span>
          <span className="numeric">{formatBuyLevelsPercentDecimal(baseScenario?.terminalGrowth ?? null)}</span>
        </div>
      </div>
    </article>
  );
}

function BuyLevelsGaugeCard({
  metrics,
}: {
  metrics: {
    currentPrice: number | null;
    fairValuePrice: number | null;
    marginOfSafetyPct: number | null;
    markerPct: number;
    state: string;
    tone: BuyLevelsGaugeTone;
  };
}): JSX.Element {
  const gaugeReading = describeBuyLevelsGauge(metrics.marginOfSafetyPct);

  return (
    <article className="buy-levels-top-card">
      <h3><SectionHeading label="Fair Value Gauge" slug="fair-value-gauge" /></h3>
      <div className="buy-levels-gauge-summary">
        <span className={`signal-pill buy-levels-gauge-state ${`state-${metrics.tone}`}`}>{metrics.state}</span>
        <p className="buy-levels-note">
          {gaugeReading} The marker compares current price with <GlossaryTerm label="Fair Value (BASE)" slug="fair-value-per-share" />. Left is cheaper; right is richer.
        </p>
      </div>
      <div className="buy-levels-gauge-legend" aria-hidden="true">
        <span>Undervalued</span>
        <span>Fair</span>
        <span>Overpriced</span>
      </div>
      <div className="buy-levels-gauge-shell">
        <div className="buy-levels-gauge-track">
          <span className="buy-levels-gauge-marker" style={{ left: `${metrics.markerPct}%` }} />
        </div>
        <div className="buy-levels-gauge-scale">
          <span>+60% MOS</span>
          <span>0%</span>
          <span>-60% MOS</span>
        </div>
      </div>
      <div className="buy-levels-kv-list">
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Current Price" slug="current-price" /></span>
          <span className="numeric">{formatBuyLevelsCurrency(metrics.currentPrice)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Fair Value (BASE)" slug="fair-value-per-share" /></span>
          <span className="numeric">{formatBuyLevelsCurrency(metrics.fairValuePrice)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Margin of Safety" slug="margin-of-safety" /></span>
          <span className="numeric">{formatBuyLevelsSignedPercent(metrics.marginOfSafetyPct)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Valuation State" slug="valuation-state" /></span>
          <span className={`signal-pill buy-levels-gauge-state ${`state-${metrics.tone}`}`}>{metrics.state}</span>
        </div>
      </div>
    </article>
  );
}

function BuyLevelsReverseDCFCard({
  metrics,
}: {
  metrics: {
    scenario: BuyLevelsScenario;
    scenarioGrowth: number | null;
    impliedGrowthApprox: number | null;
    growthGapPp: number | null;
    status: BuyLevelsReverseStatus;
    note: string;
  };
}): JSX.Element {
  return (
    <article className="buy-levels-top-card">
	  <h3><SectionHeading label="Constant-growth diagnostic" slug="reverse-dcf-diagnostics" /></h3>
      <div className="buy-levels-kv-list">
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Scenario" slug="reverse-dcf-diagnostics" /></span>
          <span className="numeric">{metrics.scenario}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Scenario Growth" slug="growth-rate" /></span>
          <span className="numeric">{formatBuyLevelsPercentDecimal(metrics.scenarioGrowth)}</span>
        </div>
        <div className="buy-levels-kv-row">
		  <span><InlineGlossaryLabel label="Solved Implied Growth" slug="reverse-dcf-diagnostics" /></span>
          <span className="numeric">{formatBuyLevelsPercentDecimal(metrics.impliedGrowthApprox)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Growth Gap vs Anchor" slug="growth-anchor" /></span>
          <span className="numeric">{formatBuyLevelsSignedPP(metrics.growthGapPp)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Status" term="Valuation State" /></span>
          <span className={`signal-pill buy-levels-reverse-status status-${metrics.status.toLowerCase()}`}>{metrics.status}</span>
        </div>
      </div>
      <p className="buy-levels-note">{metrics.note}</p>
    </article>
  );
}

function BuyLevelsWarnings({
  warnings,
	v2 = false,
}: {
  warnings: Array<{ key: string; message: string; severity: "INFO" | "WARN" | "ERROR" }>;
	v2?: boolean;
}): JSX.Element | null {
  if (!warnings.length) {
    return null;
  }
  return (
    <details className="warning-banner buy-levels-warnings">
      <summary>
		<span><GlossaryTerm label={v2 ? "House Multiple Threshold Warnings" : "Buy Levels Warnings"} slug="buy-levels-warnings" /></span>
        <span className="warning-count">{warnings.length}</span>
      </summary>
      <div className="warning-groups">
        <div className="warning-group">
          <h4>Model Inputs</h4>
          <ul>
            {warnings.map((warning: BuyLevelsWarningData) => (
              <li key={`${warning.key}-${warning.message}`}>{warning.message}</li>
            ))}
          </ul>
        </div>
      </div>
    </details>
  );
}

function BuyLevelsTables({
  mode,
  horizon,
  view,
  currentPrice,
  includeDCF = true,
}: {
  mode: BuyLevelsMode;
  horizon: BuyLevelsHorizon;
  view: BuyLevelsModeViewData;
  currentPrice: number | null;
  includeDCF?: boolean;
}): JSX.Element {
  const bandsPlacement = computeBuyLevelsBandsPlacement(view.multiples.currentMultiple ?? null, view.multiples.bands);

  return (
    <section className="report-card buy-levels-panel">
      <div className="buy-levels-panel-section">
        <h3><SectionHeading label="EV/FCF Bands" slug="ev-fcf-overlay" /> ({mode === "STATIC" ? "Static" : "Adjusted"})</h3>
        <div className="buy-levels-bands-summary" role="status" aria-live="polite">
          <div className="buy-levels-meta-chip">
            <span className="label"><InlineGlossaryLabel label="Current EV/FCF" slug="ev-fcf-ttm" /></span>
            <span className="value">{formatBuyLevelsRatio(bandsPlacement.currentMultiple)}</span>
          </div>
          <div className="buy-levels-meta-chip">
            <span className="label"><InlineGlossaryLabel label="Current Band" slug="current-zone" /></span>
            <span className="value">{bandsPlacement.activeBandLabel ? entryLevelLabel(bandsPlacement.activeBandLabel) : BUY_LEVELS_UNAVAILABLE}</span>
          </div>
          <div className="buy-levels-meta-chip">
            <span className="label"><InlineGlossaryLabel label="Band Range" slug="entry-framework" /></span>
            <span className="value">{bandsPlacement.activeBandRange || BUY_LEVELS_UNAVAILABLE}</span>
          </div>
          <div className="buy-levels-meta-chip buy-levels-bands-summary-note">
            <span className="label"><InlineGlossaryLabel label="Context" slug="entry-framework" /></span>
            <span className="value">{bandsPlacement.summaryNote}</span>
          </div>
        </div>
        <div className="buy-levels-table-wrap">
          <table className="buy-levels-table">
            <thead>
              <tr>
                <th><TableHeaderLabel label="Level" slug="entry-framework" /></th>
                <th><TableHeaderLabel label="Range" slug="entry-framework" /></th>
                <th className="numeric-col"><TableHeaderLabel label="Representative Multiple" slug="ev-fcf-ttm" /></th>
                <th className="numeric-col"><TableHeaderLabel label="Delta vs Current" slug="margin-of-safety" /></th>
              </tr>
            </thead>
            <tbody>
              {view.multiples.bands.map((band: BuyLevelsBandData) => {
                const isActiveBand = bandsPlacement.activeBandLabel === band.label;
                return (
                  <tr key={band.label} className={isActiveBand ? "is-active-band" : ""}>
                    <td className="buy-levels-band-level-cell">
                      <span>{entryLevelLabel(band.label)}</span>
                      {isActiveBand && <span className="signal-pill buy-levels-band-current-pill">Current</span>}
                    </td>
                    <td>{formatBandRange(band.min, band.max)}</td>
                    <td className="numeric-col">{formatBuyLevelsRatio(band.multiple ?? null)}</td>
                    <td className="numeric-col">{formatBuyLevelsSignedRatio(computeBuyLevelsMultipleDelta(band.multiple ?? null, bandsPlacement.currentMultiple))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="buy-levels-panel-section">
        <h3><SectionHeading label="Implied Entry Prices" slug="entry-framework" /> ({mode === "STATIC" ? "Static" : "Adjusted"})</h3>
        <div className="buy-levels-table-wrap">
          <table className="buy-levels-table">
            <thead>
              <tr>
                <th><TableHeaderLabel label="Level" slug="entry-framework" /></th>
                <th className="numeric-col"><TableHeaderLabel label="Multiple" slug="ev-fcf-ttm" /></th>
                <th className="numeric-col"><TableHeaderLabel label="Target EV" slug="enterprise-value" /></th>
                <th className="numeric-col"><TableHeaderLabel label="Target Equity" slug="market-cap" /></th>
                <th className="numeric-col"><TableHeaderLabel label="Target Price" slug="target-buy" /></th>
                <th className="numeric-col"><TableHeaderLabel label="Delta vs Current" slug="margin-of-safety" /></th>
              </tr>
            </thead>
            <tbody>
              {view.impliedPrices.levels.map((level) => (
                <tr key={level.label}>
                  <td>{entryLevelLabel(level.label)}</td>
                  <td className="numeric-col">{formatBuyLevelsRatio(level.multiple ?? null)}</td>
                  <td className="numeric-col">{formatBuyLevelsCurrency(level.targetEv ?? null)}</td>
                  <td className="numeric-col">{formatBuyLevelsCurrency(level.targetEquity ?? null)}</td>
                  <td className="numeric-col">{formatBuyLevelsCurrency(level.targetPrice ?? null)}</td>
                  <td className="numeric-col">{formatBuyLevelsSignedPercent(computeBuyLevelsDeltaPct(level.targetPrice ?? null, currentPrice))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {includeDCF && (
        <div className="buy-levels-panel-section">
          <BuyLevelsDCFTable view={view} horizon={horizon} />
        </div>
      )}
    </section>
  );
}

function BuyLevelsDCFTable({
  view,
  horizon,
}: {
  view: BuyLevelsModeViewData;
  horizon: BuyLevelsHorizon;
}): JSX.Element {
  const selected = horizon === "3Y" ? view.dcf.horizon3y : view.dcf.horizon5y;

  return (
    <>
      <h3><SectionHeading label="Discounted Cash Flow" slug="dcf-sanity-check" /> ({horizon})</h3>
      <div className="buy-levels-dcf-meta">
        <div className="buy-levels-meta-chip">
          <span className="label"><InlineGlossaryLabel label="Growth Anchor" slug="growth-anchor" /></span>
          <span className="value">{formatPercentNullable(selected.growthAnchor ?? null)}</span>
        </div>
        <div className="buy-levels-meta-chip">
          <span className="label"><InlineGlossaryLabel label="Growth Source" slug="growth-source" /></span>
          <span className="value">{selected.growthSource || "N/A"}</span>
        </div>
      </div>
      <div className="buy-levels-table-wrap">
        <table className="buy-levels-table">
          <thead>
            <tr>
              <th><TableHeaderLabel label="Scenario" slug="reverse-dcf-diagnostics" /></th>
              <th className="numeric-col"><TableHeaderLabel label="Growth Rate" slug="growth-rate" /></th>
              <th className="numeric-col"><TableHeaderLabel label="Discount Rate" slug="discount-rate" /></th>
              <th className="numeric-col"><TableHeaderLabel label="Terminal Growth" slug="terminal-growth" /></th>
              <th className="numeric-col"><TableHeaderLabel label="Fair EV" slug="enterprise-value" /></th>
              <th className="numeric-col"><TableHeaderLabel label="Fair Value / Share" slug="fair-value-per-share" /></th>
              <th className="numeric-col"><TableHeaderLabel label="Deep Discount" slug="target-buy" /></th>
              <th className="numeric-col"><TableHeaderLabel label="Standard Discount" slug="target-buy" /></th>
              <th className="numeric-col"><TableHeaderLabel label="Modest Discount" slug="accumulate-threshold" /></th>
              <th><TableHeaderLabel label="Validity" slug="warnings" /></th>
              <th><TableHeaderLabel label="Note" slug="warnings" /></th>
            </tr>
          </thead>
          <tbody>
            {selected.scenarios.map((scenario: BuyLevelsScenarioData) => (
              <tr key={`${scenario.name}-${selected.years}`}>
                <td>{scenario.name}</td>
                <td className="numeric-col">{formatBuyLevelsPercentDecimal(scenario.growthRate ?? null)}</td>
                <td className="numeric-col">{formatBuyLevelsPercentDecimal(scenario.discountRate ?? null)}</td>
                <td className="numeric-col">{formatBuyLevelsPercentDecimal(scenario.terminalGrowth ?? null)}</td>
                <td className="numeric-col">{formatBuyLevelsCurrency(scenario.fairValueEv ?? null)}</td>
                <td className="numeric-col">{formatBuyLevelsCurrency(scenario.fairValuePrice ?? null)}</td>
                <td className="numeric-col">{formatBuyLevelsCurrency(scenario.entryLadder.strongBuy ?? null)}</td>
                <td className="numeric-col">{formatBuyLevelsCurrency(scenario.entryLadder.buy ?? null)}</td>
                <td className="numeric-col">{formatBuyLevelsCurrency(scenario.entryLadder.accumulate ?? null)}</td>
                <td>
                  <span className={`signal-pill ${scenario.valid ? "signal-pass" : "signal-fail"}`}>
                    {scenario.valid ? "VALID" : "INVALID"}
                  </span>
                </td>
                <td>{scenario.note || BUY_LEVELS_UNAVAILABLE}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function pickScenario(horizon: BuyLevelsHorizonData, scenarioName: BuyLevelsScenario): BuyLevelsScenarioData | undefined {
  return horizon.scenarios.find((scenario) => scenario.name === scenarioName);
}

function computeBuyLevelsGauge(currentPrice: number | null, fairValuePrice: number | null): {
  currentPrice: number | null;
  fairValuePrice: number | null;
  marginOfSafetyPct: number | null;
  markerPct: number;
  state: string;
  tone: BuyLevelsGaugeTone;
} {
  if (currentPrice === null || fairValuePrice === null || !Number.isFinite(currentPrice) || !Number.isFinite(fairValuePrice) || currentPrice <= 0) {
    return {
      currentPrice: currentPrice ?? null,
      fairValuePrice: fairValuePrice ?? null,
      marginOfSafetyPct: null,
      markerPct: 50,
      state: "Unavailable",
      tone: "unavailable",
    };
  }

  const marginOfSafetyPct = ((fairValuePrice - currentPrice) / currentPrice) * 100;
  let state = "Fair";
  let tone: BuyLevelsGaugeTone = "fair";
  if (marginOfSafetyPct >= 30) {
    state = "Deep Value";
    tone = "deep-value";
  } else if (marginOfSafetyPct >= 10) {
    state = "Value";
    tone = "value";
  } else if (marginOfSafetyPct > -10) {
    state = "Fair";
    tone = "fair";
  } else if (marginOfSafetyPct >= -30) {
    state = "Rich";
    tone = "rich";
  } else {
    state = "Overpriced";
    tone = "overpriced";
  }

  const clamped = clampNumber(marginOfSafetyPct, -60, 60);
  const markerPct = ((60 - clamped) / 120) * 100;
  return {
    currentPrice,
    fairValuePrice,
    marginOfSafetyPct,
    markerPct,
    state,
    tone,
  };
}

function describeBuyLevelsGauge(marginOfSafetyPct: number | null): string {
  if (marginOfSafetyPct === null || !Number.isFinite(marginOfSafetyPct)) {
    return "Current price and fair value are both required before the gauge can be placed.";
  }
  if (marginOfSafetyPct > 0.5) {
    return `Current price sits ${formatAbsolutePercentValue(marginOfSafetyPct)} below base fair value.`;
  }
  if (marginOfSafetyPct < -0.5) {
    return `Current price sits ${formatAbsolutePercentValue(marginOfSafetyPct)} above base fair value.`;
  }
  return "Current price is roughly aligned with base fair value.";
}

function reverseMetricsFromAnalysis(analysis?: InvestmentAnalysisV2): {
  scenario: BuyLevelsScenario;
  scenarioGrowth: number | null;
  impliedGrowthApprox: number | null;
  growthGapPp: number | null;
  status: BuyLevelsReverseStatus;
  note: string;
} | null {
  if (!analysis) {
    return null;
  }
  const diagnostic = analysis.constantGrowthDiagnostic;
  return {
    scenario: "BASE",
    scenarioGrowth: null,
    impliedGrowthApprox: diagnostic.impliedGrowth,
    growthGapPp: null,
    status: diagnostic.impliedGrowth === null ? "UNAVAILABLE" : "ALIGNED",
    note: diagnostic.note,
  };
}

function displayScreeningDisposition(report: NonNullable<InvestmentDetailResponse["report"]>): string {
  return report.analysisV2?.screening.disposition || report.finalActionMapping.resolvedAction;
}

function displayQuantitativeScore(report: NonNullable<InvestmentDetailResponse["report"]>): number {
  return report.analysisV2?.coverage.conservativeScore ?? report.scoringSummary.normalizedScore100;
}

function computeReverseDCFHeuristic(
  currentPrice: number | null,
  selectedHorizon: BuyLevelsHorizonData,
  scenario: BuyLevelsScenarioData | undefined,
): {
  scenario: BuyLevelsScenario;
  scenarioGrowth: number | null;
  impliedGrowthApprox: number | null;
  growthGapPp: number | null;
  status: BuyLevelsReverseStatus;
  note: string;
} {
  const scenarioName = (scenario?.name || "BASE") as BuyLevelsScenario;
  const suffix = "Heuristic estimate; not a solved implied-growth model.";
  const scenarioGrowth = scenario?.growthRate;
  const fairValue = scenario?.fairValuePrice ?? null;
  const anchor = selectedHorizon.growthAnchor ?? null;
  const years = selectedHorizon.years;

  if (
    currentPrice === null ||
    fairValue === null ||
    anchor === null ||
    !Number.isFinite(currentPrice) ||
    !Number.isFinite(fairValue) ||
    !Number.isFinite(anchor) ||
    !Number.isFinite(scenarioGrowth) ||
    currentPrice <= 0 ||
    fairValue <= 0 ||
    years <= 0
  ) {
    return {
      scenario: scenarioName,
      scenarioGrowth: Number.isFinite(scenarioGrowth) ? scenarioGrowth : null,
      impliedGrowthApprox: null,
      growthGapPp: null,
      status: "UNAVAILABLE",
      note: `Insufficient inputs for reverse DCF diagnostics. ${suffix}`,
    };
  }

  const priceToFair = currentPrice / fairValue;
  const impliedGrowthApprox = (Math.pow(priceToFair, 1 / years) - 1) + scenarioGrowth;
  if (!Number.isFinite(impliedGrowthApprox)) {
    return {
      scenario: scenarioName,
      scenarioGrowth,
      impliedGrowthApprox: null,
      growthGapPp: null,
      status: "UNAVAILABLE",
      note: `Unable to compute finite implied growth approximation. ${suffix}`,
    };
  }

  const growthGapPp = (impliedGrowthApprox - anchor) * 100;
  const absGap = Math.abs(growthGapPp);

  let status: BuyLevelsReverseStatus = "ALIGNED";
  if (growthGapPp < -2) {
    status = "DISCOUNTED";
  } else if (absGap <= 2) {
    status = "ALIGNED";
  } else if (absGap <= 6) {
    status = "STRETCHED";
  } else {
    status = "AGGRESSIVE";
  }

  const notePrefix = status === "DISCOUNTED"
    ? "Price-implied growth sits below the anchor growth."
    : status === "ALIGNED"
      ? "Price-implied growth is close to the anchor growth."
      : status === "STRETCHED"
        ? "Price-implied growth is above anchor by a moderate amount."
        : "Price-implied growth is materially above anchor.";

  return {
    scenario: scenarioName,
    scenarioGrowth,
    impliedGrowthApprox,
    growthGapPp,
    status,
    note: `${notePrefix} ${suffix}`,
  };
}

function BuyLevelsRateBaselineCard({
  metrics,
}: {
  metrics: RateBaselineMetrics | null;
}): JSX.Element {
  const hasMacroInputs = metrics !== null;

  return (
    <article className="buy-levels-top-card">
      <h3><SectionHeading label="Rates Baseline" slug="rates-baseline" /></h3>
      <div className="buy-levels-kv-list">
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Current FCF Yield" slug="fcf-yield" /></span>
          <span className="numeric">{formatMacroPercentNullable(metrics?.currentFcfYield ?? null)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Current FCF Yield - 3M" slug="fcf-3m" /></span>
          <span className="numeric">{formatBasisPointsNullable(metrics?.currentSpreadToThreeMonth ?? null)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Current FCF Yield - 10Y" slug="fcf-10y" /></span>
          <span className="numeric">{formatBasisPointsNullable(metrics?.currentSpreadToTenYear ?? null)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Buy Threshold Yield" slug="buy-threshold-yield" /></span>
          <span className="numeric">{formatMacroPercentNullable(metrics?.buyThresholdYield ?? null)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Buy Threshold Yield - 10Y" slug="buy-yld-10y" /></span>
          <span className="numeric">{formatBasisPointsNullable(metrics?.buyThresholdSpreadToTenYear ?? null)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="3M Bill" slug="3m-bill" /></span>
          <span className="numeric">{formatMacroPercentNullable(metrics?.threeMonthBill ?? null)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="10Y Note" slug="10y-note" /></span>
          <span className="numeric">{formatMacroPercentNullable(metrics?.tenYearNote ?? null)}</span>
        </div>
        <div className="buy-levels-kv-row">
          <span><InlineGlossaryLabel label="Curve Regime" slug="curve-regime" /></span>
          <span className="numeric">{describeCurveRegime(metrics?.curveRegime)}</span>
        </div>
      </div>
      {!hasMacroInputs && <p className="buy-levels-note">Rates baseline is unavailable. Run the macro sync and recompute workflow to populate policy and Treasury context.</p>}
    </article>
  );
}

function computeBuyLevelsDeltaPct(targetPrice: number | null, currentPrice: number | null): number | null {
  if (
    targetPrice === null ||
    currentPrice === null ||
    !Number.isFinite(targetPrice) ||
    !Number.isFinite(currentPrice) ||
    currentPrice <= 0
  ) {
    return null;
  }
  return ((targetPrice - currentPrice) / currentPrice) * 100;
}

function computeBuyLevelsMultipleDelta(targetMultiple: number | null | undefined, currentMultiple: number | null): number | null {
  if (
    targetMultiple === null ||
    targetMultiple === undefined ||
    currentMultiple === null ||
    !Number.isFinite(targetMultiple) ||
    !Number.isFinite(currentMultiple)
  ) {
    return null;
  }
  return targetMultiple - currentMultiple;
}

function computeBuyLevelsBandsPlacement(currentMultiple: number | null, bands: BuyLevelsBandData[]): {
  currentMultiple: number | null;
  activeBandLabel: string | null;
  activeBandRange: string | null;
  summaryNote: string;
} {
  if (currentMultiple === null || !Number.isFinite(currentMultiple) || currentMultiple <= 0) {
    return {
      currentMultiple: null,
      activeBandLabel: null,
      activeBandRange: null,
      summaryNote: "Current EV/FCF is unavailable, so band placement cannot be determined.",
    };
  }

  const matchedBand = bands.find((band) => isBuyLevelsMultipleWithinBand(currentMultiple, band)) ?? null;
  if (!matchedBand) {
    return {
      currentMultiple,
      activeBandLabel: null,
      activeBandRange: null,
      summaryNote: "Current EV/FCF is outside configured bands for the selected mode.",
    };
  }

  return {
    currentMultiple,
    activeBandLabel: matchedBand.label,
    activeBandRange: formatBandRange(matchedBand.min, matchedBand.max),
    summaryNote: "Boundary values use first-match placement across ordered bands.",
  };
}

function impliedTargetPrice(
  view: BuyLevelsModeViewData["impliedPrices"],
  label: string,
): number | null {
  const level = view.levels.find((candidate) => candidate.label.toUpperCase() === label.toUpperCase());
  return level?.targetPrice ?? null;
}

function computeCustomDCFScenario(input: {
  fcfTtm: number | null;
  netDebt: number | null;
  sharesOutstanding: number | null;
  horizonYears: number;
  growthRatePct: number;
  discountRatePct: number;
  terminalGrowthPct: number;
}): {
  fairValueEv: number | null;
  fairValuePrice: number | null;
  entryLadder: {
    strongBuy: number | null;
    buy: number | null;
    accumulate: number | null;
  };
  valid: boolean;
  note: string;
} {
  const fcf = input.fcfTtm ?? null;
  const netDebt = input.netDebt ?? null;
  const sharesOutstanding = input.sharesOutstanding ?? null;
  const growthRate = input.growthRatePct / 100;
  const discountRate = input.discountRatePct / 100;
  const terminalGrowth = input.terminalGrowthPct / 100;
  if (
    fcf == null || !Number.isFinite(fcf) || fcf <= 0 ||
    netDebt == null || !Number.isFinite(netDebt) ||
    sharesOutstanding == null || !Number.isFinite(sharesOutstanding) || sharesOutstanding <= 0
  ) {
    return {
      fairValueEv: null,
      fairValuePrice: null,
      entryLadder: { strongBuy: null, buy: null, accumulate: null },
      valid: false,
      note: "Seed inputs are incomplete for a custom DCF run.",
    };
  }
  if (!Number.isFinite(growthRate) || !Number.isFinite(discountRate) || !Number.isFinite(terminalGrowth)) {
    return {
      fairValueEv: null,
      fairValuePrice: null,
      entryLadder: { strongBuy: null, buy: null, accumulate: null },
      valid: false,
      note: "Growth, discount, and terminal inputs must be finite.",
    };
  }
  if (discountRate <= terminalGrowth) {
    return {
      fairValueEv: null,
      fairValuePrice: null,
      entryLadder: { strongBuy: null, buy: null, accumulate: null },
      valid: false,
      note: "Discount rate must stay above terminal growth.",
    };
  }
  let fairValueEv = 0;
  let projectedFcf = fcf;
  for (let year = 1; year <= input.horizonYears; year += 1) {
    projectedFcf *= (1 + growthRate);
    fairValueEv += projectedFcf / ((1 + discountRate) ** year);
  }
  const terminalFcf = projectedFcf * (1 + terminalGrowth);
  const terminalValue = terminalFcf / (discountRate - terminalGrowth);
  fairValueEv += terminalValue / ((1 + discountRate) ** input.horizonYears);
  const fairEquity = fairValueEv - netDebt;
  const fairValuePrice = fairEquity / sharesOutstanding;
  return {
    fairValueEv,
    fairValuePrice,
    entryLadder: {
      strongBuy: fairValuePrice * (1 - 0.30),
      buy: fairValuePrice * (1 - 0.20),
      accumulate: fairValuePrice * (1 - 0.10),
    },
    valid: true,
    note: "",
  };
}

function buildCustomDCFCopy(
  ticker: string,
  scenario: CustomDCFState,
  result: ReturnType<typeof computeCustomDCFScenario>,
): string {
  return [
    `${ticker} custom DCF scenario`,
    `Horizon: ${scenario.horizon}`,
    `Growth rate: ${scenario.growthRate.toFixed(1)}%`,
    `Discount rate: ${scenario.discountRate.toFixed(1)}%`,
    `Terminal growth: ${scenario.terminalGrowth.toFixed(1)}%`,
    `Fair value per share: ${formatCurrencyNullable(result.fairValuePrice)}`,
    `Deep-discount price: ${formatCurrencyNullable(result.entryLadder.strongBuy)}`,
    `Standard-discount price: ${formatCurrencyNullable(result.entryLadder.buy)}`,
    `Modest-discount price: ${formatCurrencyNullable(result.entryLadder.accumulate)}`,
    `Validity: ${result.valid ? "valid" : result.note || "invalid"}`,
    "This local scenario does not change the research disposition.",
  ].join("\n");
}

function isBuyLevelsMultipleWithinBand(multiple: number, band: BuyLevelsBandData): boolean {
  if (!Number.isFinite(multiple)) {
    return false;
  }
  const minOk = band.min === undefined || multiple >= band.min;
  const maxOk = band.max === undefined || multiple <= band.max;
  return minOk && maxOk;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const BUY_LEVELS_UNAVAILABLE = "Unavailable";

function formatBuyLevelsCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return BUY_LEVELS_UNAVAILABLE;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatBuyLevelsRatio(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return BUY_LEVELS_UNAVAILABLE;
  }
  return `${value.toFixed(1)}x`;
}

function formatBuyLevelsPercentDecimal(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return BUY_LEVELS_UNAVAILABLE;
  }
  return `${(value * 100).toFixed(1)}%`;
}

function formatBuyLevelsSignedPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return BUY_LEVELS_UNAVAILABLE;
  }
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

function formatBuyLevelsSignedRatio(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return BUY_LEVELS_UNAVAILABLE;
  }
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}x`;
}

function formatBuyLevelsSignedPP(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return BUY_LEVELS_UNAVAILABLE;
  }
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)} pp`;
}

function formatBuyLevelsNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return BUY_LEVELS_UNAVAILABLE;
  }
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumberCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "N/A";
  }
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
}

function formatETFPercentValue(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "N/A";
  }
  return `${value.toFixed(2)}%`;
}

function formatRatio(value: number): string {
  if (!Number.isFinite(value) || value === 0) {
    return "-";
  }
  return `${value.toFixed(1)}x`;
}

function formatRatioOrNA(value: number): string {
  if (!Number.isFinite(value) || value === 0) {
    return "N/A";
  }
  return `${value.toFixed(1)}x`;
}

function formatRatioNullable(value: number | null): string {
  if (value === null || !Number.isFinite(value) || value === 0) {
    return "N/A";
  }
  return `${value.toFixed(1)}x`;
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value) || value === 0) {
    return "-";
  }
  return `${(value * 100).toFixed(1)}%`;
}

function formatPercentNullable(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  return `${(value * 100).toFixed(1)}%`;
}

function formatPercentValueNullable(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  return `${value.toFixed(1)}%`;
}

function formatNumberNullable(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  return value.toFixed(2);
}

function formatDateTimeLabel(value: string | null | undefined): string {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function entryLevelLabel(value: string): string {
  const normalized = value.trim().toUpperCase().replace(/[_-]+/g, " ");
  if (normalized === "STRONG BUY") return "Deep Discount";
  if (normalized === "BUY") return "Standard Discount";
  if (normalized === "ACCUMULATE") return "Modest Discount";
  return value;
}

function formatCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return "-";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCurrencyNullable(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  return formatCurrency(value);
}

function formatSignedCurrencyNullable(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  const abs = formatCurrency(Math.abs(value));
  if (value > 0) {
    return `+${abs}`;
  }
  if (value < 0) {
    return `-${abs}`;
  }
  return abs;
}

function formatSignedPercentNullable(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  if (value > 0) {
    return `+${value.toFixed(2)}%`;
  }
  if (value < 0) {
    return `${value.toFixed(2)}%`;
  }
  return `${value.toFixed(2)}%`;
}

function formatAbsolutePercentValue(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "N/A";
  }
  return `${Math.abs(value).toFixed(1)}%`;
}

function formatSignedPercentValueNullable(value: number | null): string {
  return formatSignedPercentNullable(value);
}

function formatPP(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)} pp`;
}

function shortCurrency(value: number): string {
  if (!Number.isFinite(value) || value === 0) {
    return "0";
  }
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  return `${value.toFixed(0)}`;
}

function zoneLabel(zone: string): string {
  if (!zone) {
    return "-";
  }
  if (zone === "UNAVAILABLE") {
    return "Unavailable";
  }
  return zone;
}

function boolLabel(value: boolean): string {
  return value ? "YES" : "NO";
}

function formatScore(value: number, max: number): string {
  if (!Number.isFinite(value)) {
    return "N/A";
  }
  const precision = max === 100 ? 1 : 0;
  return `${value.toFixed(precision)} / ${max}`;
}

function formatBandRange(min?: number, max?: number): string {
  const hasMin = typeof min === "number";
  const hasMax = typeof max === "number";
  if (hasMin && hasMax) {
    return `${min!.toFixed(0)}x - ${max!.toFixed(0)}x`;
  }
  if (hasMin) {
    return `>= ${min!.toFixed(0)}x`;
  }
  if (hasMax) {
    return `<= ${max!.toFixed(0)}x`;
  }
  return "N/A";
}

function formatMetricAxisTick(value: number, kind: MetricKind): string {
  if (!Number.isFinite(value)) {
    return "0";
  }
  switch (kind) {
    case "currency":
      return shortCurrency(value);
    case "ratio":
      return `${value.toFixed(1)}x`;
    case "percentDecimal":
      return `${(value * 100).toFixed(0)}%`;
    case "percentValue":
      return `${value.toFixed(0)}%`;
    case "pp":
      return `${value.toFixed(0)}pp`;
    case "number":
    default:
      return value.toFixed(1);
  }
}

function formatMetricValue(value: number, kind: MetricKind): string {
  if (!Number.isFinite(value)) {
    return "N/A";
  }
  switch (kind) {
    case "currency":
      return formatCurrency(value);
    case "ratio":
      return `${value.toFixed(2)}x`;
    case "percentDecimal":
      return `${(value * 100).toFixed(2)}%`;
    case "percentValue":
      return `${value.toFixed(2)}%`;
    case "pp":
      return `${value.toFixed(2)} pp`;
    case "number":
    default:
      return value.toFixed(2);
  }
}

function metricLegend(kind: MetricKind): string {
  switch (kind) {
    case "currency":
      return "USD";
    case "ratio":
      return "multiple (x)";
    case "percentDecimal":
      return "percentage (%)";
    case "percentValue":
      return "percentage points (%)";
    case "pp":
      return "delta (pp)";
    case "number":
    default:
      return "raw";
  }
}

function dedupeStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const value of values) {
    const normalized = value.trim();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    deduped.push(normalized);
  }
  return deduped;
}

function hasEquityReportShape(report: InvestmentDetailResponse["report"] | undefined): report is InvestmentDetailResponse["report"] {
  if (!report || typeof report !== "object") {
    return false;
  }
  const candidate = report as {
    businessQuality?: unknown;
    moatProxy?: unknown;
    valuationAndDiscipline?: unknown;
    redFlags?: unknown;
    finalActionMapping?: unknown;
  };
  return Boolean(
    candidate.businessQuality &&
    candidate.moatProxy &&
    candidate.valuationAndDiscipline &&
    candidate.finalActionMapping &&
    Array.isArray(candidate.redFlags),
  );
}

function computeTrailingReturn(
  points: Array<{ close: number }>,
  lookbackDays: number,
): number | null {
  if (lookbackDays <= 0 || points.length <= lookbackDays) {
    return null;
  }
  const endPoint = points[points.length - 1];
  const startPoint = points[points.length - 1 - lookbackDays];
  if (!endPoint || !startPoint) {
    return null;
  }
  if (!Number.isFinite(endPoint.close) || !Number.isFinite(startPoint.close) || startPoint.close <= 0) {
    return null;
  }
  return ((endPoint.close / startPoint.close) - 1) * 100;
}

async function copyToClipboard(value: string, setStatus: (value: string) => void): Promise<void> {
  if (!value) {
    setStatus("No Text");
    setTimeout(() => setStatus(""), 1500);
    return;
  }
  try {
    await navigator.clipboard.writeText(value);
    setStatus("Copied");
  } catch (_) {
    setStatus("Copy Failed");
  }
  setTimeout(() => setStatus(""), 1500);
}

function buildBuyLevelsCopyContext(input: BuyLevelsCopyInput): {
  selectedHorizon: BuyLevelsHorizonData;
  baseScenario: BuyLevelsScenarioData | undefined;
  selectedReverseScenario: BuyLevelsScenarioData | undefined;
  gaugeMetrics: {
    currentPrice: number | null;
    fairValuePrice: number | null;
    marginOfSafetyPct: number | null;
    markerPct: number;
    state: string;
    tone: BuyLevelsGaugeTone;
  };
  reverseMetrics: {
    scenario: BuyLevelsScenario;
    scenarioGrowth: number | null;
    impliedGrowthApprox: number | null;
    growthGapPp: number | null;
    status: BuyLevelsReverseStatus;
    note: string;
  };
  bandsPlacement: {
    currentMultiple: number | null;
    activeBandLabel: string | null;
    activeBandRange: string | null;
    summaryNote: string;
  };
} {
  const selectedHorizon = input.horizon === "3Y" ? input.view.dcf.horizon3y : input.view.dcf.horizon5y;
  const baseScenario = pickScenario(selectedHorizon, "BASE");
  const selectedReverseScenario = pickScenario(selectedHorizon, input.reverseScenario);
  const gaugeMetrics = computeBuyLevelsGauge(
    input.buyLevels.currentPrice ?? null,
    baseScenario?.fairValuePrice ?? null,
  );
	const reverseMetrics = reverseMetricsFromAnalysis(input.data.report?.analysisV2) || computeReverseDCFHeuristic(
	  input.buyLevels.currentPrice ?? null,
	  selectedHorizon,
	  selectedReverseScenario,
	);
  const bandsPlacement = computeBuyLevelsBandsPlacement(input.view.multiples.currentMultiple ?? null, input.view.multiples.bands);

  return {
    selectedHorizon,
    baseScenario,
    selectedReverseScenario,
    gaugeMetrics,
    reverseMetrics,
    bandsPlacement,
  };
}

function buildCopyFriendlyBuyLevels(input: BuyLevelsCopyInput): string {
  const { data, buyLevels, view, mode, horizon, reverseScenario } = input;
  const {
    selectedHorizon,
    baseScenario,
    selectedReverseScenario,
    gaugeMetrics,
    reverseMetrics,
    bandsPlacement,
  } = buildBuyLevelsCopyContext(input);
  const lines: string[] = [];

	lines.push(`${data.ticker} - ${data.report?.analysisV2 ? "House Multiple Threshold Snapshot" : "Buy Levels Snapshot"}`);
  lines.push(buildDetailDateLine(data));
  lines.push("");

  lines.push("Context");
  lines.push(`- Mode: ${mode}`);
  lines.push(`- DCF Horizon: ${horizon}`);
  lines.push(`- Reverse Scenario: ${reverseScenario}`);
  lines.push("");

  lines.push("Top Row Metrics");
  lines.push(`- Quality Tier: ${buyLevels.qualityTier}`);
  lines.push(`- Current Price (Derived): ${formatBuyLevelsCurrency(buyLevels.currentPrice ?? null)}`);
  lines.push(`- Weighted Avg Shares: ${formatBuyLevelsNumber(buyLevels.sharesOutstanding ?? null)}`);
  lines.push(`- FCF (TTM): ${formatBuyLevelsCurrency(data.summary.fcfTtm)}`);
  lines.push(`- Net Debt: ${formatBuyLevelsCurrency(data.summary.netDebt)}`);
  lines.push(`- Growth Anchor: ${formatBuyLevelsPercentDecimal(selectedHorizon.growthAnchor ?? null)}`);
  lines.push(`- Discount Rate (BASE): ${formatBuyLevelsPercentDecimal(baseScenario?.discountRate ?? null)}`);
  lines.push(`- Terminal Growth (BASE): ${formatBuyLevelsPercentDecimal(baseScenario?.terminalGrowth ?? null)}`);
  lines.push(`- Fair Value (BASE): ${formatBuyLevelsCurrency(gaugeMetrics.fairValuePrice)}`);
  lines.push(`- Margin of Safety: ${formatBuyLevelsSignedPercent(gaugeMetrics.marginOfSafetyPct)}`);
  lines.push(`- Valuation State: ${gaugeMetrics.state}`);
  lines.push(`- Reverse Scenario Growth: ${formatBuyLevelsPercentDecimal(selectedReverseScenario?.growthRate ?? null)}`);
  lines.push(`- Implied Growth Approx: ${formatBuyLevelsPercentDecimal(reverseMetrics.impliedGrowthApprox)}`);
  lines.push(`- Growth Gap vs Anchor: ${formatBuyLevelsSignedPP(reverseMetrics.growthGapPp)}`);
	lines.push(`- ${data.report?.analysisV2 ? "Constant-growth Diagnostic" : "Reverse DCF"} Status: ${reverseMetrics.status}`);
	lines.push(`- ${data.report?.analysisV2 ? "Constant-growth Diagnostic" : "Reverse DCF"} Note: ${reverseMetrics.note}`);
  lines.push("");

  lines.push(`EV/FCF Bands (${mode})`);
  lines.push(`- Current EV/FCF: ${formatBuyLevelsRatio(bandsPlacement.currentMultiple)}`);
  lines.push(`- Current Band: ${bandsPlacement.activeBandLabel ? entryLevelLabel(bandsPlacement.activeBandLabel) : BUY_LEVELS_UNAVAILABLE}`);
  lines.push(`- Band Range: ${bandsPlacement.activeBandRange || BUY_LEVELS_UNAVAILABLE}`);
  lines.push(`- Context: ${bandsPlacement.summaryNote}`);
  for (const band of view.multiples.bands) {
    lines.push(
      `- ${entryLevelLabel(band.label)}: range=${formatBandRange(band.min, band.max)}, multiple=${formatBuyLevelsRatio(band.multiple ?? null)}, delta=${formatBuyLevelsSignedRatio(computeBuyLevelsMultipleDelta(band.multiple ?? null, bandsPlacement.currentMultiple))}`,
    );
  }
  lines.push("");

  lines.push(`Implied Entry Prices (${mode})`);
  for (const level of view.impliedPrices.levels) {
    lines.push(
      `- ${entryLevelLabel(level.label)}: multiple=${formatBuyLevelsRatio(level.multiple ?? null)}, targetEV=${formatBuyLevelsCurrency(level.targetEv ?? null)}, targetEquity=${formatBuyLevelsCurrency(level.targetEquity ?? null)}, targetPrice=${formatBuyLevelsCurrency(level.targetPrice ?? null)}, deltaVsCurrent=${formatBuyLevelsSignedPercent(computeBuyLevelsDeltaPct(level.targetPrice ?? null, buyLevels.currentPrice ?? null))}`,
    );
  }
  lines.push("");

  lines.push(`Discounted Cash Flow (${horizon})`);
  lines.push(`- Growth Anchor: ${formatBuyLevelsPercentDecimal(selectedHorizon.growthAnchor ?? null)}`);
  lines.push(`- Source: ${selectedHorizon.growthSource || "N/A"}`);
  for (const scenario of selectedHorizon.scenarios) {
    lines.push(
      `- ${scenario.name}: growth=${formatBuyLevelsPercentDecimal(scenario.growthRate ?? null)}, discount=${formatBuyLevelsPercentDecimal(scenario.discountRate ?? null)}, terminal=${formatBuyLevelsPercentDecimal(scenario.terminalGrowth ?? null)}, fairEV=${formatBuyLevelsCurrency(scenario.fairValueEv ?? null)}, fairValue/share=${formatBuyLevelsCurrency(scenario.fairValuePrice ?? null)}, deepDiscount=${formatBuyLevelsCurrency(scenario.entryLadder.strongBuy ?? null)}, standardDiscount=${formatBuyLevelsCurrency(scenario.entryLadder.buy ?? null)}, modestDiscount=${formatBuyLevelsCurrency(scenario.entryLadder.accumulate ?? null)}, validity=${scenario.valid ? "VALID" : "INVALID"}, note=${scenario.note || BUY_LEVELS_UNAVAILABLE}`,
    );
  }

  if (buyLevels.warnings && buyLevels.warnings.length > 0) {
    lines.push("");
	lines.push(data.report?.analysisV2 ? "House Multiple Threshold Warnings" : "Buy Levels Warnings");
    for (const warning of buyLevels.warnings) {
      lines.push(`- [${warning.severity}] ${warning.message}`);
    }
  }

  return lines.join("\n");
}

function buildCopyFriendlyBuyLevelsMarkdown(input: BuyLevelsCopyInput): string {
  const { data, buyLevels, view, mode, horizon, reverseScenario } = input;
  const {
    selectedHorizon,
    baseScenario,
    selectedReverseScenario,
    gaugeMetrics,
    reverseMetrics,
    bandsPlacement,
  } = buildBuyLevelsCopyContext(input);
  const lines: string[] = [];

	lines.push(`# ${data.ticker} ${data.report?.analysisV2 ? "House Multiple Threshold Snapshot" : "Buy Levels Snapshot"}`);
  lines.push(buildDetailDateLine(data, true));
  lines.push("");

  lines.push("## Context");
  lines.push(`- Mode: ${mode}`);
  lines.push(`- DCF Horizon: ${horizon}`);
  lines.push(`- Reverse Scenario: ${reverseScenario}`);
  lines.push("");

  lines.push("## Top Row Metrics");
  lines.push(`- Quality Tier: ${buyLevels.qualityTier}`);
  lines.push(`- Current Price (Derived): ${formatBuyLevelsCurrency(buyLevels.currentPrice ?? null)}`);
  lines.push(`- Weighted Avg Shares: ${formatBuyLevelsNumber(buyLevels.sharesOutstanding ?? null)}`);
  lines.push(`- FCF (TTM): ${formatBuyLevelsCurrency(data.summary.fcfTtm)}`);
  lines.push(`- Net Debt: ${formatBuyLevelsCurrency(data.summary.netDebt)}`);
  lines.push(`- Growth Anchor: ${formatBuyLevelsPercentDecimal(selectedHorizon.growthAnchor ?? null)}`);
  lines.push(`- Discount Rate (BASE): ${formatBuyLevelsPercentDecimal(baseScenario?.discountRate ?? null)}`);
  lines.push(`- Terminal Growth (BASE): ${formatBuyLevelsPercentDecimal(baseScenario?.terminalGrowth ?? null)}`);
  lines.push(`- Fair Value (BASE): ${formatBuyLevelsCurrency(gaugeMetrics.fairValuePrice)}`);
  lines.push(`- Margin of Safety: ${formatBuyLevelsSignedPercent(gaugeMetrics.marginOfSafetyPct)}`);
  lines.push(`- Valuation State: ${gaugeMetrics.state}`);
  lines.push(`- Reverse Scenario Growth: ${formatBuyLevelsPercentDecimal(selectedReverseScenario?.growthRate ?? null)}`);
  lines.push(`- Implied Growth Approx: ${formatBuyLevelsPercentDecimal(reverseMetrics.impliedGrowthApprox)}`);
  lines.push(`- Growth Gap vs Anchor: ${formatBuyLevelsSignedPP(reverseMetrics.growthGapPp)}`);
	lines.push(`- ${data.report?.analysisV2 ? "Constant-growth Diagnostic" : "Reverse DCF"} Status: ${reverseMetrics.status}`);
	lines.push(`- ${data.report?.analysisV2 ? "Constant-growth Diagnostic" : "Reverse DCF"} Note: ${reverseMetrics.note}`);
  lines.push("");

  lines.push(`## EV/FCF Bands (${mode})`);
  lines.push(`- Current EV/FCF: ${formatBuyLevelsRatio(bandsPlacement.currentMultiple)}`);
  lines.push(`- Current Band: ${bandsPlacement.activeBandLabel ? entryLevelLabel(bandsPlacement.activeBandLabel) : BUY_LEVELS_UNAVAILABLE}`);
  lines.push(`- Band Range: ${bandsPlacement.activeBandRange || BUY_LEVELS_UNAVAILABLE}`);
  lines.push(`- Context: ${bandsPlacement.summaryNote}`);
  lines.push("");
  lines.push("| Level | Range | Representative Multiple | Delta vs Current |");
  lines.push("| --- | --- | ---: | ---: |");
  for (const band of view.multiples.bands) {
    lines.push(
      `| ${toMarkdownCell(entryLevelLabel(band.label))} | ${toMarkdownCell(formatBandRange(band.min, band.max))} | ${toMarkdownCell(formatBuyLevelsRatio(band.multiple ?? null))} | ${toMarkdownCell(formatBuyLevelsSignedRatio(computeBuyLevelsMultipleDelta(band.multiple ?? null, bandsPlacement.currentMultiple)))} |`,
    );
  }
  lines.push("");

  lines.push(`## Implied Entry Prices (${mode})`);
  lines.push("| Level | Multiple | Target EV | Target Equity | Target Price | Delta vs Current |");
  lines.push("| --- | ---: | ---: | ---: | ---: | ---: |");
  for (const level of view.impliedPrices.levels) {
    lines.push(
      `| ${toMarkdownCell(entryLevelLabel(level.label))} | ${toMarkdownCell(formatBuyLevelsRatio(level.multiple ?? null))} | ${toMarkdownCell(formatBuyLevelsCurrency(level.targetEv ?? null))} | ${toMarkdownCell(formatBuyLevelsCurrency(level.targetEquity ?? null))} | ${toMarkdownCell(formatBuyLevelsCurrency(level.targetPrice ?? null))} | ${toMarkdownCell(formatBuyLevelsSignedPercent(computeBuyLevelsDeltaPct(level.targetPrice ?? null, buyLevels.currentPrice ?? null)))} |`,
    );
  }
  lines.push("");

  lines.push(`## Discounted Cash Flow (${horizon})`);
  lines.push(`- Growth Anchor: ${formatBuyLevelsPercentDecimal(selectedHorizon.growthAnchor ?? null)}`);
  lines.push(`- Source: ${selectedHorizon.growthSource || "N/A"}`);
  lines.push("");
  lines.push("| Scenario | Growth | Discount | Terminal | Fair EV | Fair Value/Share | Deep Discount | Standard Discount | Modest Discount | Validity | Note |");
  lines.push("| --- | ---: | ---: | ---: | ---: | ---: | ---: | ---: | ---: | --- | --- |");
  for (const scenario of selectedHorizon.scenarios) {
    lines.push(
      `| ${toMarkdownCell(scenario.name)} | ${toMarkdownCell(formatBuyLevelsPercentDecimal(scenario.growthRate ?? null))} | ${toMarkdownCell(formatBuyLevelsPercentDecimal(scenario.discountRate ?? null))} | ${toMarkdownCell(formatBuyLevelsPercentDecimal(scenario.terminalGrowth ?? null))} | ${toMarkdownCell(formatBuyLevelsCurrency(scenario.fairValueEv ?? null))} | ${toMarkdownCell(formatBuyLevelsCurrency(scenario.fairValuePrice ?? null))} | ${toMarkdownCell(formatBuyLevelsCurrency(scenario.entryLadder.strongBuy ?? null))} | ${toMarkdownCell(formatBuyLevelsCurrency(scenario.entryLadder.buy ?? null))} | ${toMarkdownCell(formatBuyLevelsCurrency(scenario.entryLadder.accumulate ?? null))} | ${scenario.valid ? "VALID" : "INVALID"} | ${toMarkdownCell(scenario.note || BUY_LEVELS_UNAVAILABLE)} |`,
    );
  }

  if (buyLevels.warnings && buyLevels.warnings.length > 0) {
    lines.push("");
	lines.push(data.report?.analysisV2 ? "## House Multiple Threshold Warnings" : "## Buy Levels Warnings");
    for (const warning of buyLevels.warnings) {
      lines.push(`- [${warning.severity}] ${warning.message}`);
    }
  }

  return lines.join("\n");
}

function toMarkdownCell(value: string): string {
  return value.replace(/\|/g, "\\|");
}

function buildCopyFriendlyFundamentals(data: InvestmentDetailResponse): string {
  const report = data.report;
  const lines: string[] = [];

  lines.push(`${data.ticker} - Fundamentals Snapshot`);
  lines.push(buildDetailDateLine(data));
  lines.push("");

  lines.push("Business Quality");
  lines.push(`- Revenue TTM: ${formatCurrency(report.businessQuality.revenue.revenueTtm)}`);
  lines.push(`- Revenue YoY (TTM): ${formatPercentNullable(report.businessQuality.revenue.revenueYoYTtm)}`);
  lines.push(`- Revenue 3Y CAGR: ${formatPercentNullable(report.businessQuality.revenue.revenue3yCagr)}`);
  lines.push(`- Gross Margin: ${formatPercentNullable(report.businessQuality.margins.grossMargin)}`);
  lines.push(`- Industry Gross Margin: ${formatPercentNullable(report.businessQuality.margins.industryMedianGrossMargin)}`);
  lines.push(`- Relative Gross Margin: ${formatPP(report.businessQuality.margins.relativeGrossMarginPp)}`);
  lines.push(`- Operating Margin: ${formatPercentNullable(report.businessQuality.margins.operatingMargin)}`);
  lines.push(`- Industry Operating Margin: ${formatPercentNullable(report.businessQuality.margins.industryMedianOperatingMargin)}`);
  lines.push(`- Relative Operating Margin: ${formatPP(report.businessQuality.margins.relativeOperatingMarginPp)}`);
  lines.push(`- FCF Margin: ${formatPercentNullable(report.businessQuality.margins.fcfMargin)}`);
  lines.push(`- Industry FCF Margin: ${formatPercentNullable(report.businessQuality.margins.industryMedianFcfMargin)}`);
  lines.push(`- Relative FCF Margin: ${formatPP(report.businessQuality.margins.relativeFcfMarginPp)}`);
  lines.push(`- CFO (TTM): ${formatCurrency(report.businessQuality.cashFlow.cfoTtm)}`);
  lines.push(`- CapEx (TTM): ${formatCurrency(report.businessQuality.cashFlow.capexTtm)}`);
  lines.push(`- FCF (TTM): ${formatCurrency(report.businessQuality.cashFlow.fcfTtm)}`);
  lines.push(`- ROIC (TTM): ${formatPercentValueNullable(report.businessQuality.capitalEfficiency.roicTtm)}`);
  lines.push(`- ROE (TTM): ${formatPercentValueNullable(report.businessQuality.capitalEfficiency.roeTtm)}`);
  lines.push(`- Debt / FCF: ${formatRatio(report.businessQuality.balanceSheet.debtToFcfTtm)}`);
  lines.push(`- Current Ratio: ${formatNumberNullable(report.businessQuality.balanceSheet.currentRatio)}`);
  lines.push(`- Score: ${report.businessQuality.score}/65`);
  lines.push("");

	lines.push(report.analysisV2 ? "Historical Durability Proxy" : "Moat Proxy");
  lines.push(`- ROIC persistence: ${report.moatProxy.signals.roicPersistence}`);
  lines.push(`- Margin stability: ${report.moatProxy.signals.marginStability}`);
  lines.push(`- Revenue durability: ${report.moatProxy.signals.revenueDurability}`);
	if (!report.analysisV2) lines.push(`- Reinvestment efficiency: ${report.moatProxy.signals.reinvestmentEfficiency}`);
	lines.push(`- Score: ${report.analysisV2?.factors.durabilityProxy?.earned ?? report.moatProxy.score}/20`);
	lines.push(report.analysisV2 ? `- Evidence Confidence: ${report.analysisV2.dataConfidence}` : `- Confidence Band: ${report.moatConfidence.band}`);
  for (const note of report.moatConfidence.justification || []) {
    lines.push(`- Note: ${note}`);
  }
  lines.push("");

  lines.push("FCF Regime Shift");
  lines.push(`- CapEx / CFO: ${formatPercentValueNullable(report.fcfRegimeShift.capexToCfo)}`);
  lines.push(`- Trend (3Y): ${report.fcfRegimeShift.trend3y}`);
  lines.push(`- Maintenance-like: ${boolLabel(report.fcfRegimeShift.maintenanceLike)}`);
  lines.push(`- Growth-optional: ${boolLabel(report.fcfRegimeShift.growthOptional)}`);
  lines.push(`- Structurally rising: ${boolLabel(report.fcfRegimeShift.structurallyRising)}`);
  lines.push(`- Regime: ${report.fcfRegimeShift.regime}`);
  lines.push(`- Penalty: ${report.fcfRegimeShift.penaltyPoints}`);
  for (const note of report.fcfRegimeShift.justification || []) {
    lines.push(`- Note: ${note}`);
  }
  lines.push("");

  lines.push("Valuation & Capital Discipline");
  lines.push(`- Market Cap: ${formatCurrency(report.valuationAndDiscipline.marketData.marketCap)}`);
  lines.push(`- Enterprise Value: ${formatCurrency(report.valuationAndDiscipline.marketData.enterpriseValue)}`);
  lines.push(`- EV / FCF (TTM): ${formatRatioNullable(report.valuationAndDiscipline.valuation.evFcfTtm)}`);
  lines.push(`- P / FCF (TTM): ${formatRatioNullable(report.valuationAndDiscipline.valuation.pFcfTtm)}`);
  lines.push(`- EV / EBITDA (TTM): ${formatRatioNullable(report.valuationAndDiscipline.valuation.evEbitdaTtm)}`);
  lines.push(`- P / E (TTM): ${formatRatioNullable(report.valuationAndDiscipline.valuation.pERatioTtm)}`);
  lines.push(`- Dividend payout ratio: ${formatPercentValueNullable(report.valuationAndDiscipline.capitalAllocation.dividendPayoutRatio)}`);
  lines.push(`- Buybacks (TTM): ${formatCurrencyNullable(report.valuationAndDiscipline.capitalAllocation.buybacksTtm)}`);
  lines.push(`- Net share trend (3Y): ${report.valuationAndDiscipline.capitalAllocation.netShareCountTrend3y}`);
  lines.push(`- Net share CAGR (3Y): ${formatPercentNullable(report.valuationAndDiscipline.capitalAllocation.netShareCountCagr3y)}`);
  lines.push(`- CapEx / CFO: ${formatPercentValueNullable(report.valuationAndDiscipline.capitalAllocation.capexToCfo)}`);
  lines.push(`- Pre-penalty score: ${report.valuationAndDiscipline.prePenaltyScore}/35`);
  lines.push(`- Adjusted score: ${report.valuationAndDiscipline.adjustedScore}/35`);
  lines.push("");

  lines.push("Red Flags");
  for (const flag of report.redFlags || []) {
    lines.push(`- [${flag.triggered ? "x" : " "}] ${flag.label} (${flag.evidence || "N/A"})`);
  }
  lines.push("");

  lines.push("Scoring Summary");
  lines.push(`- Business Quality: ${report.scoringSummary.businessQualityScore}/65`);
	lines.push(`- ${report.analysisV2 ? "Historical Durability" : "Moat Proxy"}: ${report.analysisV2?.factors.durabilityProxy?.earned ?? report.scoringSummary.moatProxyScore}/20`);
  lines.push(`- Valuation & Discipline: ${report.scoringSummary.valuationScoreAdjusted}/35`);
  lines.push(`- Total: ${report.scoringSummary.totalScore120}/120`);
	lines.push(`- Conservative Quantitative Screen: ${displayQuantitativeScore(report).toFixed(1)}/100`);
  lines.push("");

	lines.push(report.analysisV2 ? "House Multiple Thresholds" : "Buy-Price Overlay");
  lines.push(`- Current EV/FCF: ${formatRatio(report.buyPriceOverlay.evFcfTtm)}`);
  lines.push(`- Current Zone: ${report.buyPriceOverlay.currentZone}`);
  for (const band of report.buyPriceOverlay.bands || []) {
    lines.push(`- ${entryLevelLabel(band.label)}: ${formatBandRange(band.min, band.max)} (${band.description})`);
  }
  lines.push("");

	lines.push(report.analysisV2 ? "Research Disposition" : "Final Action");
	lines.push(`- Screening Disposition: ${displayScreeningDisposition(report)}`);
	if (!report.analysisV2) lines.push(`- Moat Band: ${report.finalActionMapping.moatBand}`);
  lines.push(`- Valuation Zone: ${report.finalActionMapping.valuationZone}`);
  lines.push(`- Score Used: ${formatScore(report.finalActionMapping.score, 100)}`);
  for (const reason of report.finalActionMapping.reasons || []) {
    lines.push(`- Reason: ${reason}`);
  }
  if (report.warnings && report.warnings.length > 0) {
    lines.push("");
    lines.push("Data Warnings");
    for (const warning of report.warnings) {
      lines.push(`- ${warning}`);
    }
  }

  return lines.join("\n");
}

function buildCopyFriendlyFundamentalsMarkdown(data: InvestmentDetailResponse): string {
  const report = data.report;
  const lines: string[] = [];

  lines.push(`# ${data.ticker} Fundamentals Snapshot`);
  lines.push(buildDetailDateLine(data, true));
  lines.push("");

  lines.push("## Business Quality");
  lines.push(`- Revenue TTM: ${formatCurrency(report.businessQuality.revenue.revenueTtm)}`);
  lines.push(`- Revenue YoY (TTM): ${formatPercentNullable(report.businessQuality.revenue.revenueYoYTtm)}`);
  lines.push(`- Revenue 3Y CAGR: ${formatPercentNullable(report.businessQuality.revenue.revenue3yCagr)}`);
  lines.push(`- Gross Margin: ${formatPercentNullable(report.businessQuality.margins.grossMargin)}`);
  lines.push(`- Industry Gross Margin: ${formatPercentNullable(report.businessQuality.margins.industryMedianGrossMargin)}`);
  lines.push(`- Relative Gross Margin: ${formatPP(report.businessQuality.margins.relativeGrossMarginPp)}`);
  lines.push(`- Operating Margin: ${formatPercentNullable(report.businessQuality.margins.operatingMargin)}`);
  lines.push(`- Industry Operating Margin: ${formatPercentNullable(report.businessQuality.margins.industryMedianOperatingMargin)}`);
  lines.push(`- Relative Operating Margin: ${formatPP(report.businessQuality.margins.relativeOperatingMarginPp)}`);
  lines.push(`- FCF Margin: ${formatPercentNullable(report.businessQuality.margins.fcfMargin)}`);
  lines.push(`- Industry FCF Margin: ${formatPercentNullable(report.businessQuality.margins.industryMedianFcfMargin)}`);
  lines.push(`- Relative FCF Margin: ${formatPP(report.businessQuality.margins.relativeFcfMarginPp)}`);
  lines.push(`- CFO (TTM): ${formatCurrency(report.businessQuality.cashFlow.cfoTtm)}`);
  lines.push(`- CapEx (TTM): ${formatCurrency(report.businessQuality.cashFlow.capexTtm)}`);
  lines.push(`- FCF (TTM): ${formatCurrency(report.businessQuality.cashFlow.fcfTtm)}`);
  lines.push(`- ROIC (TTM): ${formatPercentValueNullable(report.businessQuality.capitalEfficiency.roicTtm)}`);
  lines.push(`- ROE (TTM): ${formatPercentValueNullable(report.businessQuality.capitalEfficiency.roeTtm)}`);
  lines.push(`- Debt / FCF: ${formatRatio(report.businessQuality.balanceSheet.debtToFcfTtm)}`);
  lines.push(`- Current Ratio: ${formatNumberNullable(report.businessQuality.balanceSheet.currentRatio)}`);
  lines.push(`- Score: ${report.businessQuality.score}/65`);
  lines.push("");

	lines.push(report.analysisV2 ? "## Historical Durability Proxy" : "## Moat Proxy");
  lines.push(`- ROIC persistence: ${report.moatProxy.signals.roicPersistence}`);
  lines.push(`- Margin stability: ${report.moatProxy.signals.marginStability}`);
  lines.push(`- Revenue durability: ${report.moatProxy.signals.revenueDurability}`);
	if (!report.analysisV2) lines.push(`- Reinvestment efficiency: ${report.moatProxy.signals.reinvestmentEfficiency}`);
	lines.push(`- Score: ${report.analysisV2?.factors.durabilityProxy?.earned ?? report.moatProxy.score}/20`);
	lines.push(report.analysisV2 ? `- Evidence Confidence: ${report.analysisV2.dataConfidence}` : `- Confidence Band: ${report.moatConfidence.band}`);
  for (const note of report.moatConfidence.justification || []) {
    lines.push(`- Note: ${note}`);
  }
  lines.push("");

  lines.push("## FCF Regime Shift");
  lines.push(`- CapEx / CFO: ${formatPercentValueNullable(report.fcfRegimeShift.capexToCfo)}`);
  lines.push(`- Trend (3Y): ${report.fcfRegimeShift.trend3y}`);
  lines.push(`- Maintenance-like: ${boolLabel(report.fcfRegimeShift.maintenanceLike)}`);
  lines.push(`- Growth-optional: ${boolLabel(report.fcfRegimeShift.growthOptional)}`);
  lines.push(`- Structurally rising: ${boolLabel(report.fcfRegimeShift.structurallyRising)}`);
  lines.push(`- Regime: ${report.fcfRegimeShift.regime}`);
  lines.push(`- Penalty: ${report.fcfRegimeShift.penaltyPoints}`);
  for (const note of report.fcfRegimeShift.justification || []) {
    lines.push(`- Note: ${note}`);
  }
  lines.push("");

  lines.push("## Valuation & Capital Discipline");
  lines.push(`- Market Cap: ${formatCurrency(report.valuationAndDiscipline.marketData.marketCap)}`);
  lines.push(`- Enterprise Value: ${formatCurrency(report.valuationAndDiscipline.marketData.enterpriseValue)}`);
  lines.push(`- EV / FCF (TTM): ${formatRatioNullable(report.valuationAndDiscipline.valuation.evFcfTtm)}`);
  lines.push(`- P / FCF (TTM): ${formatRatioNullable(report.valuationAndDiscipline.valuation.pFcfTtm)}`);
  lines.push(`- EV / EBITDA (TTM): ${formatRatioNullable(report.valuationAndDiscipline.valuation.evEbitdaTtm)}`);
  lines.push(`- P / E (TTM): ${formatRatioNullable(report.valuationAndDiscipline.valuation.pERatioTtm)}`);
  lines.push(`- Dividend payout ratio: ${formatPercentValueNullable(report.valuationAndDiscipline.capitalAllocation.dividendPayoutRatio)}`);
  lines.push(`- Buybacks (TTM): ${formatCurrencyNullable(report.valuationAndDiscipline.capitalAllocation.buybacksTtm)}`);
  lines.push(`- Net share trend (3Y): ${report.valuationAndDiscipline.capitalAllocation.netShareCountTrend3y}`);
  lines.push(`- Net share CAGR (3Y): ${formatPercentNullable(report.valuationAndDiscipline.capitalAllocation.netShareCountCagr3y)}`);
  lines.push(`- CapEx / CFO: ${formatPercentValueNullable(report.valuationAndDiscipline.capitalAllocation.capexToCfo)}`);
  lines.push(`- Pre-penalty score: ${report.valuationAndDiscipline.prePenaltyScore}/35`);
  lines.push(`- Adjusted score: ${report.valuationAndDiscipline.adjustedScore}/35`);
  lines.push("");

  lines.push("## Red Flags");
  for (const flag of report.redFlags || []) {
    lines.push(`- [${flag.triggered ? "x" : " "}] ${flag.label} (${flag.evidence || "N/A"})`);
  }
  lines.push("");

  lines.push("## Scoring Summary");
  lines.push(`- Business Quality: ${report.scoringSummary.businessQualityScore}/65`);
	lines.push(`- ${report.analysisV2 ? "Historical Durability" : "Moat Proxy"}: ${report.analysisV2?.factors.durabilityProxy?.earned ?? report.scoringSummary.moatProxyScore}/20`);
  lines.push(`- Valuation & Discipline: ${report.scoringSummary.valuationScoreAdjusted}/35`);
  lines.push(`- Total: ${report.scoringSummary.totalScore120}/120`);
	lines.push(`- Conservative Quantitative Screen: ${displayQuantitativeScore(report).toFixed(1)}/100`);
  lines.push("");

	lines.push(report.analysisV2 ? "## House Multiple Thresholds" : "## Buy-Price Overlay");
  lines.push(`- Current EV/FCF: ${formatRatio(report.buyPriceOverlay.evFcfTtm)}`);
  lines.push(`- Current Zone: ${report.buyPriceOverlay.currentZone}`);
  for (const band of report.buyPriceOverlay.bands || []) {
    lines.push(`- ${entryLevelLabel(band.label)}: ${formatBandRange(band.min, band.max)} (${band.description})`);
  }
  lines.push("");

	lines.push(report.analysisV2 ? "## Research Disposition" : "## Final Action");
	lines.push(`- Screening Disposition: ${displayScreeningDisposition(report)}`);
	if (!report.analysisV2) lines.push(`- Moat Band: ${report.finalActionMapping.moatBand}`);
  lines.push(`- Valuation Zone: ${report.finalActionMapping.valuationZone}`);
  lines.push(`- Score Used: ${formatScore(report.finalActionMapping.score, 100)}`);
  for (const reason of report.finalActionMapping.reasons || []) {
    lines.push(`- Reason: ${reason}`);
  }
  if (report.warnings && report.warnings.length > 0) {
    lines.push("");
    lines.push("## Data Warnings");
    for (const warning of report.warnings) {
      lines.push(`- ${warning}`);
    }
  }

  return lines.join("\n");
}

function holdingStateForPortfolioStage(stage: PortfolioPositionStage): "CANDIDATE" | "OWNED" {
  return stage === "OWNED" ? "OWNED" : "CANDIDATE";
}

export default InvestmentTickerView;
